import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { getAssessmentDetail, getAssessmentNarrative, getRoiInputs, saveRoiInputs, processRoi, getRoiOutput, getImplementationInputs, getCmRoi, cloneAssessmentVersion, finalizeAssessment, getAssessmentVersion, releaseToCustomer, getReleaseStatus, getAssessmentReadiness, proposalDocxUrl, proposalPptxUrl, applyRecommendationOverride, clearRecommendationOverride } from "../lib/adminApi";
import { getGroupRoi } from "../lib/adminApi";
import { listQuestionsPublic } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import "./client-view.css";
import { toast } from "sonner";
import AuditMeta from "../components/AuditMeta";
import { ArrowLeft, Save, RotateCcw, Calculator, TrendingUp, ChevronDown, ChevronRight, CheckCircle2, History, Lock, Download, Mail, Send, FileText, FileType, Users } from "lucide-react";
import { useClientAssignment } from "../lib/clientAssignmentContext";
import ManageClientAccessDialog from "../components/ManageClientAccessDialog";
import { VersionHistoryDrawer, FinalizeModal, VersionBanner } from "./VersionManager";
import ImplementationInputsTab, { computeCore } from "./ImplementationInputsTab";
import {
  OverrideHistoryViewer,
  RecommendationOverridePanel,
  useEffectiveRecommendations,
} from "./RecommendationOverride";
import {
  deriveCmStrategy,
  toCmSolutionLabel,
  CM_STRATEGY_KEYS,
  CM_STRATEGY_LABELS,
  CM_STRATEGY_DESCRIPTIONS,
  CM_APPROACH_LABELS,
  CM_APPROACH_DESCRIPTIONS,
} from "./cmStrategy";
import CmRoiTab from "./CmRoiTab";
import GroupRoiTab from "./GroupRoiTab";
import EvaluationInsightsTab, {
  buildKeyEvaluationInsights,
  WH_LABELS as INSIGHTS_WH_LABELS,
  scoreBand as insightsScoreBand,
  confidenceFor as insightsConfidenceFor,
  driverChipsFor as insightsDriverChipsFor,
  buildRecommendationCopy as insightsBuildRecCopy,
  readLandscape as insightsReadLandscape,
  cmMergedAnswers,
  cmComplexityBand,
  cmComplexityExplanation,
  cmRiskAreas,
  cmCustomDevTotal,
  cmNum,
  cmFocusAreas,
  CmSnapshotRow,
  CM_COMPLEXITY_TONE,
} from "./EvaluationInsightsTab";
import { deriveProgrammeContext } from "./programmeContext";
import RfpClientInputsTab from "./RfpClientInputsTab";

// --- Display utilities -------------------------------------------------------
// Canonical warehouse type labels — business-friendly names shown everywhere.
// Fallback: snake_case → Title Case if an unknown warehouse type is introduced.
const WAREHOUSE_LABELS = {
  cdc: "Central Distribution Center",
  rdc: "Regional Distribution Center",
  satellite: "Small / Satellite Warehouse",
  spare_parts: "Spare Parts / Service Warehouse",
  manufacturing: "Manufacturing Plant Warehouse",
  ecommerce: "E-commerce Fulfillment Center",
};

// Phase-3 — Warehouse Group display-name registry. Populated from
// assessment.warehouse_groups so every report label can resolve a stable group
// UUID → group name. UUID stays the internal join key; never shown to the user.
const GROUP_NAME_MAP = {};
export function registerGroupNames(assessment) {
  (assessment?.warehouse_groups || []).forEach((g) => {
    if (g && g.id) GROUP_NAME_MAP[g.id] = g.name || GROUP_NAME_MAP[g.id] || g.id;
  });
}
const isGroupedReport = () => Object.keys(GROUP_NAME_MAP).length > 0;
const groupLabel = (key) => (key && GROUP_NAME_MAP[key]) || null;

export function warehouseLabel(key) {
  if (!key) return "—";
  if (GROUP_NAME_MAP[key]) return GROUP_NAME_MAP[key];
  if (WAREHOUSE_LABELS[key]) return WAREHOUSE_LABELS[key];
  return String(key)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

// Phase-3 — the evaluation/recommendation unit is now the Warehouse GROUP
// (keyed by stable group id). Resolve a unit key to its Warehouse Group
// display label + scenario eyebrow from assessment.warehouse_groups; fall
// back to the legacy warehouse-type label for pre-group assessments.
export function groupSiteInfo(assessment, key) {
  const g = ((assessment && assessment.warehouse_groups) || []).find((x) => x.id === key);
  if (g) {
    return {
      label: g.name || key,
      eyebrow: (g.assessment_type || "group").replace(/_/g, " "),
    };
  }
  return { label: warehouseLabel(key), eyebrow: "" };
}

// Order a set of per-site keys to follow the assessment.warehouse_groups
// array order (group-first, matching Client Inputs & Evaluation Insights).
// Keys not present in warehouse_groups (legacy warehouse-type keys) fall back
// to the supply-chain ordering used before groups existed.
export function orderSiteKeys(assessment, keys) {
  const groupOrder = ((assessment && assessment.warehouse_groups) || []).map((g) => g.id);
  const SUPPLY_CHAIN_ORDER = ["manufacturing", "cdc", "rdc", "satellite"];
  return [...keys].sort((a, b) => {
    const ga = groupOrder.indexOf(a);
    const gb = groupOrder.indexOf(b);
    if (ga !== -1 || gb !== -1) {
      const ra = ga === -1 ? 999 : ga;
      const rb = gb === -1 ? 999 : gb;
      if (ra !== rb) return ra - rb;
      return String(a).localeCompare(String(b));
    }
    const ia = SUPPLY_CHAIN_ORDER.indexOf(a);
    const ib = SUPPLY_CHAIN_ORDER.indexOf(b);
    const ra = ia === -1 ? 99 : ia;
    const rb = ib === -1 ? 99 : ib;
    if (ra !== rb) return ra - rb;
    return String(a).localeCompare(String(b));
  });
}

// Business-friendly label for enum-ish strings (scenario, strategy type, etc.).
// Preserves known acronyms (ROI, SAP, EWM, LGM, StRM, WM, MES, QM, RFID, KPI, HU, VAS, JIT, WIP).
const ACRONYMS = new Set(["ROI", "SAP", "EWM", "LGM", "STRM", "WM", "MES", "QM", "RFID", "KPI", "HU", "VAS", "JIT", "WIP", "ASRS", "FTE"]);
export function toSentenceCase(text) {
  if (text === null || text === undefined) return "—";
  const s = String(text).trim();
  if (!s) return "—";
  return s
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(w => {
      const up = w.toUpperCase();
      if (ACRONYMS.has(up)) return up === "STRM" ? "StRM" : up;
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(" ");
}

// NOTE: The former ROI_FIELDS / GROUPS constants (a global "Implementation
// Cost" edit field grouped under "Investment") were removed. Implementation
// investment is authoritatively sourced from Implementation → Cost Model
// (implementation_inputs.cost_breakdown.one_time.total_one_time_investment);
// the ROI engine never consumed roi_inputs.global_inputs.implementation_cost,
// so the field was a false second source of truth. The live ROI form uses
// GLOBAL_FIELDS / SITE_FIELDS below.


function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider mb-2">{title}</h3>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

// Decision Trail — compact chip-based explanation of which scoring rules fired.
// Palette stays on the PrimeS4 blue/neutral axis; tones are subtle.
const CHIP_TONES = {
  blue: "bg-[#E0F2FE] text-[#025979] border-[#025979]/20",
  green: "bg-[#ECFDF5] text-[#065F46] border-[#065F46]/15",
  amber: "bg-[#FEF3C7] text-[#92400E] border-[#92400E]/15",
  slate: "bg-[#F1F5F9] text-[#334155] border-[#334155]/15",
  neutral: "bg-[#F8FAFC] text-[#6B7280] border-[#E5E7EB]",
};

function DecisionChip({ label, tone = "neutral", tooltip, testid }) {
  const chip = (
    <span
      data-testid={testid}
      className={`inline-flex items-center px-2 py-0.5 border rounded-sm text-[10px] font-medium whitespace-nowrap cursor-help ${CHIP_TONES[tone]}`}
    >
      {label}
    </span>
  );
  if (!tooltip) return chip;
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={6}
        className="max-w-xs bg-[#111827] text-white text-[11px] leading-snug font-normal px-2.5 py-1.5 shadow-md border border-[#111827]"
      >
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

// Flag → chip label + subtle tone + concise tooltip
const CHIP_DEFS = {
  baseline_applied: {
    label: "Default Profile Used",
    tone: "slate",
    tooltip: "No strong capability signals detected, so baseline warehouse profile was applied.",
  },
  lean_override: {
    label: "Lean Profile Detected",
    tone: "green",
    tooltip: "Warehouse profile is lean and low complexity, making SAP LGM the best fit.",
  },
  safety_downgrade: {
    label: "Adjusted to Best-Fit Solution",
    tone: "amber",
    tooltip: "Higher solution score was adjusted due to missing required complexity or automation signals.",
  },
  advanced_threshold: {
    label: "Advanced Fit Confirmed",
    tone: "blue",
    tooltip: "EWM Advanced selected due to strong automation, integration, or operational complexity signals.",
  },
  band_rule_applied: {
    label: "Basic Preferred (Band Rule)",
    tone: "blue",
    tooltip: "EWM Advanced scored close to Basic, so the more cost-effective Basic was preferred.",
  },
  brownfield_guardrail: {
    label: "Legacy System Constraint Applied",
    tone: "slate",
    tooltip: "Brownfield scenario favors continuity with existing SAP WM-based solution.",
  },
};

const SIGNAL_DEFS = {
  high_automation: {
    label: "High Automation",
    tone: "neutral",
    tooltip: "High automation requirements detected in warehouse operations.",
  },
  high_integration: {
    label: "High Integration",
    tone: "neutral",
    tooltip: "Complex system integrations require advanced warehouse capabilities.",
  },
  complex_operations: {
    label: "Complex Operations",
    tone: "neutral",
    tooltip: "Operational complexity (e.g. VAS, multi-step picking) requires advanced solution support.",
  },
  low_complexity_fit: {
    label: "Low Complexity Fit",
    tone: "neutral",
    tooltip: "Simple warehouse profile fits a lightweight, low-footprint solution.",
  },
};

function DecisionTrail({ flags, siteKey }) {
  if (!flags || Object.keys(flags).length === 0) return null;
  const chips = [];
  for (const flagKey of ["baseline_applied", "lean_override", "safety_downgrade", "advanced_threshold", "band_rule_applied", "brownfield_guardrail"]) {
    if (flags[flagKey]) {
      const def = CHIP_DEFS[flagKey];
      chips.push({ key: flagKey, ...def });
    }
  }
  const signals = flags.signals || {};
  for (const sigKey of ["high_automation", "high_integration", "complex_operations", "low_complexity_fit"]) {
    if (signals[sigKey]) {
      const def = SIGNAL_DEFS[sigKey];
      chips.push({ key: `sig-${sigKey}`, ...def });
    }
  }

  if (chips.length === 0) return null;
  const displayed = chips.slice(0, 6);

  return (
    <div className="mt-3 pt-3 border-t border-[#E5E7EB]" data-testid={`decision-trail-${siteKey}`}>
      <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1.5">Decision Trail</p>
      <TooltipProvider delayDuration={150}>
        <div className="flex flex-wrap gap-1.5">
          {displayed.map(c => (
            <DecisionChip
              key={c.key}
              label={c.label}
              tone={c.tone}
              tooltip={c.tooltip}
              testid={`chip-${siteKey}-${c.key}`}
            />
          ))}
        </div>
      </TooltipProvider>
    </div>
  );
}

// Section titles for Client Inputs Summary (ordered)
const SECTION_TITLE = {
  "module1:client_details": "Client Details",
  "module2:warehouse_context": "Warehouse Context",
  "module3:org": "Warehouse Profile & Volumes",
  "module3:inbound": "Inbound Processes",
  "module3:outbound": "Outbound Processes",
  "module3:inventory": "Internal Movements & Inventory",
  "module3:production": "Production & Manufacturing",
  "module3:automation": "Smart Warehousing",
  "module3:integration": "Integrations & Interfaces",
  "module3:reporting": "Reporting & KPIs",
  "module3:pain_points": "Business Priorities & Future Strategy",
  "module3:migration_readiness": "Current System & Migration Readiness",
  // --- Cloud Migration sections (informational data-capture) ---
  "module2:migration_landscape": "Current SAP Landscape",
  "module3:system_infrastructure": "System & Infrastructure",
  "module3:data_volume": "Data Volume",
  "module3:integration_landscape": "Integration Landscape",
  "module3:custom_development": "Custom Development",
  "module3:operations_cutover": "Operations & Cutover",
  "module3:migration_objectives": "Migration Objectives",
  "module3:current_parallel_projects": "Current & Parallel Projects",
};
const SECTION_ORDER = [
  "module1:client_details",
  "module2:warehouse_context",
  "module2:migration_landscape",
  "module3:org",
  "module3:inbound",
  "module3:outbound",
  "module3:inventory",
  "module3:production",
  "module3:automation",
  "module3:integration",
  "module3:reporting",
  "module3:pain_points",
  "module3:migration_readiness",
  // Cloud Migration Module 3 sections
  "module3:system_infrastructure",
  "module3:data_volume",
  "module3:integration_landscape",
  "module3:custom_development",
  "module3:operations_cutover",
  "module3:migration_objectives",
  "module3:current_parallel_projects",
];

// Humanized fallback title for any section not explicitly mapped above — keeps
// the tab future-proof so a newly added Question Bank section is never silently
// dropped from Client Inputs.
function sectionTitleFallback(key) {
  const raw = String(key || "").split(":").pop() || "";
  if (!raw) return key;
  return raw
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function labelFromOptions(options, value) {
  if (value === undefined || value === null || value === "") return null;
  if (Array.isArray(value)) {
    const parts = value
      .map(v => (options && Array.isArray(options) ? options.find(o => String(o.value) === String(v))?.label : null) || WAREHOUSE_LABELS[v] || toSentenceCase(v))
      .filter(Boolean);
    return parts.length ? parts.join(", ") : null;
  }
  if (Array.isArray(options)) {
    const hit = options.find(o => String(o.value) === String(value));
    if (hit?.label) return hit.label;
  }
  // Numeric passthrough
  if (typeof value === "number") return value.toLocaleString();
  const str = String(value);
  // Warehouse key fallback
  if (WAREHOUSE_LABELS[str]) return WAREHOUSE_LABELS[str];
  // Snake-case-ish enum fallback → sentence case
  if (/^[a-z0-9_]+$/.test(str) && str.includes("_") === false && str.length < 40) {
    return toSentenceCase(str);
  }
  if (/^[a-z0-9_]+$/.test(str) && str.includes("_")) return toSentenceCase(str);
  return str;
}

function CollapsibleSection({ title, count, defaultOpen = true, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[#F8FAFC] transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-[#9CA3AF]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#9CA3AF]" />}
          <span className="text-[12px] font-semibold text-[#111827]">{title}</span>
          <span className="text-[10px] text-[#9CA3AF]">({count})</span>
        </div>
      </button>
      {open && <div className="px-3 pb-3 border-t border-[#E5E7EB]">{children}</div>}
    </div>
  );
}

export function ClientInputsSummary({ assessment, questions: questionsOverride }) {
  const [questions, setQuestions] = useState(questionsOverride || null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (questionsOverride) { setQuestions(questionsOverride); return; }
    listQuestionsPublic()
      .then(setQuestions)
      .catch(() => setErr("Failed to load question catalog"));
  }, [questionsOverride]);

  if (!assessment) return null;
  if (err) return <div className="text-xs text-red-600">{err}</div>;
  if (!questions) return <div className="text-xs text-[#9CA3AF]">Loading client inputs…</div>;

  // Build answers into two buckets:
  //   groups    → module1/module2 (non-per-group) sections: { key: [items] }
  //   m3ByGroup → module3 answers keyed by Warehouse Group: { groupId: { key: [items] } }
  const groups = {};
  const m3ByGroup = {};
  const mkItem = (q, label) => ({
    order: q.display_order ?? 0,
    label: q.question_text,
    value: label,
    field_key: q.field_key,
  });
  const answerLabel = (q, rawValue) => {
    if (rawValue === undefined || rawValue === null || rawValue === "") return null;
    if (Array.isArray(rawValue) && rawValue.length === 0) return null;
    return labelFromOptions(q.answer_options, rawValue);
  };
  const pushFlat = (q, rawValue) => {
    const label = answerLabel(q, rawValue);
    if (label === null) return;
    const key = `${q.module}:${q.section}`;
    (groups[key] || (groups[key] = [])).push(mkItem(q, label));
  };
  const pushGroup = (q, rawValue, groupId) => {
    const label = answerLabel(q, rawValue);
    if (label === null) return;
    const key = `${q.module}:${q.section}`;
    const gm = m3ByGroup[groupId] || (m3ByGroup[groupId] = {});
    (gm[key] || (gm[key] = [])).push(mkItem(q, label));
  };

  // Top-level fields (module1 + scenario + industry)
  const top = assessment;
  const m2 = (assessment.module_data || {}).module2 || {};
  // Warehouse Groups are the Step 3 answer namespace: module3_groups[groupId].
  // Read-time fallback to the legacy module_data.module3 (warehouse-type keyed)
  // so pre-Warehouse-Group records still render their Step 3 answers.
  const m3groups = (assessment.module_data || {}).module3_groups || {};
  const m3legacy = (assessment.module_data || {}).module3 || {};
  const m3 = Object.keys(m3groups).length ? m3groups : m3legacy;
  const wgById = Object.fromEntries(((assessment.warehouse_groups) || []).map((g) => [g.id, g]));

  for (const q of questions) {
    if (!q?.field_key) continue;
    if (q.module === "module1") {
      pushFlat(q, top[q.field_key]);
    } else if (q.module === "module2") {
      // Module 2 values are in module_data.module2 OR top-level (for scenario_type/industry)
      let v = m2[q.field_key];
      if (v === undefined || v === null || v === "") v = top[q.field_key];
      pushFlat(q, v);
    } else if (q.module === "module3") {
      for (const [groupId, groupAnswers] of Object.entries(m3)) {
        pushGroup(q, groupAnswers?.[q.field_key], groupId);
      }
    }
  }

  const minOrder = (arrOrMap) => {
    let m = Infinity;
    const arrs = Array.isArray(arrOrMap) ? [arrOrMap] : Object.values(arrOrMap || {});
    for (const arr of arrs) for (const it of arr) if (it.order < m) m = it.order;
    return m;
  };
  // Non-per-group (module1/module2) sections, canonical order first then extras.
  const sectionsFrom = (map) => {
    const known = SECTION_ORDER.filter(k => map[k]);
    const extra = Object.keys(map)
      .filter(k => !SECTION_ORDER.includes(k))
      .sort((a, b) => minOrder(map[a]) - minOrder(map[b]));
    return [...known, ...extra];
  };
  const topSections = sectionsFrom(groups);

  // Warehouse Group render order follows the assessment.warehouse_groups array
  // (so Group 1, 2, 3 …), then any leftover legacy keys not in that array.
  const orderedGroupIds = [
    ...((assessment.warehouse_groups) || []).map(g => g.id).filter(id => m3ByGroup[id]),
    ...Object.keys(m3ByGroup).filter(id => !((assessment.warehouse_groups) || []).some(g => g.id === id)),
  ];

  if (topSections.length === 0 && orderedGroupIds.length === 0) {
    return <p className="text-xs text-[#9CA3AF] italic">No client inputs captured for this evaluation yet.</p>;
  }

  const renderGrid = (items) => {
    const sorted = [...items].sort((a, b) => a.order - b.order);
    return (
      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
        {sorted.map((it, idx) => (
          <div key={`${it.field_key}-${idx}`} className="flex items-center justify-between py-1 border-b border-[#F3F4F6] last:border-0 text-[11px]">
            <span className="text-[#6B7280] pr-3">{it.label}</span>
            <span className="text-[#111827] font-medium text-right whitespace-nowrap">{it.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-2" data-testid="client-inputs-summary">
      {/* Client Details / Warehouse Context (module1 + module2) — not per-group */}
      {topSections.map(key => {
        const items = groups[key];
        const title = SECTION_TITLE[key] || sectionTitleFallback(key);
        return (
          <CollapsibleSection
            key={key}
            title={title}
            count={items.length}
            testid={`inputs-section-${key.replace(":", "-")}`}
          >
            <div className="mt-2">{renderGrid(items)}</div>
          </CollapsibleSection>
        );
      })}

      {/* Step 3 answers — grouped by Warehouse Group first, then by process/section */}
      {orderedGroupIds.map(groupId => {
        const secMap = m3ByGroup[groupId];
        const g = wgById[groupId];
        const secKeys = sectionsFrom(secMap);
        const total = secKeys.reduce((n, k) => n + secMap[k].length, 0);
        const groupTitle = g?.name || warehouseLabel(groupId);
        return (
          <CollapsibleSection
            key={groupId}
            title={
              <span className="inline-flex items-center gap-2">
                {groupTitle}
                {g?.assessment_type && (
                  <span className="text-[10px] font-medium text-[#6D28D9] uppercase tracking-wide">{(g.assessment_type || "").replace(/_/g, " ")}</span>
                )}
              </span>
            }
            count={total}
            testid={`inputs-group-${groupId}`}
          >
            {g && (
              <p className="text-[10px] text-[#6B7280] mt-2" data-testid={`inputs-group-meta-${groupId}`}>
                {(g.sites || []).join(", ")}
                {g.sites?.length ? " · " : ""}{g.warehouse_count} warehouse(s)
                {(g.warehouse_types || []).length ? ` · ${(g.warehouse_types || []).map(warehouseLabel).join(", ")}` : ""}
                {g.current_solution ? ` · ${g.current_solution}` : ""}
                {g.current_version ? ` v${g.current_version}` : ""}
                {g.current_solution_name ? ` (${g.current_solution_name})` : ""}
              </p>
            )}
            {secKeys.map(key => {
              const title = SECTION_TITLE[key] || sectionTitleFallback(key);
              return (
                <div key={key} className="mt-3 first:mt-2" data-testid={`inputs-group-${groupId}-section-${key.replace(":", "-")}`}>
                  <p className="text-[10px] font-semibold text-[#025979] uppercase tracking-wider mb-1.5">
                    {title} <span className="text-[#9CA3AF] normal-case">({secMap[key].length})</span>
                  </p>
                  {renderGrid(secMap[key])}
                </div>
              );
            })}
          </CollapsibleSection>
        );
      })}
    </div>
  );
}

// Canonicalize any deployment label into one of the 4 programme buckets.
function normalizeDeployment(d) {
  const x = String(d || "").toLowerCase();
  if (!x || x === "—") return null;
  if (x.includes("decentral")) return "Decentralized EWM";
  if (x.includes("hybrid")) return "Hybrid Deployment";
  if (x.includes("lean") || x.includes("n/a")) return "N/A";
  if (x.includes("embedded")) return "Embedded EWM";
  return d;
}

// Programme-level Deployment Architecture derived from the EFFECTIVE final
// recommendation (per the override-aware spec):
//   • Pure lean solution (LGM / Stock Room) across all sites → N/A
//   • Hybrid Landscape solution → Hybrid Deployment
//   • All sites same deployment → that deployment
//   • Mixed deployments (e.g. Embedded + Decentralized) → Hybrid Deployment
function deriveProgrammeDeployment(effectiveSolutions, solutionDisplay, perSiteDeployments) {
  const solSet = (effectiveSolutions || []).map((s) => String(s).toLowerCase()).filter(Boolean);
  const allLean = solSet.length > 0
    && solSet.every((s) => s.includes("lgm") || s.includes("stock room"));
  if (allLean) return "N/A";
  if (String(solutionDisplay || "").toLowerCase().includes("hybrid landscape")) {
    return "Hybrid Deployment";
  }
  const canon = Array.from(new Set((perSiteDeployments || []).map(normalizeDeployment).filter(Boolean)));
  if (canon.length === 0) return "—";
  if (canon.length === 1) return canon[0];
  return "Hybrid Deployment";
}

// Display-layer label alignment: legacy assessments persisted the SAP LGM
// deployment as "Lean Embedded / N/A". The engine now emits "N/A" (there is no
// deployment architecture called "Lean"). Normalize the label on load so every
// tab renders the same value for both new and legacy records. Display-only — no
// engine, scoring or recommendation logic is affected.
function fixDeploymentLabel(v) {
  return (typeof v === "string" && v.toLowerCase().includes("lean")) ? "N/A" : v;
}
function normalizeAssessmentDeploymentLabels(a) {
  if (!a || typeof a !== "object") return a;
  const next = { ...a };
  next.overall_deployment_type = fixDeploymentLabel(a.overall_deployment_type);
  next.system_overall_deployment_type = fixDeploymentLabel(a.system_overall_deployment_type);
  const mapFix = (m) => {
    if (!m || typeof m !== "object") return m;
    const out = {};
    for (const [k, val] of Object.entries(m)) out[k] = fixDeploymentLabel(val);
    return out;
  };
  next.deployment_recommendation_by_site = mapFix(a.deployment_recommendation_by_site);
  next.system_deployment_recommendation_by_site = mapFix(a.system_deployment_recommendation_by_site);
  return next;
}



// ---------------------------------------------------------------------------
// Cloud Migration — Recommendations tab (reuses the override workflow +
// Recommendation Library narrative; Greenfield/Brownfield untouched).
// ---------------------------------------------------------------------------
function CloudMigrationRecommendations({ assessment, assessmentId }) {
  const { data: overrideData, refresh: refreshOverrides } =
    useEffectiveRecommendations(assessmentId);
  if (!assessment) return null;
  if (!overrideData) {
    return (
      <div className="space-y-5" data-testid="overview-tab">
        <p className="text-xs text-[#6B7280] italic bg-white border border-[#E5E7EB] rounded-sm p-6 text-center">
          Loading recommendation…
        </p>
      </div>
    );
  }

  const narr = overrideData.narrative || {};
  const reasons = narr.reasons || [];
  const deployment = narr.deployment || [];
  const hasOverride = !!overrideData.summary?.has_global_override;
  const effApproach = overrideData.effective_migration_approach || "—";
  const { strategyKey, approach } = deriveCmStrategy(effApproach);
  const overrideReason = overrideData.summary?.global_override?.reason || "";

  return (
    <div className="space-y-5" data-testid="overview-tab">
      {/* Effective summary banner — Transformation Strategy (+ Approach) */}
      <section
        data-testid="rec-header"
        className="bg-gradient-to-r from-[#025979] to-[#024a63] text-white rounded-md p-4"
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
            Effective Recommendation
          </p>
          {hasOverride && (
            <span
              data-testid="rec-header-override-badge"
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FFFBEB] text-[#92400E] rounded-sm text-[10px] font-bold uppercase tracking-wider border border-[#FCD34D]"
              title="Consultant override applied"
            >
              Consultant Override Applied
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-start gap-8">
          <div>
            <p className="text-[9px] uppercase tracking-wider text-white/50 font-semibold">Transformation Strategy</p>
            <p className="text-xl font-bold mt-0.5" data-testid="rec-header-strategy" style={{ fontFamily: "Chivo, sans-serif" }}>
              {CM_STRATEGY_LABELS[strategyKey]}
            </p>
          </div>
          {strategyKey === "technical_migration" && approach && (
            <div>
              <p className="text-[9px] uppercase tracking-wider text-white/50 font-semibold">Migration Approach</p>
              <p className="text-xl font-bold mt-0.5" data-testid="rec-header-approach" style={{ fontFamily: "Chivo, sans-serif" }}>
                {approach}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Section 1 + 2 — Transformation Strategy / Migration Approach override */}
      <CmTransformationOverride
        assessmentId={assessmentId}
        overrideData={overrideData}
        onChanged={refreshOverrides}
      />

      {/* Section 3 — Why This Recommendation (reuses narrative reasons) */}
      {reasons.length > 0 && (
        <section
          data-testid="rec-why"
          className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5 border-l-2 border-l-[#025979]"
        >
          <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="rec-why-title">
            Why This Recommendation
          </h3>
          <ul className="space-y-2">
            {reasons.map((r, i) => (
              <li key={`cm-why-${i}`} data-testid={`rec-why-bullet-${i}`} className="flex items-start gap-2 text-[12.5px] text-[#0F172A] leading-relaxed">
                <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-[#025979] shrink-0" aria-hidden="true" />
                <span>
                  <span className="font-semibold">{r.label}</span>
                  {r.description ? ` — ${r.description}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Section 4 — Deployment Guidance (reuses Recommendation Library) */}
      {deployment.length > 0 && (
        <section
          data-testid="rec-deployment-guidance"
          className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5"
        >
          <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
            Deployment Guidance
          </h3>
          <ul className="space-y-2 text-[12.5px] text-[#111827] leading-relaxed">
            {deployment.map((d, i) => (
              <li key={`cm-dep-${i}`} data-testid={`rec-deployment-${i}`} className="flex items-start gap-2">
                <span className="text-[#025979] mt-0.5">•</span>
                <span>{d.description}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Section 5 — Consultant Notes (the current override rationale) */}
      {hasOverride && overrideReason && (
        <section data-testid="rec-consultant-notes" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
          <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
            Consultant Notes
          </h3>
          <p className="text-[12.5px] text-[#374151] leading-relaxed">{overrideReason}</p>
        </section>
      )}

      {/* Section 6 — Override History (reuses shared viewer) */}
      {overrideData.override_history?.length > 0 && (
        <OverrideHistoryViewer history={overrideData.override_history} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cloud Migration — two-level Transformation Strategy / Migration Approach
// override. Reuses the existing recommendation-override API + release gate;
// the two radios resolve to the single stored `solution` leaf label so no
// second override store is introduced. Greenfield/Brownfield use the shared
// RecommendationOverridePanel and are untouched.
// ---------------------------------------------------------------------------
function CmTransformationOverride({ assessmentId, overrideData, onChanged }) {
  const canOverride = !!overrideData.can_override;
  const hasOverride = !!overrideData.summary?.has_global_override;
  const minReason = overrideData.min_reason_length || 20;
  const sys = deriveCmStrategy(overrideData.system_migration_approach);
  const eff = deriveCmStrategy(overrideData.effective_migration_approach);
  const effLeaf = overrideData.effective_migration_approach || "";

  const [selStrategy, setSelStrategy] = useState(eff.strategyKey);
  const [selApproach, setSelApproach] = useState(eff.approach || "Lift & Shift");
  const [reason, setReason] = useState("");
  const [reverting, setReverting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Re-sync local selection whenever the effective recommendation changes
  // (e.g. after a save/revert round-trip).
  useEffect(() => {
    setSelStrategy(eff.strategyKey);
    setSelApproach(eff.approach || "Lift & Shift");
    setReason("");
    setReverting(false);
  }, [effLeaf]); // eslint-disable-line react-hooks/exhaustive-deps

  const selLeaf = toCmSolutionLabel(selStrategy, selApproach);
  const dirty = selLeaf !== effLeaf;

  const pickStrategy = (k) => {
    if (!canOverride) return;
    setSelStrategy(k);
  };
  const pickApproach = (a) => {
    if (!canOverride) return;
    setSelApproach(a);
  };

  const resetSelection = () => {
    setSelStrategy(eff.strategyKey);
    setSelApproach(eff.approach || "Lift & Shift");
    setReason("");
  };

  const save = async () => {
    if (reason.trim().length < minReason) {
      toast.error(`Provide an override reason of at least ${minReason} characters.`);
      return;
    }
    setSaving(true);
    try {
      await applyRecommendationOverride(assessmentId, {
        scope: "global",
        solution: selLeaf,
        reason: reason.trim(),
      });
      toast.success("Transformation strategy override applied");
      if (typeof onChanged === "function") onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply override");
    } finally {
      setSaving(false);
    }
  };

  const revert = async () => {
    if (reason.trim().length < minReason) {
      toast.error(`Provide a reason of at least ${minReason} characters to revert.`);
      return;
    }
    setSaving(true);
    try {
      await clearRecommendationOverride(assessmentId, {
        scope: "global",
        reason: reason.trim(),
      });
      toast.success("Reverted to system recommendation");
      if (typeof onChanged === "function") onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revert override");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="cm-strategy-override" className="bg-white border border-[#E5E7EB] rounded-sm px-5 py-5">
      {/* Transformation Strategy */}
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold" style={{ fontFamily: "Chivo, sans-serif" }}>
          Transformation Strategy
        </h3>
        <span className="text-[10px] text-[#9CA3AF]">System recommends: <strong className="text-[#025979]">{CM_STRATEGY_LABELS[sys.strategyKey]}</strong></span>
      </div>
      <p className="text-[11px] text-[#6B7280] mb-3">Confirm the system recommendation or override the transformation strategy.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cm-strategy-radios">
        {CM_STRATEGY_KEYS.map((k) => {
          const isSel = selStrategy === k;
          const isSystem = sys.strategyKey === k;
          return (
            <button
              key={k}
              type="button"
              data-testid={`cm-strategy-option-${k}`}
              onClick={() => pickStrategy(k)}
              disabled={!canOverride || saving}
              className={`text-left rounded-sm border p-3 transition-colors ${
                isSel ? "border-[#025979] bg-[#E0F2FE] ring-1 ring-[#025979]" : "border-[#E5E7EB] bg-white hover:border-[#025979]/40"
              } ${!canOverride ? "cursor-default" : ""}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-[13px] font-bold text-[#0F172A]" style={{ fontFamily: "Chivo, sans-serif" }}>{CM_STRATEGY_LABELS[k]}</span>
                {isSystem && <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-[#025979] bg-white border border-[#025979]/30 rounded-sm px-1 py-0.5">System</span>}
              </div>
              <p className="mt-1 text-[11px] text-[#475569] leading-relaxed">{CM_STRATEGY_DESCRIPTIONS[k]}</p>
            </button>
          );
        })}
      </div>

      {/* Migration Approach — only for Technical Migration */}
      {selStrategy === "technical_migration" && (
        <div className="mt-5" data-testid="cm-approach-section">
          <div className="flex items-center justify-between gap-3 mb-1">
            <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold" style={{ fontFamily: "Chivo, sans-serif" }}>
              Migration Approach
            </h3>
            {sys.strategyKey === "technical_migration" && sys.approach && (
              <span className="text-[10px] text-[#9CA3AF]">System recommends: <strong className="text-[#025979]">{sys.approach}</strong></span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="cm-approach-radios">
            {CM_APPROACH_LABELS.map((a) => {
              const isSel = selApproach === a;
              const isSystem = sys.strategyKey === "technical_migration" && sys.approach === a;
              return (
                <button
                  key={a}
                  type="button"
                  data-testid={`cm-approach-option-${a === "Lift & Shift" ? "lift_shift" : "lift_shift_validate"}`}
                  onClick={() => pickApproach(a)}
                  disabled={!canOverride || saving}
                  className={`text-left rounded-sm border p-3 transition-colors ${
                    isSel ? "border-[#025979] bg-[#E0F2FE] ring-1 ring-[#025979]" : "border-[#E5E7EB] bg-white hover:border-[#025979]/40"
                  } ${!canOverride ? "cursor-default" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-[13px] font-bold text-[#0F172A]" style={{ fontFamily: "Chivo, sans-serif" }}>{a}</span>
                    {isSystem && <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-[#025979] bg-white border border-[#025979]/30 rounded-sm px-1 py-0.5">System</span>}
                  </div>
                  <p className="mt-1 text-[11px] text-[#475569] leading-relaxed">{CM_APPROACH_DESCRIPTIONS[a]}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Override reason + actions (when the selection differs from effective) */}
      {canOverride && dirty && (
        <div className="mt-5 border-t border-[#E5E7EB] pt-4" data-testid="cm-override-editor">
          <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">Override Reason</label>
          <textarea
            data-testid="cm-override-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder={`Explain why the strategy is being overridden (min ${minReason} characters)…`}
            className="w-full rounded-sm border border-[#E5E7EB] px-3 py-2 text-xs focus:border-[#025979] focus:outline-none"
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="outline" data-testid="cm-override-cancel" className="rounded-sm text-xs" onClick={resetSelection} disabled={saving}>
              Cancel
            </Button>
            <Button data-testid="cm-override-save" className="rounded-sm text-xs bg-[#025979] hover:bg-[#03738C] text-white" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save Override"}
            </Button>
          </div>
        </div>
      )}

      {/* Revert control when an override is active and selection matches it */}
      {canOverride && hasOverride && !dirty && (
        <div className="mt-5 border-t border-[#E5E7EB] pt-4" data-testid="cm-override-revert-area">
          {!reverting ? (
            <button data-testid="cm-override-revert-open" onClick={() => setReverting(true)} className="text-[11px] text-[#025979] underline">
              Revert to system recommendation
            </button>
          ) : (
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">Reason for reverting</label>
              <textarea
                data-testid="cm-override-revert-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder={`Explain why the override is being removed (min ${minReason} characters)…`}
                className="w-full rounded-sm border border-[#E5E7EB] px-3 py-2 text-xs focus:border-[#025979] focus:outline-none"
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="outline" data-testid="cm-override-revert-cancel" className="rounded-sm text-xs" onClick={() => { setReverting(false); setReason(""); }} disabled={saving}>
                  Cancel
                </Button>
                <Button data-testid="cm-override-revert-confirm" className="rounded-sm text-xs bg-[#B45309] hover:bg-[#92400E] text-white" onClick={revert} disabled={saving}>
                  {saving ? "Reverting…" : "Revert"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {!canOverride && (
        <p className="mt-4 text-[11px] text-[#9CA3AF] italic" data-testid="cm-override-locked">
          {overrideData.is_released_to_client
            ? "This assessment has been released to the customer; create a new version to amend the recommendation."
            : "Recommendation overrides are locked for this view."}
        </p>
      )}
    </section>
  );
}



function OverviewTab({ assessment, assessmentId }) {
  // Consultant Recommendation Override layer — fetched once per assessment,
  // refreshed after every successful apply/clear. Engines are NEVER re-run.
  const { data: overrideData, refresh: refreshOverrides } =
    useEffectiveRecommendations(assessmentId);
  if (!assessment) return null;
  const recs = assessment.final_recommendation_by_site || {};
  const deploys = assessment.deployment_recommendation_by_site || {};
  const complexities = assessment.complexity_scores_by_site || {};
  const tags = assessment.explanation_tags_by_site || {};
  // Phase-3 — order per-site keys by the Warehouse Group array order (group-first,
  // matching Client Inputs & Evaluation Insights); legacy keys fall back to the
  // supply-chain ordering used before Warehouse Groups existed.
  const siteKeys = orderSiteKeys(assessment, Object.keys(recs));
  const roiOutput = assessment.roi_output || {};
  const summary = roiOutput.overall_roi_summary || {};
  // Solution Strategy — derived from the EFFECTIVE (override-aware) per-site
  // solutions so a saved Advisory Override is reflected here and on every tab.
  // Falls back to the ROI display label only when no per-site solution exists.
  const _effSol = overrideData?.effective_solution_by_site || {};
  const solutions = Array.from(new Set(siteKeys.map((s) => _effSol[s] || recs[s]).filter(Boolean)));
  const isHybrid = solutions.length > 1;
  const solutionDisplay = solutions.length === 1
    ? solutions[0]
    : solutions.length > 1
      ? `Hybrid (${solutions.map((s) => String(s).replace("SAP ", "")).join(" + ")})`
      : (summary.recommendation_display || "—");
  const deployment = assessment.overall_deployment_type || "—";
  const totalLocations = Number(summary.warehouse_count_total || 0);
  const typesCount = siteKeys.length;

  // System-side baselines (engine output) for the SmartEvaluate "System vs
  // Final" comparison cards. These come from the override endpoint payload
  // because the main assessment dict already carries the effective values
  // (engines are NOT re-run; the admin route propagates overrides into the
  // standard recommendation surface keys). When no override is active, the
  // override payload mirrors the engine output anyway.
  const systemRecsMap = overrideData?.system_solution_by_site || recs;
  const systemSolutions = Array.from(new Set(Object.values(systemRecsMap).filter(Boolean)));
  const systemSolutionDisplay = systemSolutions.length === 1
    ? systemSolutions[0]
    : (systemSolutions.length > 1
        ? `Hybrid (${systemSolutions.map((s) => String(s).replace("SAP ", "")).join(" + ")})`
        : "—");
  // ── Decision-layer derivations (display-only — engines are NEVER re-run) ──
  const maxComplexity = siteKeys.reduce((m, k) => {
    const s = Number((complexities[k] || {}).score || 0);
    return s > m ? s : m;
  }, 0);
  const ls = insightsReadLandscape(assessment);
  const m2 = assessment.module_data?.module2 || {};
  const effSolBySite = overrideData?.effective_solution_by_site || {};
  const effDepBySite = overrideData?.effective_deployment_by_site || {};
  const sysSolBySite = overrideData?.system_solution_by_site || recs;
  const sysDepBySite = overrideData?.system_deployment_by_site || deploys;
  const siteSolution = (s) => effSolBySite[s] || recs[s] || "—";
  const siteDeployment = (s) => fixDeploymentLabel(effDepBySite[s] || deploys[s] || "—");
  // Hosting RECOMMENDATION (Option C — Greenfield/Brownfield). The system
  // recommendation is the engine's architect best-fit, NOT the customer's
  // stated preference (ls.hosting_model / assessment.hosting_model /
  // preferred_hosting_model). Effective precedence:
  //   saved consultant hosting override → engine architect best-fit → Not Decided.
  // Customer preference stays in Evaluation Insights only.
  const _ha = assessment.hosting_advisory || {};
  const bestFitHosting = _ha.architect_best_fit_hosting || null;
  const overrideHosting = overrideData?.summary?.global_override?.hosting_model || null;
  const systemHosting = bestFitHosting || "Not Decided";
  const effectiveHosting = overrideHosting || bestFitHosting || "Not Decided";
  // Programme header Deployment Architecture is derived from the effective
  // (override-aware) per-site recommendations, not the stored engine overall.
  const programmeDeployment = deriveProgrammeDeployment(
    siteKeys.map(siteSolution),
    solutionDisplay,
    siteKeys.map(siteDeployment),
  );
  // System (pre-override) programme deployment — same derivation over the
  // system per-site values, so the Final card + "View Original" stay identical
  // to the header on multi-site sites (e.g. mixed → Hybrid Deployment).
  const systemProgrammeDeployment = deriveProgrammeDeployment(
    siteKeys.map((s) => sysSolBySite[s] || recs[s]),
    systemSolutionDisplay,
    siteKeys.map((s) => fixDeploymentLabel(sysDepBySite[s] || deploys[s])),
  );
  const siteOverridden = (s) => {
    const ov = overrideData?.consultant_overrides?.by_site?.[s];
    return !!(ov?.solution || ov?.deployment)
      || siteSolution(s) !== (sysSolBySite[s] || recs[s])
      || siteDeployment(s) !== (sysDepBySite[s] || deploys[s]);
  };
  const decisionBullets = buildDecisionBullets({
    siteKeys, tags, maxComplexity, totalLocations, typesCount, ls, m2,
    unitNoun: ((assessment.warehouse_groups) || []).length ? "warehouse group" : "warehouse type",
  });
  const rolloutLabel =
    ROLLOUT_APPROACH_LABELS[assessment.implementation_inputs?.rollout_mode] || null;

  // NOTE: Commercial derivations (totalInvestment / totalAnnualSavings),
  // implementation derivations (totalWeeks / totalHours / waveForSite),
  // and the cost_breakdown / waves / bySite reads have been intentionally
  // removed from this Recommendations workspace. They live where they
  // belong now — ROI tab and Implementation tab. The underlying data on
  // the assessment document (impl.cost_breakdown, impl.waves,
  // roi_output.roi_by_site, etc.) is preserved untouched.

  const nCols = Math.min(siteKeys.length || 1, 3);
  const colsClass = nCols >= 3
    ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
    : nCols === 2
      ? "grid-cols-1 md:grid-cols-2"
      : "grid-cols-1";

  return (
    <div className="space-y-5" data-testid="overview-tab">
      {/* ── SECTION 1 — Final recommendation header (the decision banner) ── */}
      <RecommendationsHeader
        solutionDisplay={solutionDisplay}
        deployment={programmeDeployment}
        isHybrid={isHybrid}
        typesCount={siteKeys.length}
        unitNoun={((assessment.warehouse_groups) || []).length ? "warehouse group" : "warehouse type"}
        coverageLocations={formatLocationCoverage(assessment, siteKeys)}
        hasOverride={!!overrideData?.summary?.has_global_override
          || (overrideData?.summary?.per_site_overridden || []).length > 0}
        hostingModel={effectiveHosting || "Not Decided"}
      />

      {siteKeys.length === 0 ? (
        <p
          data-testid="rec-empty"
          className="text-xs text-[#6B7280] italic bg-white border border-[#E5E7EB] rounded-sm p-6 text-center"
        >
          This evaluation has not been processed yet — run processing to see per-warehouse recommendations.
        </p>
      ) : (
        <>
          {/* ── SECTION 2 — Why this recommendation (executive bullets) ── */}
          {decisionBullets.length > 0 && (
            <section
              data-testid="rec-why"
              className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5 border-l-2 border-l-[#025979]"
            >
              <h3
                className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3"
                style={{ fontFamily: "Chivo, sans-serif" }}
                data-testid="rec-why-title"
              >
                Why This Recommendation
              </h3>
              <ul className="space-y-2">
                {decisionBullets.map((b, i) => (
                  <li
                    key={`why-${i}`}
                    data-testid={`rec-why-bullet-${i}`}
                    className="flex items-start gap-2 text-[12.5px] text-[#0F172A] leading-relaxed"
                  >
                    <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-[#025979] shrink-0" aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ── SECTION 3 — Warehouse recommendation summary (read-only) ── */}
          <section
            data-testid="rec-columns"
            className={`grid ${colsClass} gap-4`}
          >
            {siteKeys.map((site) => {
              const info = groupSiteInfo(assessment, site);
              return (
                <WarehouseRecommendationColumn
                  key={site}
                  site={site}
                  label={info.label}
                  eyebrow={info.eyebrow}
                  solution={siteSolution(site)}
                  deployment={siteDeployment(site)}
                  signals={tags[site] || []}
                  hasOverride={siteOverridden(site)}
                />
              );
            })}
          </section>

          {/* ── SECTION 4 — Consultant recommendation override (decision area).
              The ONLY editable surface on this tab. Engines are NEVER re-run —
              see /api/admin/.../recommendations/effective for the data
              contract. Existing override functionality is unchanged; only the
              presentation is consolidated into this dedicated decision area. ── */}
          {overrideData && (
            <section
              data-testid="rec-decision-area"
              className="bg-white border border-[#E5E7EB] rounded-sm px-5 py-5"
            >
              <h3
                className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3"
                style={{ fontFamily: "Chivo, sans-serif" }}
                data-testid="rec-decision-area-title"
              >
                Consultant Recommendation Override
              </h3>
              <RecommendationOverridePanel
                assessmentId={assessmentId}
                scope="global"
                systemSolution={systemSolutionDisplay}
                systemDeployment={systemProgrammeDeployment}
                systemHosting={systemHosting}
                effectiveSolution={
                  overrideData.summary?.global_override?.solution || solutionDisplay
                }
                effectiveDeployment={
                  overrideData.summary?.global_override?.deployment || programmeDeployment
                }
                effectiveHosting={effectiveHosting}
                hasOverride={!!overrideData.summary?.has_global_override}
                overrideEntry={overrideData.summary?.global_override}
                canOverride={!!overrideData.can_override}
                cannotOverrideReason={
                  overrideData.is_released_to_client
                    ? "This assessment has been released to the customer; create a new version to apply recommendation amendments."
                    : undefined
                }
                allowedSolutions={overrideData.allowed_solutions}
                allowedDeployments={overrideData.allowed_deployments}
                deploymentOptionsBySolution={overrideData.deployment_options_by_solution}
                allowedHostingModels={overrideData.allowed_hosting_models}
                minReasonLength={overrideData.min_reason_length}
                onChanged={refreshOverrides}
              />

              {siteKeys.length > 1 && (
                <div
                  className="mt-4 pt-4 border-t border-[#E5E7EB]"
                  data-testid="rec-per-site-overrides"
                >
                  <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-3">
                    Per-Warehouse Overrides
                  </p>
                  <div className="space-y-3">
                    {siteKeys.map((site) => (
                      <PerSiteOverridePanel
                        key={site}
                        site={site}
                        assessment={assessment}
                        overrideData={overrideData}
                        recommendation={recs[site]}
                        deployment={deploys[site]}
                        assessmentId={assessmentId}
                        onChanged={refreshOverrides}
                      />
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ── SECTION 6 — Future implementation bridge. Reads the existing
              rollout strategy when available; self-hides otherwise. ── */}
          {rolloutLabel && (
            <section
              data-testid="rec-transformation-approach"
              className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-5 py-4 flex flex-wrap items-center justify-between gap-3"
            >
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold">
                  Recommended Transformation Approach
                </p>
                <p
                  className="text-sm font-bold text-[#0F172A] mt-0.5"
                  data-testid="rec-transformation-approach-value"
                  style={{ fontFamily: "Chivo" }}
                >
                  {rolloutLabel}
                </p>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">
                Detailed rollout plan on the Implementation tab →
              </span>
            </section>
          )}

          {/* Audit trail — append-only override history. Hidden when empty. */}
          {overrideData?.override_history?.length > 0 && (
            <OverrideHistoryViewer history={overrideData.override_history} />
          )}
        </>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Recommendations tab — decision-layer helpers
// ---------------------------------------------------------------------------
// Display-only. No scoring / recommendation / deployment / ROI / override
// engine calls. The Recommendations tab answers "what should the client do?"
// — the heavy analysis ("why?") lives on the Evaluation Insights tab.

// Maps the stored implementation rollout strategy to an executive label for
// the Future Implementation Bridge. Unknown / missing modes hide the section.
const ROLLOUT_APPROACH_LABELS = {
  big_bang: "Single Wave Rollout",
  pilot_phased: "Pilot + Phased Rollout",
  parallel: "Multi-Wave Transformation",
};

// Build 3–5 concise executive decision bullets from EXISTING recommendation
// drivers (engine explanation tags) + landscape context. No scores, no
// capability analysis, no long paragraphs.
function buildDecisionBullets({ siteKeys, tags, maxComplexity, totalLocations, typesCount, ls, m2, unitNoun }) {
  if (!siteKeys || siteKeys.length === 0) return [];
  const noun = unitNoun || "warehouse type";
  const bullets = [];
  const seen = new Set();
  const add = (t) => {
    if (!t) return;
    const k = String(t).trim().toLowerCase();
    if (!k || seen.has(k) || bullets.length >= 5) return;
    seen.add(k);
    bullets.push(String(t).trim());
  };
  // Operational complexity context
  if (maxComplexity >= 3) add("High warehouse operational complexity");
  else if (maxComplexity >= 2) add("Moderate warehouse operational complexity");
  else add("Streamlined warehouse operational profile");
  // Network shape
  if (totalLocations > 1 || typesCount > 1) {
    add(`Multi-site warehouse network (${typesCount} ${noun}${typesCount === 1 ? "" : "s"})`);
  } else {
    add("Single-site warehouse operation");
  }
  // Existing SAP landscape (skip the greenfield "no existing solution" case)
  if (ls?.current_landscape_summary && !/no existing/i.test(ls.current_landscape_summary)) {
    add(ls.current_landscape_summary);
  }
  // Top engine drivers — most frequent explanation tags across sites
  const freq = {};
  siteKeys.forEach((s) => (tags[s] || []).forEach((t) => { freq[t] = (freq[t] || 0) + 1; }));
  Object.keys(freq).sort((a, b) => freq[b] - freq[a]).forEach(add);
  // Transformation intent (from existing Step-2 landscape inputs). Multi-select
  // fields may arrive as arrays, so coerce to a string before matching.
  const asText = (v) => (Array.isArray(v) ? v.join(" ") : (v == null ? "" : String(v))).toLowerCase();
  const preserve = asText(m2?.preserve_architecture_importance);
  if (preserve === "important" || preserve === "critical") add("Preserve current warehouse architecture");
  const obj = asText(m2?.primary_transformation_objective);
  if (obj.includes("cloud")) add("Cloud transformation objective");
  return bullets.slice(0, 5);
}

// Per-warehouse consultant override — relocated from the (now read-only)
// recommendation summary cards into the dedicated decision area. Wraps the
// UNCHANGED RecommendationOverridePanel with the same per-site engine-safe
// data contract (per-site > global > system precedence).
function PerSiteOverridePanel({ site, assessment, overrideData, recommendation, deployment, assessmentId, onChanged }) {
  const info = groupSiteInfo(assessment, site);
  const sitePerSiteOverride = overrideData?.consultant_overrides?.by_site?.[site];
  const systemSiteSolution =
    overrideData?.system_solution_by_site?.[site] || recommendation || "—";
  const systemSiteDeployment = fixDeploymentLabel(
    overrideData?.system_deployment_by_site?.[site] || deployment || "—");
  const effectiveSiteSolution =
    overrideData?.effective_solution_by_site?.[site] || recommendation || "—";
  const effectiveSiteDeployment = fixDeploymentLabel(
    overrideData?.effective_deployment_by_site?.[site] || deployment || "—");
  const hasSiteOverride =
    !!(sitePerSiteOverride?.solution || sitePerSiteOverride?.deployment)
    || effectiveSiteSolution !== systemSiteSolution
    || effectiveSiteDeployment !== systemSiteDeployment;
  return (
    <div data-testid={`rec-site-override-${site}`}>
      <p className="text-[11px] font-semibold text-[#111827] mb-1.5" style={{ fontFamily: "Chivo" }}>
        {info.eyebrow && (
          <span className="text-[9px] uppercase tracking-[0.15em] text-[#9CA3AF] mr-1.5">
            {String(info.eyebrow).toUpperCase()}
          </span>
        )}
        {info.label}
      </p>
      <RecommendationOverridePanel
        assessmentId={assessmentId}
        scope="site"
        siteCode={site}
        systemSolution={systemSiteSolution}
        systemDeployment={systemSiteDeployment}
        effectiveSolution={effectiveSiteSolution}
        effectiveDeployment={effectiveSiteDeployment}
        hasOverride={hasSiteOverride}
        overrideEntry={sitePerSiteOverride}
        canOverride={!!overrideData.can_override}
        cannotOverrideReason={
          overrideData.is_released_to_client
            ? "This assessment has been released to the customer; create a new version to apply recommendation amendments."
            : undefined
        }
        allowedSolutions={overrideData.allowed_solutions}
        allowedDeployments={overrideData.allowed_deployments}
        deploymentOptionsBySolution={overrideData.deployment_options_by_solution}
        minReasonLength={overrideData.min_reason_length}
        onChanged={onChanged}
        compact
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Phase-4 — Recommendations tab helpers
// ---------------------------------------------------------------------------

// Canonical currency formatter for the Recommendations surface — always
// rounds to whole units so KPI strip / Investment Breakdown / Phased ROI /
// column cells / Final Review modal all render identically. Empty / zero
// values render as the em-dash fallback so we never leak `USD 0`.
function fmtCurrency(n, currency = "USD", { emptyAsDash = false } = {}) {
  const v = Number(n);
  if (!Number.isFinite(v) || (emptyAsDash && v === 0)) return "—";
  return `${currency} ${Math.round(v).toLocaleString()}`;
}

// Business-friendly wave name: "Wave 1 (Pilot)" / "Wave 2 (Rollout Wave 1)" ...
function prettyWaveName(wave, index) {
  const i = (index ?? 0) + 1;
  const raw = String(wave?.name || "").toLowerCase();
  if (i === 1 || raw.includes("pilot")) return `Wave ${i} (Pilot)`;
  return `Wave ${i} (Rollout Wave ${i - 1})`;
}

// Wave label with go-live month: "Wave 1 (Pilot) → Go-live Month 3"
function prettyWaveLabel(wave, index) {
  const name = prettyWaveName(wave, index);
  const m = wave?.go_live_month;
  return m ? `${name} → Go-live Month ${m}` : name;
}


// ---------------------------------------------------------------------------
// Phase-4 — Recommendations tab header / kpis / breakdowns / column card
// ---------------------------------------------------------------------------
function HeaderField({ label, value, testid, emphasize }) {
  return (
    <div className="min-w-0">
      <p className="text-[9px] uppercase tracking-wider text-white/50 font-semibold">{label}</p>
      <p
        className={`mt-0.5 truncate ${emphasize ? "text-[15px] font-bold" : "text-[12.5px] font-semibold"}`}
        data-testid={testid}
        title={value}
        style={emphasize ? { fontFamily: "Chivo" } : undefined}
      >
        {value}
      </p>
    </div>
  );
}

// Coverage site count for the Recommendations header. Mirrors the Implementation
// tab's CALCULATED total (sum of Module 3 `op_warehouse_count` across
// representative sites, fallback 1 each) — NOT the client-entered warehouse-count
// band. Prefers the persisted, reconciled `implementation_inputs.total_warehouses`
// so both tabs always agree.
function formatLocationCoverage(assessment, siteKeys) {
  let total = Number(assessment?.implementation_inputs?.total_warehouses);
  if (!Number.isFinite(total) || total <= 0) {
    // Phase-3 — prefer the Warehouse Group namespace; fall back to legacy module3.
    const groups = assessment?.module_data?.module3_groups || {};
    const m3 = assessment?.module_data?.module3 || {};
    const source = Object.keys(groups).length ? groups : m3;
    const keys = (siteKeys && siteKeys.length) ? siteKeys : Object.keys(source);
    total = keys.reduce((n, k) => {
      const c = Number((source?.[k] || {}).op_warehouse_count);
      return n + (Number.isFinite(c) && c > 0 ? c : 1);
    }, 0);
  }
  if (total > 0) return `${total} site${total === 1 ? "" : "s"}`;
  return "Sites not specified";
}

function RecommendationsHeader({ solutionDisplay, deployment, isHybrid, typesCount, coverageLocations, hasOverride, hostingModel, unitNoun }) {
  // Program-level recommendation summary. Solution + Deployment reflect the
  // EFFECTIVE (consultant-override-aware) values; Hosting Model and
  // Transformation Pattern are program-level context with safe fallbacks.
  return (
    <section
      data-testid="rec-header"
      className="bg-gradient-to-r from-[#025979] to-[#024a63] text-white rounded-md p-4"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
          Recommended Transformation Program
        </p>
        <div className="flex items-center gap-2 shrink-0">
          {isHybrid && (
            <span
              className="inline-flex items-center px-2 py-0.5 bg-white/20 rounded-sm text-[10px] font-medium"
              data-testid="rec-header-hybrid-chip"
            >
              Hybrid
            </span>
          )}
          {hasOverride && (
            <span
              data-testid="rec-header-override-badge"
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FFFBEB] text-[#92400E] rounded-sm text-[10px] font-bold uppercase tracking-wider border border-[#FCD34D]"
              title="Consultant override applied"
            >
              Consultant Override Applied
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* 4-column recommendation summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 flex-1 min-w-0">
          <HeaderField label="Solution Strategy" value={solutionDisplay} testid="rec-header-solution" emphasize />
          <HeaderField label="Deployment Architecture" value={deployment || "—"} testid="rec-header-deployment" />
          <HeaderField label="Hosting Recommendation" value={hostingModel || "Not Decided"} testid="rec-header-hosting" />
        </div>
        {/* Coverage — pinned far right */}
        <div className="text-right shrink-0 sm:pl-4 sm:border-l sm:border-white/10">
          <p className="text-[9px] uppercase tracking-wider text-white/50 font-semibold">Coverage</p>
          <p className="text-[12.5px] font-semibold mt-0.5" data-testid="rec-header-coverage">
            {typesCount || 0} {unitNoun || "warehouse type"}{typesCount === 1 ? "" : "s"} across {coverageLocations}
          </p>
        </div>
      </div>
    </section>
  );
}

function RecommendationsKpis({ summary, currency, totalWeeks, totalHours }) {
  const invested = Number(summary.total_investment || 0);
  const annual = Number(summary.total_annual_savings || 0);
  const fmtMoney = (n) => fmtCurrency(n, currency, { emptyAsDash: true });
  const months = totalWeeks > 0 ? (totalWeeks / 4.345).toFixed(1) : "0";

  // Primary metrics — equal-weight cards.
  const primary = (label, value, testid) => (
    <div
      data-testid={testid}
      className="bg-white border border-[#E5E7EB] rounded-sm px-3 py-2.5"
    >
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</p>
      <p className="text-base font-bold text-[#0F172A] mt-1">{value}</p>
    </div>
  );
  // Hero metric — 3-Year ROI (slightly larger / accent background).
  const hero = (label, value, testid) => (
    <div
      data-testid={testid}
      className="bg-[#025979] text-white rounded-sm px-3 py-2.5 shadow-sm"
    >
      <p className="text-[10px] uppercase tracking-wider text-white/70">{label}</p>
      <p className="text-lg font-extrabold mt-0.5" style={{ fontFamily: "Chivo" }}>{value}</p>
    </div>
  );
  // Secondary metrics — visually lighter (ghost-style).
  const secondary = (label, value, testid) => (
    <div
      data-testid={testid}
      className="bg-[#F8FAFC] border border-dashed border-[#E5E7EB] rounded-sm px-3 py-2.5"
    >
      <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{label}</p>
      <p className="text-sm font-semibold text-[#475569] mt-1">{value}</p>
    </div>
  );

  return (
    <section
      data-testid="rec-kpis"
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
    >
      {primary("Total Investment", fmtMoney(invested), "rec-kpi-investment")}
      {primary("Annual Savings", fmtMoney(annual), "rec-kpi-savings")}
      {primary(
        "Payback",
        summary.overall_payback_months != null
          ? `${Number(summary.overall_payback_months).toFixed(1)} mo`
          : "—",
        "rec-kpi-payback",
      )}
      {hero(
        "3-Year ROI",
        summary.overall_roi_percent != null
          ? `${Number(summary.overall_roi_percent).toFixed(1)}%`
          : "—",
        "rec-kpi-roi",
      )}
      {secondary(
        "Timeline",
        totalWeeks > 0 ? `${Math.round(totalWeeks)} wks · ${months} mo` : "—",
        "rec-kpi-timeline",
      )}
      {secondary(
        "Effort",
        totalHours > 0 ? `${Number(totalHours).toLocaleString()} hrs` : "—",
        "rec-kpi-effort",
      )}
    </section>
  );
}

function InvestmentBreakdownBlock({ costBreakdown }) {
  const cb = costBreakdown || {};
  const ot = cb.one_time || {};
  const rc = cb.recurring || {};
  const currency = cb.currency || "USD";
  const fmt = (n) => fmtCurrency(n, currency);
  const totalOT = Number(ot.total_one_time_investment || 0)
    || (Number(ot.implementation_fees || 0) + Number(ot.hardware_devices || 0)
      + Number(ot.integration_custom_dev || 0) + Number(ot.internal_business_effort || 0));
  const hasOT = totalOT > 0;
  const hasOP = Number(rc.total_annual_recurring || 0) > 0
    || Number(rc.sap_license_subscription_delta || 0) > 0
    || Number(rc.annual_support_ams || 0) > 0;
  if (!hasOT && !hasOP) {
    return (
      <section
        data-testid="rec-investment-breakdown-empty"
        className="bg-white border border-dashed border-[#E5E7EB] rounded-sm p-4 text-center"
      >
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-1">
          Investment Breakdown
        </p>
        <p className="text-[11px] text-[#9CA3AF]">
          Cost-model inputs not entered yet. Fill in the Implementation tab to surface the breakdown.
        </p>
      </section>
    );
  }
  const pct = (n) => totalOT > 0 ? `${((Number(n || 0) / totalOT) * 100).toFixed(0)}%` : "—";

  const capexRow = (label, value, share, testid) => (
    <div className="flex items-baseline justify-between py-1 gap-3" data-testid={testid}>
      <span className="text-[11px] text-[#6B7280] min-w-0 truncate">{label}</span>
      <span className="flex items-baseline gap-2 whitespace-nowrap">
        <span className="text-[10px] text-[#9CA3AF] tabular-nums">{share}</span>
        <span className="text-[12px] font-semibold text-[#0F172A] tabular-nums">{value}</span>
      </span>
    </div>
  );
  const opexRow = (label, value, testid) => (
    <div className="flex items-baseline justify-between py-1" data-testid={testid}>
      <span className="text-[11px] text-[#6B7280]">{label}</span>
      <span className="text-[12px] font-semibold text-[#0F172A] tabular-nums">{value}</span>
    </div>
  );
  return (
    <section
      data-testid="rec-investment-breakdown"
      className="bg-white border border-[#E5E7EB] rounded-sm p-4"
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-2">
        Investment Breakdown
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#0F172A] mb-1">
            One-Time Investment (CAPEX)
          </p>
          {capexRow("Implementation Fees", fmt(ot.implementation_fees), pct(ot.implementation_fees), "rec-cost-impl-fees")}
          {capexRow("Hardware & Devices", fmt(ot.hardware_devices), pct(ot.hardware_devices), "rec-cost-hw")}
          {capexRow("Integration / Custom Dev", fmt(ot.integration_custom_dev), pct(ot.integration_custom_dev), "rec-cost-integration")}
          {capexRow("Internal Business Effort", fmt(ot.internal_business_effort), pct(ot.internal_business_effort), "rec-cost-internal")}
          <div className="mt-1 pt-1.5 border-t border-[#E5E7EB]">
            <div className="flex items-baseline justify-between py-1" data-testid="rec-cost-total-onetime">
              <span className="text-[11px] font-semibold text-[#111827]">Total One-Time Investment</span>
              <span className="text-[12px] font-bold text-[#025979] tabular-nums">{fmt(totalOT)}</span>
            </div>
          </div>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#0F172A] mb-1">
            Annual Recurring (OPEX)
          </p>
          {opexRow("SAP License / Subscription Δ", fmt(rc.sap_license_subscription_delta), "rec-cost-license")}
          {opexRow("Annual Support / AMS", fmt(rc.annual_support_ams), "rec-cost-ams")}
          <div className="mt-1 pt-1.5 border-t border-[#E5E7EB]">
            <div className="flex items-baseline justify-between py-1" data-testid="rec-cost-total-recurring">
              <span className="text-[11px] font-semibold text-[#111827]">Total Annual Recurring</span>
              <span className="text-[12px] font-bold text-[#025979] tabular-nums">{fmt(rc.total_annual_recurring)}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PhasedRoiStrip({ summary, currency }) {
  if (!summary.phased_enabled || !summary.savings_by_year) return null;
  const sby = summary.savings_by_year;
  const fmt = (n) => fmtCurrency(n, currency);
  const cell = (label, val, testid) => (
    <div className="bg-white border border-[#E5E7EB] rounded-sm px-3 py-2" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</p>
      <p className="text-sm font-bold text-[#0F172A] mt-1">{val}</p>
    </div>
  );
  return (
    <section
      data-testid="rec-phased-roi"
      className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-4"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979]">
            Phased ROI Realization
          </p>
          <p className="text-[11px] text-[#6B7280]">
            Savings realized progressively as warehouses go live.
          </p>
        </div>
        <p className="text-[11px] text-[#475569]">
          Phased payback:{" "}
          <span className="font-semibold text-[#0F172A]" data-testid="rec-phased-payback">
            {summary.phased_payback_months != null
              ? `${Number(summary.phased_payback_months).toFixed(1)} mo`
              : "—"}
          </span>
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {cell("Year 1", fmt(sby.year1), "rec-phased-y1")}
        {cell("Year 2", fmt(sby.year2), "rec-phased-y2")}
        {cell("Year 3", fmt(sby.year3), "rec-phased-y3")}
      </div>
    </section>
  );
}

function RecImplementationSummary({ impl, waves, totalWeeks }) {
  const rolloutLabel = ({
    pilot_phased: "Pilot → Phased",
    parallel: "Parallel",
    big_bang: "Big Bang",
  })[impl.rollout_mode] || impl.rollout_mode || "—";
  const months = totalWeeks > 0 ? (totalWeeks / 4.345).toFixed(1) : "0";
  return (
    <section
      data-testid="rec-impl-summary"
      className="bg-white border border-[#E5E7EB] rounded-sm p-4"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979]">
          Implementation Summary
        </p>
        <p className="text-[11px] text-[#6B7280]">
          Rollout: <span className="font-semibold text-[#0F172A]" data-testid="rec-impl-rollout">{rolloutLabel}</span>
        </p>
      </div>
      <p className="text-[11px] text-[#475569] mb-2" data-testid="rec-impl-duration-line">
        Total duration:{" "}
        <span className="font-semibold text-[#0F172A]" data-testid="rec-impl-duration">
          {totalWeeks > 0 ? `${Math.round(totalWeeks)} weeks (${months} months)` : "—"}
        </span>
      </p>
      {waves.length === 0 ? (
        <p className="text-[11px] text-[#9CA3AF] italic">
          No waves defined. Regenerate waves from the Implementation tab.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="rec-impl-waves">
          {waves.map((w, i) => (
            <div
              key={w.id || i}
              data-testid={`rec-impl-wave-${i}`}
              className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2.5 border-l-2 border-l-[#025979]"
            >
              <p className="text-[11px] font-semibold text-[#0F172A] leading-snug">
                {prettyWaveLabel(w, i)}
              </p>
              {Array.isArray(w.scope_labels) && w.scope_labels.length > 0 && (
                <p className="mt-1 text-[11px] text-[#475569]">
                  {w.scope_labels.join(" + ")}
                </p>
              )}
              <p className="mt-1.5 text-[10px] uppercase tracking-wider text-[#9CA3AF]">
                Weeks {w.start_week ?? 0}–
                {w.end_week ?? ((w.start_week || 0) + (w.duration_weeks || 0))}
              </p>
            </div>
          ))}
        </div>
      )}
      {waves.length > 0 && (
        <p
          data-testid="rec-impl-insight"
          className="mt-3 pt-2 border-t border-[#E5E7EB] text-[11px] text-[#475569] italic"
        >
          Phased rollout reduces risk and enables early value realization.
        </p>
      )}
    </section>
  );
}

// Read-only warehouse recommendation summary card (decision view). Shows only
// the decision essentials — warehouse type, recommended solution, deployment
// architecture, and up to 4 key drivers. All scoring / capability / confidence
// detail and the editable per-site override have moved to (respectively) the
// Evaluation Insights tab and the dedicated Consultant Override decision area.
function WarehouseRecommendationColumn({ site, label, eyebrow, solution, deployment, signals, hasOverride }) {
  const drivers = (signals || []).slice(0, 4);
  const title = label || warehouseLabel(site);
  return (
    <article
      data-testid={`rec-col-${site}`}
      className="bg-white border border-[#E5E7EB] rounded-sm p-4 flex flex-col gap-3 border-l-2 border-l-[#025979]"
    >
      <header className="space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {eyebrow && (
              <p
                className="text-[9px] uppercase tracking-[0.15em] text-[#9CA3AF]"
                data-testid={`rec-col-${site}-eyebrow`}
              >
                {String(eyebrow).toUpperCase()}
              </p>
            )}
            <h3
              className="text-sm font-bold text-[#111827]"
              data-testid={`rec-col-${site}-title`}
              style={{ fontFamily: "Chivo" }}
            >
              {title}
            </h3>
          </div>
          {hasOverride && (
            <span
              data-testid={`rec-col-${site}-override-flag`}
              className="px-1.5 py-0.5 bg-[#E0F2FE] border border-[#0093CB]/40 text-[#025979] rounded-sm text-[9px] font-bold uppercase tracking-wider"
              title="Consultant override applied"
            >
              Overridden
            </span>
          )}
        </div>
        <p
          className="text-[15px] font-bold text-[#025979]"
          data-testid={`rec-col-${site}-solution`}
          style={{ fontFamily: "Chivo" }}
        >
          {solution || "—"}
        </p>
        <p className="text-[11px]">
          <span className="text-[9px] uppercase tracking-wider text-[#9CA3AF] mr-1.5">Deployment</span>
          <span className="text-[#475569]" data-testid={`rec-col-${site}-deployment`}>{deployment || "—"}</span>
        </p>
      </header>

      {drivers.length > 0 && (
        <div data-testid={`rec-col-${site}-drivers`}>
          <p className="text-[9px] uppercase tracking-wider text-[#9CA3AF] mb-1">Key Drivers</p>
          <ul className="space-y-1">
            {drivers.map((d, i) => (
              <li
                key={`${site}-drv-${i}`}
                data-testid={`rec-col-${site}-driver-${i}`}
                className="flex items-start gap-1.5 text-[11px] text-[#374151] leading-relaxed"
              >
                <span className="mt-[5px] w-1 h-1 rounded-full bg-[#025979] shrink-0" aria-hidden="true" />
                {d}
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}

/**
 * Combined recommendation-narrative block shown directly under the strategy
 * highlight. Per-site narrative text + business-driver tags are unchanged —
 * this is purely a layout reshuffle so the report flows "What → Why → Details".
 */
function RecommendationNarrativeSection({ siteKeys, narratives }) {
  const sitesWithNarrative = siteKeys.filter((s) => narratives[s]);
  if (sitesWithNarrative.length === 0) return null;
  return (
    <section
      data-testid="recommendation-narrative-section"
      className="bg-white border border-[#E5E7EB] rounded-sm p-4 space-y-4"
    >
      <div>
        <p className="text-[11px] text-[#025979] uppercase tracking-wider font-semibold">
          Recommendation Narrative
        </p>
        <p className="text-[11px] text-[#6B7280] mt-0.5">
          Why the scoring engine arrived at this strategy — in plain language.
        </p>
      </div>
      <div className={sitesWithNarrative.length > 1 ? "grid grid-cols-1 md:grid-cols-2 gap-4" : ""}>
        {sitesWithNarrative.map((site) => {
          const narrative = narratives[site];
          const drivers = narrative.business_drivers || [];
          return (
            <div key={site} data-testid={`narrative-${site}`} className="space-y-2">
              {sitesWithNarrative.length > 1 && (
                <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">
                  {warehouseLabel(site)}
                </p>
              )}
              {narrative.narrative_summary && (
                <p
                  data-testid={`narrative-summary-${site}`}
                  className="text-[12px] text-[#111827] leading-relaxed"
                >
                  {narrative.narrative_summary}
                </p>
              )}
              {drivers.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid={`narrative-drivers-${site}`}>
                  {drivers.map((d, idx) => (
                    <span
                      key={`${site}-drv-${idx}`}
                      title={d.description}
                      className="inline-flex items-center px-2 py-0.5 bg-[#F1F5F9] text-[#334155] border border-[#334155]/15 rounded-sm text-[10px] font-medium"
                    >
                      {d.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

const SITE_FIELDS = [
  { key: "avg_fte_cost", label: "Avg FTE Cost", step: "1", placeholder: "e.g. 55000" },
  { key: "error_cost_per_order", label: "Error Cost per Order", step: "0.01", placeholder: "e.g. 15" },
  { key: "default_returns_per_month", label: "Default Returns Processed per Month", step: "1", placeholder: "e.g. 100" },
  { key: "warehouse_count_override", label: "Warehouse Count Override", step: "1", placeholder: "(optional)" },
];
const GLOBAL_FIELDS = [
  { key: "currency", label: "Currency", type: "text", placeholder: "USD" },
  { key: "exchange_rate", label: "Exchange Rate", type: "number", step: "0.0001" },
  { key: "year1_realization_pct", label: "Year 1 Realization %", type: "number", step: "1" },
  { key: "confidence_multiplier_pct", label: "Confidence Multiplier %", type: "number", step: "1" },
];

function ClientDataRow({ label, value, suffix }) {
  const display = value === null || value === undefined || value === "" ? "—" : String(value);
  return (
    <div className="flex items-center justify-between py-1 text-[11px]">
      <span className="text-[#6B7280]">{label}</span>
      <span className={display === "—" ? "text-[#9CA3AF] italic" : "text-[#111827] font-medium"}>{display}{suffix && display !== "—" ? ` ${suffix}` : ""}</span>
    </div>
  );
}

function SiteRoiInputsBlock({ siteKey, recommendation, clientData, defaults, values, onChange, embedded = false }) {
  const label = warehouseLabel(siteKey);
  return (
    <div
      className={embedded
        ? "bg-white border border-[#E5E7EB] rounded-sm p-3"
        : "bg-white border border-[#E5E7EB] rounded-sm p-4"}
      data-testid={`roi-site-block-${siteKey}`}
    >
      {!embedded && (
        <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#E5E7EB]">
          <div>
            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Warehouse Type</p>
            <p className="text-sm font-bold text-[#111827]">{label}</p>
          </div>
          {recommendation && (
            <span className="px-2 py-0.5 bg-[#025979] text-white rounded-sm text-[10px] font-medium">{recommendation}</span>
          )}
        </div>
      )}

      {/* Client-provided data (read-only) */}
      <div className="mb-4 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-3" data-testid={`client-data-${siteKey}`}>
        <p className="text-[10px] font-semibold text-[#025979] uppercase tracking-wider mb-2">Client Provided Data (Read-only)</p>
        <ClientDataRow label="Number of warehouses" value={clientData?.warehouse_count} />
        <ClientDataRow label="Orders per year (per warehouse)" value={clientData?.orders_per_year} />
        <ClientDataRow label="Staff size (per warehouse)" value={clientData?.staff_size} />
        <ClientDataRow label="Returns processed per month" value={clientData?.returns_per_month} />
        <ClientDataRow label="Average inventory value" value={clientData?.inventory_value} />
      </div>

      {/* Consultant overrides */}
      <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-2">Consultant Overrides</p>
      <p className="text-[10px] text-[#9CA3AF] italic mb-3">
        These values act as consultant defaults / overrides for this warehouse type. Client-provided operational values will be used first when available.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {SITE_FIELDS.map(f => {
          const val = values?.[f.key];
          const def = defaults?.[f.key];
          const differs = val !== undefined && val !== null && val !== "" && def !== undefined && String(val) !== String(def);
          return (
            <div key={f.key}>
              <label className="text-xs font-medium text-[#374151] mb-1 block flex items-center gap-1">
                {f.label}
                {differs && <span className="text-[9px] text-[#025979] bg-[#E0F2FE] px-1 py-0.5 rounded-sm">overridden</span>}
              </label>
              <Input
                data-testid={`roi-site-${siteKey}-${f.key}`}
                type="number"
                step={f.step}
                value={val ?? ""}
                placeholder={def !== undefined && def !== null ? String(def) : f.placeholder}
                onChange={e => onChange(siteKey, f.key, e.target.value)}
                className="rounded-sm text-sm"
              />
              {def !== undefined && def !== null && (
                <p className="text-[10px] text-[#9CA3AF] mt-0.5">Default: {String(def)}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Merged ROI tab — auto-calculates on every input change with a short debounce
 * and always reflects the latest computed ROI. Replaces the previous
 * `RoiInputsTab` + `RoiOutputTab` split so consultants can never stare at a
 * stale output: top = live output, bottom = editable inputs.
 */
// ---------------------------------------------------------------------------
// ROI tab — CFO-grade financial decision view
// ---------------------------------------------------------------------------
// TopValueDriversStrip — aggregated "where does annual savings come from" row
// set. Sits between the KPI tiles and the Investment Breakdown on the ROI
// tab. Reads straight from `summary.combined_top_value_drivers` (backend
// ROI engine output); percentages are computed vs total_annual_savings so
// values match the headline Annual Savings figure exactly. Silently hides
// when the drivers aren't populated yet.
function TopValueDriversStrip({ summary, currency, exchangeRate = 1 }) {
  const total = Number(summary?.total_annual_savings) || 0;
  const drivers = Array.isArray(summary?.combined_top_value_drivers)
    ? summary.combined_top_value_drivers
    : [];
  if (drivers.length === 0 || total <= 0) return null;
  const fmt = (v) => fmtCurrency((Number(v) || 0) * exchangeRate, currency);
  const top = drivers.slice(0, 5);
  const maxAmt = Math.max(0, ...top.map((d) => Number(d.amount) || 0));
  return (
    <section
      data-testid="cfo-top-value-drivers"
      className="bg-white border border-[#E5E7EB] rounded-sm"
    >
      <header className="flex items-baseline justify-between flex-wrap gap-2 px-5 pt-4 pb-2">
        <h3 className="text-[11px] uppercase tracking-wider text-[#025979] font-semibold" style={{ fontFamily: "Chivo, sans-serif" }}>
          Top Value Drivers
        </h3>
        <p className="text-[10.5px] text-[#6B7280]">Aggregated across all warehouse types · % of annual savings</p>
      </header>
      <ol>
        {top.map((d, i) => {
          const amt = Number(d.amount) || 0;
          const pct = total > 0 ? Math.round((amt / total) * 100) : 0;
          const barPct = maxAmt > 0 ? Math.round((amt / maxAmt) * 100) : 0;
          return (
            <li
              key={d.key || d.label || i}
              data-testid={`cfo-driver-${i}`}
              className="flex items-center gap-4 px-5 py-2.5 border-t border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors"
            >
              <span className="w-5 text-[12px] font-semibold text-[#025979] tabular-nums">{i + 1}.</span>
              <div className="flex-1 min-w-0">
                <p
                  data-testid={`cfo-driver-label-${i}`}
                  className="text-[12.5px] text-[#0F172A] font-medium truncate"
                  title={d.label}
                >
                  {d.label}
                </p>
                <div className="h-1.5 mt-1 bg-[#F3F4F6] rounded-sm overflow-hidden">
                  <div
                    className="h-full bg-emerald-500"
                    style={{ width: `${barPct}%` }}
                  />
                </div>
              </div>
              <span
                data-testid={`cfo-driver-pct-${i}`}
                className="w-12 text-right text-[12.5px] font-semibold text-[#065F46] tabular-nums"
              >
                {pct}%
              </span>
              <span
                data-testid={`cfo-driver-value-${i}`}
                className="w-32 text-right text-[12.5px] font-bold text-[#0F172A] tabular-nums"
              >
                {fmt(amt)}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function CfoRoiView({ summary, currency, assessment, bySite, exchangeRate = 1 }) {
  const fmt = (n) => fmtCurrency(n, currency);
  const payback = summary?.overall_payback_months;
  const roiPct = summary?.overall_roi_percent;
  const sby = summary?.savings_by_year || {};
  // The consultant ROI tab is a WORKING tool, not a CFO presentation —
  // the summary sentence / decision checklist / program-level Top Savings
  // Drivers card are intentionally NOT surfaced here. The client-facing
  // dashboard and Executive Summary PDF already cover that narrative.
  const isPhased = !!summary?.phased_enabled && summary?.savings_by_year;

  return (
    <div className="space-y-5" data-testid="cfo-roi-view">
      <InvestmentSourceBanner summary={summary} currency={currency} />

      {/* KPI hero strip — 4 metrics, ROI = hero, Payback = secondary highlight. */}
      <section
        data-testid="cfo-kpis"
        className="grid grid-cols-1 md:grid-cols-4 gap-3"
      >
        <div
          data-testid="cfo-kpi-investment"
          className="bg-white border border-[#E5E7EB] rounded-sm p-4"
        >
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280]">
            Total Investment (one-time)
          </p>
          <p className="text-base font-bold text-[#0F172A] mt-1 tabular-nums">
            {fmt(summary?.total_investment)}
          </p>
        </div>
        <div
          data-testid="cfo-kpi-savings"
          className="bg-white border border-[#E5E7EB] rounded-sm p-4"
        >
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280]">
            Annual Savings
          </p>
          <p className="text-base font-bold text-[#0F172A] mt-1 tabular-nums">
            {fmt(summary?.total_annual_savings)}
          </p>
        </div>
        <div
          data-testid="cfo-kpi-payback"
          className="bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-sm p-4"
        >
          <p className="text-[10px] uppercase tracking-wider text-[#92400E]">
            Payback Period
          </p>
          <p className="text-lg font-bold text-[#0F172A] mt-0.5 tabular-nums">
            {payback != null ? `${Number(payback).toFixed(1)} mo` : "—"}
          </p>
        </div>
        <div
          data-testid="cfo-kpi-roi"
          className="bg-[#025979] text-white rounded-sm p-4 shadow-sm"
        >
          <p className="text-[10px] uppercase tracking-wider text-white/70">
            3-Year ROI
          </p>
          <p
            className="text-2xl font-extrabold mt-0.5 tabular-nums"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {roiPct != null ? `${Number(roiPct).toFixed(1)}%` : "—"}
          </p>
        </div>
      </section>

      {/* TOP VALUE DRIVERS — aggregated across all warehouse types,
          explains WHERE the annual savings are coming from. Sits between
          the KPI strip and the Investment Breakdown to answer the CFO's
          first follow-up question. */}
      <TopValueDriversStrip
        summary={summary}
        currency={currency}
        exchangeRate={exchangeRate}
      />

      {/* Investment Breakdown — consultant working view. Top Savings
          Drivers + CFO-style decision checklist + "recommend to proceed"
          line are intentionally NOT rendered here — those belong in the
          Executive Summary PDF / Client Dashboard, not in the consultant's
          working tool. Per-warehouse top value drivers already appear
          inside each warehouse block below. */}
      <CfoInvestmentBreakdown
        assessment={assessment}
        currency={currency}
      />

      {/* Benefit Realization Timeline — program-level payback is already
          shown at the top KPI strip, so we intentionally DO NOT render a
          second payback figure here (phased vs steady-state mismatch
          confuses CFOs). Year 2 / Year 3 labelled as steady-state when
          they match Year 2. */}
      {isPhased && (
        <section
          data-testid="cfo-phased-roi"
          className="bg-white border border-[#E5E7EB] rounded-sm p-4"
        >
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-2">
            Benefit Realization Timeline
          </p>
          {(() => {
            const y1 = Number(sby.year1 || 0);
            const y2 = Number(sby.year2 || 0);
            const y3 = Number(sby.year3 || 0);
            // Year N is "steady-state" if it's within 2% of the largest.
            const peak = Math.max(y1, y2, y3, 1);
            const isSteady = (v) => peak > 0 && Math.abs(v - peak) / peak <= 0.02;
            const tagFor = (v, isY1) => isY1
              ? "Ramp-up benefit"
              : (isSteady(v) ? "Steady-state benefit" : "Ramp-up benefit");
            const cards = [
              ["Year 1", y1, tagFor(y1, true), "cfo-phased-y1"],
              ["Year 2", y2, tagFor(y2, false), "cfo-phased-y2"],
              ["Year 3", y3, tagFor(y3, false), "cfo-phased-y3"],
            ];
            return (
              <div className="grid grid-cols-3 gap-3">
                {cards.map(([label, value, tag, tid]) => (
                  <div
                    key={tid}
                    data-testid={tid}
                    className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-3 py-2"
                  >
                    <p className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</p>
                    <p className="text-base font-bold text-[#0F172A] mt-0.5 tabular-nums">{fmt(value)}</p>
                    <p className="text-[10px] text-[#025979] mt-0.5">{tag}</p>
                  </div>
                ))}
              </div>
            );
          })()}
          <p className="mt-2 text-[11px] text-[#475569] italic">
            Year 1 reflects rollout ramp-up. Year 2+ reflects full steady-state benefits.
          </p>
        </section>
      )}

      {/* CFO-style "Decision View" checklist intentionally removed — this
          tab is a consultant WORKING view. Decision framing lives in the
          Executive Summary PDF and Client Dashboard. */}
    </div>
  );
}

function CfoInvestmentBreakdown({ assessment, currency }) {
  const impl = assessment?.implementation_inputs || {};
  const cb = impl.cost_breakdown || {};
  const ot = cb.one_time || {};
  const rc = cb.recurring || {};
  const fmt = (n) => fmtCurrency(n, currency);
  const hasOT = Number(ot.total_one_time_investment || 0) > 0;
  const hasOP = Number(rc.total_annual_recurring || 0) > 0;
  if (!hasOT && !hasOP) {
    return (
      <section
        data-testid="cfo-investment-breakdown-empty"
        className="bg-white border border-dashed border-[#E5E7EB] rounded-sm p-4 text-center"
      >
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-1">
          Investment Breakdown
        </p>
        <p className="text-[11px] text-[#9CA3AF]">
          Cost-model inputs not entered yet.
        </p>
      </section>
    );
  }
  const row = (label, value, testid, bold = false) => (
    <div
      className="flex items-baseline justify-between py-1"
      data-testid={testid}
    >
      <span className={`text-[11px] ${bold ? "font-semibold text-[#0F172A]" : "text-[#6B7280]"}`}>
        {label}
      </span>
      <span className={`text-[12px] tabular-nums ${bold ? "font-bold text-[#025979]" : "font-semibold text-[#0F172A]"}`}>
        {value}
      </span>
    </div>
  );
  return (
    <section
      data-testid="cfo-investment-breakdown"
      className="bg-white border border-[#E5E7EB] rounded-sm p-4"
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-2">
        Investment Breakdown
      </p>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#0F172A] mb-1">
        One-Time (CAPEX)
      </p>
      {row("Implementation", fmt(ot.implementation_fees), "cfo-cost-impl")}
      {row("Hardware", fmt(ot.hardware_devices), "cfo-cost-hw")}
      {row("Integration", fmt(ot.integration_custom_dev), "cfo-cost-integration")}
      {row("Internal Effort", fmt(ot.internal_business_effort), "cfo-cost-internal")}
      <div className="mt-1 pt-1.5 border-t border-[#E5E7EB]">
        {row("Total One-Time", fmt(ot.total_one_time_investment), "cfo-cost-total-onetime", true)}
      </div>
      <p className="mt-3 text-[10px] font-semibold uppercase tracking-wider text-[#0F172A] mb-1">
        Annual Recurring (OPEX)
      </p>
      {row("License", fmt(rc.sap_license_subscription_delta), "cfo-cost-license")}
      {row("AMS", fmt(rc.annual_support_ams), "cfo-cost-ams")}
      <div className="mt-1 pt-1.5 border-t border-[#E5E7EB]">
        {row("Total Annual Recurring", fmt(rc.total_annual_recurring), "cfo-cost-total-recurring", true)}
      </div>
    </section>
  );
}

function CfoTopSavingsDrivers({ drivers, currency }) {
  const fmt = (n) => fmtCurrency(n, currency);
  if (!drivers || drivers.length === 0) {
    return (
      <section
        data-testid="cfo-top-savings-empty"
        className="bg-white border border-dashed border-[#E5E7EB] rounded-sm p-4 text-center"
      >
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-1">
          Top Savings Drivers
        </p>
        <p className="text-[11px] text-[#9CA3AF]">
          Value drivers not calculated yet.
        </p>
      </section>
    );
  }
  return (
    <section
      data-testid="cfo-top-savings-drivers"
      className="bg-white border border-[#E5E7EB] rounded-sm p-4"
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-2">
        Top Savings Drivers
      </p>
      <ul className="space-y-2">
        {drivers.map((d, i) => (
          <li
            key={d.key || i}
            data-testid={`cfo-driver-${i}`}
            className="flex items-baseline justify-between gap-3 border-l-2 border-[#025979] pl-3 py-0.5"
          >
            <span className="text-[12px] text-[#0F172A] font-medium truncate">
              {d.label}
            </span>
            <span className="text-[12px] font-semibold text-[#025979] tabular-nums whitespace-nowrap">
              {fmt(d.amount)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}


function InvestmentSourceBanner({ summary, currency }) {
  if (!summary) return null;
  const isLegacy = summary.investment_source !== "implementation_plan";
  const oneTime = summary.total_one_time_investment ?? summary.total_investment ?? 0;
  const recurring = summary.total_annual_recurring ?? 0;
  const fmt = (n) => {
    if (n == null || Number.isNaN(Number(n))) return "—";
    return `${currency || "USD"} ${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };
  return (
    <div
      data-testid="roi-investment-source-banner"
      className={`flex flex-wrap items-center justify-between gap-3 rounded-sm border px-4 py-3 ${
        isLegacy ? "bg-amber-50 border-amber-200" : "bg-[#E0F2FE] border-[#025979]/30"
      }`}
    >
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
        <div data-testid="roi-banner-one-time">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] mr-2">Total One-Time Investment</span>
          <span className="text-sm font-bold text-[#0F172A]">{fmt(oneTime)}</span>
        </div>
        <div data-testid="roi-banner-recurring">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] mr-2">Total Annual Recurring</span>
          <span className="text-sm font-semibold text-[#0F172A]">{fmt(recurring)}</span>
        </div>
      </div>
      {isLegacy ? (
        <p className="text-[11px] text-amber-900 max-w-md">
          Investment is still derived from the legacy per-site × warehouse count engine.
          Fill the <strong>Cost Model</strong> on the Implementation tab to switch ROI to the
          new single-source-of-truth investment base.
        </p>
      ) : (
        <p className="text-[11px] text-[#025979] max-w-md">
          Investment is sourced from <strong>Implementation → Cost Model</strong>. Edit cost
          buckets there; ROI recomputes automatically on save.
        </p>
      )}
    </div>
  );
}

function RoiTab({ assessmentId, assessment, readOnly }) {
  const [view, setView] = useState(null);
  const [output, setOutput] = useState(null);
  const [globalValues, setGlobalValues] = useState({});
  const [siteValues, setSiteValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [msg, setMsg] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [exchangeRate, setExchangeRate] = useState(1);
  // Counter bumped by every user edit. The recompute effect fires only when
  // this changes (not on initial load) → no accidental first-render save.
  const [pending, setPending] = useState(0);
  const isMountedRef = useRef(true);
  // Refs that always hold the freshest user edits + a "dirty" flag so we can
  // flush any unsaved edits synchronously on unmount (e.g. the consultant
  // switches to another tab before the 500ms debounced save has fired).
  const latestPayloadRef = useRef(null);
  const hasPendingSaveRef = useRef(false);

  const buildPayload = (gvals, svals) => {
    const payload = { global_inputs: {}, site_inputs: {} };
    GLOBAL_FIELDS.forEach((f) => {
      const val = gvals[f.key];
      if (val === "" || val === null || val === undefined) return;
      payload.global_inputs[f.key] = f.type === "number" ? Number(val) : val;
    });
    Object.entries(svals).forEach(([site, fields]) => {
      const clean = {};
      SITE_FIELDS.forEach((f) => {
        const val = fields?.[f.key];
        if (val === "" || val === null || val === undefined) return;
        clean[f.key] = Number(val);
      });
      if (Object.keys(clean).length) payload.site_inputs[site] = clean;
    });
    return payload;
  };

  useEffect(() => {
    // Reset on every mount so React StrictMode's dev double-mount doesn't
    // leave the ref stuck at `false` after the first cleanup.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Flush any unsaved edits to the backend before the component goes
      // away. Axios keeps the in-flight request alive even though we don't
      // await it, so persistence survives a tab switch.
      if (hasPendingSaveRef.current && latestPayloadRef.current) {
        try {
          saveRoiInputs(assessmentId, latestPayloadRef.current);
        } catch { /* no-op — unmount path */ }
        hasPendingSaveRef.current = false;
        latestPayloadRef.current = null;
      }
    };
  }, [assessmentId]);

  const loadInputsAndCompute = async () => {
    setLoading(true);
    try {
      const inp = await getRoiInputs(assessmentId);
      setView(inp);
      setGlobalValues({ ...(inp.global_values || {}) });
      const sv = {};
      Object.entries(inp.site_values || {}).forEach(([k, v]) => { sv[k] = { ...v }; });
      setSiteValues(sv);
      setCurrency(inp.global_values?.currency || inp.values?.currency || "USD");
      const rate = Number(inp.global_values?.exchange_rate ?? inp.values?.exchange_rate);
      setExchangeRate(Number.isFinite(rate) && rate > 0 ? rate : 1);
      // Always compute fresh ROI on open so downstream views (Review Report)
      // can't be tricked by a stale snapshot.
      if (assessment?.scoring_status === "completed") {
        try {
          // Read-only client: never trigger the compute-and-persist POST
          // (blocked for clients). Read the stored finalized ROI instead.
          const r = readOnly
            ? await getRoiOutput(assessmentId)
            : await processRoi(assessmentId);
          if (isMountedRef.current) setOutput(r.roi_output);
        } catch { /* leave output null — error surface is small */ }
      }
    } catch {
      setMsg("Failed to load ROI");
      setTimeout(() => setMsg(""), 2500);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadInputsAndCompute(); }, [assessmentId, assessment?.scoring_status]);

  // Debounced save + recalc. Triggered only by user edits (pending > 0).
  useEffect(() => {
    if (pending === 0 || loading) return undefined;
    // Snapshot the freshest edits into refs so the unmount cleanup can
    // flush them if the debounce hasn't fired yet.
    latestPayloadRef.current = buildPayload(globalValues, siteValues);
    hasPendingSaveRef.current = true;
    const handle = setTimeout(async () => {
      const payload = buildPayload(globalValues, siteValues);
      setCalculating(true);
      try {
        await saveRoiInputs(assessmentId, payload);
        // Save completed — clear the "dirty" flag so unmount won't re-send.
        hasPendingSaveRef.current = false;
        latestPayloadRef.current = null;
        const r = await processRoi(assessmentId);
        if (!isMountedRef.current) return;
        setOutput(r.roi_output);
        if (globalValues.currency) setCurrency(String(globalValues.currency));
        setMsg("Updated");
        setTimeout(() => isMountedRef.current && setMsg(""), 1500);
      } catch (e) {
        if (!isMountedRef.current) return;
        setMsg(e?.response?.data?.detail || "Recalculation failed");
        setTimeout(() => isMountedRef.current && setMsg(""), 3000);
      } finally {
        if (isMountedRef.current) setCalculating(false);
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [pending]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep display-only currency and exchange rate in sync with the user's live
  // edits so the ROI cards re-render immediately, without waiting for the
  // 500 ms debounced save/recompute roundtrip.
  useEffect(() => {
    if (globalValues.currency) setCurrency(String(globalValues.currency).toUpperCase());
  }, [globalValues.currency]);
  useEffect(() => {
    const rate = Number(globalValues.exchange_rate);
    if (Number.isFinite(rate) && rate > 0) setExchangeRate(rate);
  }, [globalValues.exchange_rate]);

  // Page-refresh / browser-close guard: if the user reloads while there is a
  // pending edit the axios debounce never fires. Use a keepalive fetch so the
  // PUT survives navigation and the edit is preserved on reload.
  useEffect(() => {
    const flush = () => {
      if (!hasPendingSaveRef.current || !latestPayloadRef.current) return;
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/assessments/${assessmentId}/roi-inputs`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          keepalive: true,
          body: JSON.stringify(latestPayloadRef.current),
        });
        hasPendingSaveRef.current = false;
      } catch { /* no-op — unload path */ }
    };
    window.addEventListener("beforeunload", flush);
    return () => window.removeEventListener("beforeunload", flush);
  }, [assessmentId]);

  const touch = () => setPending((n) => n + 1);
  const updateGlobal = (k, v) => { setGlobalValues((prev) => ({ ...prev, [k]: v })); touch(); };
  const updateSite = (siteKey, k, v) => {
    setSiteValues((prev) => ({ ...prev, [siteKey]: { ...(prev[siteKey] || {}), [k]: v } }));
    touch();
  };

  const resetToDefaults = () => {
    if (!view) return;
    setGlobalValues({ ...(view.global_defaults || {}) });
    const sv = {};
    Object.keys(view.site_defaults || {}).forEach((k) => { sv[k] = {}; });
    setSiteValues(sv);
    touch();
  };

  if (loading) return <p className="text-xs text-[#9CA3AF]">Loading ROI…</p>;
  if (!view) return <p className="text-xs text-red-500">{msg || "Failed to load ROI"}</p>;

  const scoringDone = assessment?.scoring_status === "completed";
  const sites = view.representative_sites || [];
  const bySite = output?.roi_by_site || {};
  const siteKeys = Object.keys(bySite);
  const summary = output?.overall_roi_summary;

  return (
    <div className="space-y-5" data-testid="roi-tab">
      {/* LIVE OUTPUT (top) — always reflects the latest inputs. */}
      <section className="relative">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5 text-[#025979]" />
            <h3 className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider">
              Output (live)
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {calculating ? (
              <span data-testid="roi-calculating" className="text-[10px] text-[#025979] bg-[#E0F2FE] px-2 py-0.5 rounded-sm font-medium">
                Recalculating…
              </span>
            ) : msg ? (
              <span data-testid="roi-msg" className="text-[10px] text-[#025979] bg-[#E0F2FE] px-2 py-0.5 rounded-sm font-medium">
                {msg}
              </span>
            ) : output ? (
              <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-sm font-medium">
                In sync
              </span>
            ) : null}
          </div>
        </div>

        {!scoringDone ? (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-5 text-sm text-amber-900" data-testid="roi-blocked">
            Scoring must be completed before ROI can be calculated. Process the evaluation first.
          </div>
        ) : !output || siteKeys.length === 0 ? (
          <div className="bg-gray-50 border border-dashed border-[#D1D5DB] rounded-sm p-8 text-center" data-testid="roi-output-placeholder">
            <p className="text-sm text-[#9CA3AF] italic">
              {!output ? "ROI will compute shortly — edit any input below to refresh." : "No per-site recommendations were found. Re-process scoring first."}
            </p>
          </div>
        ) : (
          <CfoRoiView
            summary={summary}
            currency={currency}
            assessment={assessment}
            bySite={bySite}
            exchangeRate={exchangeRate}
          />
        )}
      </section>

      {/* EDITABLE INPUTS (bottom) — collapsed by default so the CFO view
          isn't cluttered with operational edit controls. Expanding reveals
          every change auto-saves + recomputes. */}
      <details
        className="group border-t-2 border-[#E5E7EB] pt-5"
        data-testid="roi-inputs-toggle"
      >
        <summary className="cursor-pointer list-none flex items-center justify-between py-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#025979]">
              Model Inputs (advanced)
            </span>
            <span className="text-[10px] text-[#9CA3AF]">
              Tune efficiency assumptions &amp; realization rates
            </span>
          </div>
          <span
            className="text-[10px] text-[#025979] font-semibold group-open:hidden"
          >
            Show
          </span>
          <span
            className="text-[10px] text-[#025979] font-semibold hidden group-open:inline"
          >
            Hide
          </span>
        </summary>
        <div className="space-y-5 mt-4">
          <div className="flex items-center justify-end">
            <Button
              data-testid="reset-roi-btn"
              variant="outline"
              onClick={resetToDefaults}
              className="rounded-sm text-xs border-[#D1D5DB] text-[#6B7280]"
            >
              <RotateCcw className="w-3 h-3 mr-1.5" /> Reset to defaults
            </Button>
          </div>

          <p className="text-xs text-[#6B7280] -mt-2">
            <strong className="text-[#025979]">Investment</strong> is sourced from the{" "}
            <strong>Implementation tab → Cost Model</strong>. Edit the inputs below only to
            tune efficiency assumptions and realization rates.
          </p>

          {/* Global inputs */}
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-5" data-testid="global-roi-inputs">
            <h4 className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider mb-3">Global inputs (evaluation-level)</h4>
            <div className="grid grid-cols-4 gap-3">
              {GLOBAL_FIELDS.map((f) => {
                const val = globalValues[f.key];
                const def = view.global_defaults?.[f.key];
                const differs = val !== undefined && val !== null && val !== "" && def !== undefined && String(val) !== String(def);
                return (
                  <div key={f.key}>
                    <label className="text-xs font-medium text-[#374151] mb-1 block flex items-center gap-1">
                      {f.label}
                      {differs && <span className="text-[9px] text-[#025979] bg-[#E0F2FE] px-1 py-0.5 rounded-sm">overridden</span>}
                    </label>
                    <Input
                      data-testid={`roi-global-${f.key}`}
                      type={f.type}
                      step={f.step}
                      value={val ?? ""}
                      placeholder={def !== undefined && def !== null ? String(def) : f.placeholder || ""}
                      onChange={(e) => updateGlobal(f.key, e.target.value)}
                      className="rounded-sm text-sm"
                    />
                    {def !== undefined && def !== null && (
                      <p className="text-[10px] text-[#9CA3AF] mt-0.5">Default: {String(def)}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Per warehouse type — unified blocks. Each block shows Inputs
              (editable) directly followed by its matching Outputs so the
              consultant can tune assumptions and read savings in one place
              without scrolling between sections. Order follows the supply
              chain: Manufacturing → CDC → RDC → others. */}
          {sites.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-900">
              No representative warehouse types selected yet. Complete Module 2 first.
            </div>
          ) : (
            <div className="space-y-4" data-testid="roi-per-site-blocks">
              <h4 className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider">
                {(assessment?.warehouse_groups || []).length > 0 ? "Per Warehouse Group" : "Per Warehouse Type"} ({sites.length})
              </h4>
              {orderSiteKeys(assessment, sites).map((site) => {
                const roiUnit = groupSiteInfo(assessment, site);
                return (
                <div
                  key={site}
                  data-testid={`roi-block-${site}`}
                  className="border border-[#025979]/20 rounded-md bg-[#FBFCFD] p-4 space-y-3"
                >
                  <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-2">
                    <p
                      className="text-sm font-bold text-[#0F172A]"
                      style={{ fontFamily: "Chivo, sans-serif" }}
                      data-testid={`roi-block-${site}-title`}
                    >
                      {roiUnit.eyebrow && (
                        <span className="block text-[9px] uppercase tracking-[0.15em] text-[#9CA3AF] font-semibold">
                          {String(roiUnit.eyebrow).toUpperCase()}
                        </span>
                      )}
                      {roiUnit.label}
                    </p>
                    <div className="flex items-center gap-3 text-[10px]">
                      {view.site_defaults?.[site]?.complexity_label && (
                        <span className="text-[#6B7280]">
                          Complexity:{" "}
                          <span className="text-[#111827] font-medium">
                            {view.site_defaults[site].complexity_label}
                          </span>
                        </span>
                      )}
                      {view.site_recommendations?.[site] && (
                        <span className="px-2 py-0.5 bg-[#025979] text-white rounded-sm text-[10px] font-medium">
                          {view.site_recommendations[site]}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* INPUTS — appear first. */}
                  <div data-testid={`roi-block-${site}-inputs`}>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-1.5">
                      Inputs
                    </p>
                    <SiteRoiInputsBlock
                      siteKey={site}
                      recommendation={view.site_recommendations?.[site]}
                      clientData={view.client_data?.[site]}
                      defaults={view.site_defaults?.[site]}
                      values={siteValues[site] || {}}
                      onChange={updateSite}
                      embedded
                    />
                  </div>

                  {/* OUTPUTS — immediately below the inputs. Only shown
                      when ROI has been computed for this site. */}
                  {bySite[site] && (
                    <div data-testid={`roi-block-${site}-outputs`}>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-1.5">
                        Outputs
                      </p>
                      <SiteRoiCard
                        siteKey={site}
                        site={bySite[site]}
                        currency={currency}
                        exchangeRate={exchangeRate}
                        embedded
                      />
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      </details>
    </div>
  );
}

function formatCurrency(v, currency = "USD") {
  if (v === null || v === undefined || isNaN(v)) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `$${Math.round(v).toLocaleString()}`;
  }
}

function Stat({ label, value, tone = "default", testid }) {
  const toneClasses = {
    default: "bg-white border-[#E5E7EB]",
    primary: "bg-[#E0F2FE] border-[#025979]/20",
    success: "bg-emerald-50 border-emerald-200",
  }[tone];
  return (
    <div className={`border rounded-sm p-4 ${toneClasses}`} data-testid={testid}>
      <p className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">{label}</p>
      <p className="text-lg font-bold text-[#111827]">{value}</p>
    </div>
  );
}

function SiteRoiCard({ siteKey, site, currency, exchangeRate = 1, embedded = false }) {
  const fmt = (v) => formatCurrency(v == null ? v : v * exchangeRate, currency);
  const buckets = site.bucket_breakdown || {};
  const bucketLabels = {
    inbound_process_efficiency: "Inbound & Warehouse Process Efficiency",
    outbound_fulfillment_accuracy: "Outbound Efficiency & Fulfillment Accuracy",
    admin_backoffice_productivity: "Admin & Back-Office Productivity",
    inventory_working_capital: "Inventory Accuracy & Working Capital",
    downtime_risk_avoidance: "Replenishment, Downtime & Risk Avoidance",
  };
  const locationCount = site.warehouse_count || 1;
  // Per-warehouse payback / ROI% / multiplier intentionally NOT rendered
  // here. ROI, Payback, and 3-Year ROI are program-level metrics owned by
  // the top KPI strip — surfacing them per-site creates conflicting
  // numbers (phased vs steady-state per-site).
  return (
    <div
      className={embedded
        ? "bg-white border border-[#E5E7EB] rounded-sm p-3"
        : "bg-white border border-[#E5E7EB] rounded-sm p-4"}
      data-testid={`site-roi-card-${siteKey}`}
    >
      {!embedded && (
        <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#E5E7EB]">
          <div>
            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Warehouse Type</p>
            <p className="text-sm font-bold text-[#111827]">
              {warehouseLabel(siteKey)}
            </p>
          </div>
          <span className="text-[10px] text-[#6B7280]">
            Complexity: <span className="text-[#111827] font-medium">{site.complexity_label}</span>
          </span>
        </div>
      )}

      {/* Financials for this warehouse type.
          "Allocated Program Investment" is a proportional slice of the
          program total based on location count — NOT a separately estimated
          implementation cost for this warehouse type. The helper text is
          explicit about that so consultants don't misread the number. */}
      <div
        className="grid grid-cols-3 gap-2 mb-2"
        data-testid={`site-${siteKey}-financials`}
      >
        <Stat
          label="Sites Included"
          value={`${locationCount} warehouse${locationCount > 1 ? "s" : ""}`}
          testid={`site-${siteKey}-locations`}
        />
        <Stat
          label="Annual Savings"
          value={fmt(site.expected_savings)}
          tone="primary"
          testid={`site-${siteKey}-annual`}
        />
        <Stat
          label="Allocated Program Investment"
          value={fmt(site.implementation_cost)}
          testid={`site-${siteKey}-alloc-investment`}
        />
      </div>
      <p
        className="text-[10px] text-[#6B7280] mb-3"
        data-testid={`site-${siteKey}-alloc-basis`}
      >
        Proportional allocation based on location count. Not separately estimated per warehouse.
      </p>

      {/* Per-warehouse supplemental breakdown — only when more than 1 warehouse */}
      {locationCount > 1 && (
        <div className="mb-3 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-2.5 grid grid-cols-2 gap-3 text-[10px]" data-testid={`site-${siteKey}-per-wh`}>
          <div>
            <span className="text-[#9CA3AF]">Per-warehouse Savings:</span>{" "}
            <span className="text-[#111827] font-medium">{fmt(site.per_warehouse_expected_savings)}</span>
          </div>
          <div>
            <span className="text-[#9CA3AF]">Staff per warehouse:</span>{" "}
            <span className="text-[#111827] font-medium">{Math.round(site.staff_per_warehouse || 0)}</span>
          </div>
        </div>
      )}

      <div className="mb-3">
        <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1.5">Savings Contribution Breakdown</p>
        <div className="space-y-0.5">
          {Object.entries(buckets).map(([k, v]) => (
            <div
              key={k}
              className="flex items-baseline justify-between text-[11px] py-0.5"
              data-testid={`site-${siteKey}-bucket-${k}`}
            >
              <span className="text-[#374151] truncate">{bucketLabels[k]}</span>
              <span className="text-[#111827] font-medium tabular-nums whitespace-nowrap">{fmt(v)}</span>
            </div>
          ))}
        </div>
      </div>

      {site.top_value_drivers?.length > 0 && (
        <div className="pt-3 border-t border-[#E5E7EB]">
          <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1.5">Top Value Drivers</p>
          <div className="flex flex-wrap gap-1.5">
            {site.top_value_drivers.map((d, i) => (
              <span key={d.key} className="text-[10px] px-2 py-0.5 bg-[#E0F2FE] text-[#025979] rounded-sm font-medium">
                {i + 1}. {d.label} · {fmt(d.amount)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OverallRoiSummary({ summary, currency, exchangeRate = 1 }) {
  if (!summary) return null;
  const fmt = (v) => formatCurrency(v == null ? v : v * exchangeRate, currency);
  const buckets = summary.combined_bucket_breakdown || {};
  const max = Math.max(...Object.values(buckets), 1);
  const bucketLabels = {
    inbound_process_efficiency: "Inbound & Warehouse Process Efficiency",
    outbound_fulfillment_accuracy: "Outbound Efficiency & Fulfillment Accuracy",
    admin_backoffice_productivity: "Admin & Back-Office Productivity",
    inventory_working_capital: "Inventory Accuracy & Working Capital",
    downtime_risk_avoidance: "Replenishment, Downtime & Risk Avoidance",
  };
  return (
    <div className="bg-gradient-to-br from-[#025979] to-[#03738C] text-white rounded-sm p-5" data-testid="overall-roi-summary">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-white/70">Overall Combined ROI</p>
          <p className="text-sm font-bold">Across {summary.site_count} Warehouse Type{summary.site_count > 1 ? "s" : ""}{summary.warehouse_count_total ? ` · ${summary.warehouse_count_total} warehouse${summary.warehouse_count_total > 1 ? "s" : ""}` : ""}</p>
        </div>
        <span className="px-2 py-0.5 bg-white/20 rounded-sm text-[10px] font-medium" data-testid="overall-recommendation-label">
          Recommendation: {summary.recommendation_display || summary.dominant_recommendation}
        </span>
      </div>

      {summary.is_hybrid && summary.site_solution_map && (
        <div className="bg-white/5 backdrop-blur rounded-sm p-3 mb-4" data-testid="site-solution-map">
          <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1.5">Hybrid Solution — Site Mapping</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(summary.site_solution_map).map(([site, rec]) => (
              <div key={site} className="flex items-center gap-1.5 bg-white/10 rounded-sm px-2 py-1" data-testid={`site-solution-${site}`}>
                <span className="text-[10px] tracking-wider text-white/70">{warehouseLabel(site)}</span>
                <span className="text-white/50">→</span>
                <span className="text-[11px] font-semibold text-white">{rec.replace("SAP ", "")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-white/10 backdrop-blur rounded-sm p-3" data-testid="overall-stat-investment">
          <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Total Investment</p>
          <p className="text-base font-bold">{fmt(summary.total_investment)}</p>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-sm p-3" data-testid="overall-stat-annual">
          <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Annual Savings</p>
          <p className="text-base font-bold">{fmt(summary.total_annual_savings)}</p>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-sm p-3" data-testid="overall-stat-payback">
          <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Payback</p>
          <p className="text-base font-bold">{summary.overall_payback_months !== null ? `${summary.overall_payback_months} mo` : "—"}</p>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-sm p-3" data-testid="overall-stat-roi">
          <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">3-Yr ROI</p>
          <p className="text-base font-bold">{summary.overall_roi_percent}%</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-white/10 backdrop-blur rounded-sm p-3">
          <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Year 1 Savings</p>
          <p className="text-sm font-bold">{fmt(summary.total_year1_savings)}</p>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-sm p-3">
          <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Conservative</p>
          <p className="text-sm font-bold">{fmt(summary.total_conservative_savings)}</p>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-sm p-3">
          <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Optimistic</p>
          <p className="text-sm font-bold">{fmt(summary.total_optimistic_savings)}</p>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-sm p-3">
          <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">3-Yr Benefit</p>
          <p className="text-sm font-bold">{fmt(summary.overall_3_year_benefit)}</p>
        </div>
      </div>

      {summary.phased_enabled && summary.savings_by_year && (
        <PhasedRoiCard summary={summary} fmt={fmt} />
      )}

      <div className="bg-white/5 backdrop-blur rounded-sm p-3 mb-3">
        <p className="text-[10px] uppercase tracking-wider text-white/60 mb-2">Combined Savings Breakdown</p>
        <div className="space-y-1">
          {Object.entries(buckets).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2" data-testid={`overall-bucket-${k}`}>
              <div className="flex-1 text-[10px] text-white/90">{bucketLabels[k]}</div>
              <div className="flex-[2] bg-white/10 rounded-sm h-1.5">
                <div className="bg-white h-1.5 rounded-sm" style={{ width: `${max > 0 ? (v / max) * 100 : 0}%` }} />
              </div>
              <div className="w-24 text-right text-[10px] font-medium">{fmt(v)}</div>
            </div>
          ))}
        </div>
      </div>

      {summary.combined_top_value_drivers?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1.5">Top Combined Value Drivers</p>
          <div className="flex flex-wrap gap-1.5">
            {summary.combined_top_value_drivers.map((d, i) => (
              <span key={d.key} className="text-[10px] px-2 py-0.5 bg-white/20 rounded-sm font-medium">
                {i + 1}. {d.label} · {fmt(d.amount)}
              </span>
            ))}
          </div>
        </div>
      )}

      {summary.overall_roi_ceiling_applied && (
        <p className="text-[9px] text-amber-200 italic mt-2">Sanity ceiling applied (raw: {summary.overall_roi_percent_raw}%)</p>
      )}
    </div>
  );
}

function PhasedRoiCard({ summary, fmt }) {
  const sby = summary.savings_by_year || {};
  const y1 = Number(sby.year1 || 0);
  const y2 = Number(sby.year2 || 0);
  const y3 = Number(sby.year3 || 0);
  const steady = Number(summary.total_annual_savings || 0);
  const maxVal = Math.max(steady, y1, y2, y3, 1);
  const phasedPayback = summary.phased_payback_months;
  const steadyPayback = summary.overall_payback_months;
  const waves = Array.isArray(summary.phased_waves) ? summary.phased_waves : [];

  const row = (label, value, testid, tone) => (
    <div className="flex items-center gap-2" data-testid={testid}>
      <div className="flex-1 text-[10px] text-white/90">{label}</div>
      <div className="flex-[2] bg-white/10 rounded-sm h-1.5">
        <div
          className={tone === "steady" ? "bg-white/50 h-1.5 rounded-sm" : "bg-[#5EB8D8] h-1.5 rounded-sm"}
          style={{ width: `${maxVal > 0 ? (value / maxVal) * 100 : 0}%` }}
        />
      </div>
      <div className="w-24 text-right text-[10px] font-medium">{fmt(value)}</div>
    </div>
  );

  return (
    <div
      data-testid="phased-roi-card"
      className="bg-white/5 backdrop-blur rounded-sm p-3 mb-3 border border-white/10"
    >
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">
            Phased ROI Realization
          </p>
          <p className="text-[10px] text-white/50">
            Reflects wave go-live timing — sites only contribute after they go live.
          </p>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="text-white/60">Phased payback:</span>
          <span className="font-bold text-white" data-testid="phased-payback-months">
            {phasedPayback != null ? `${Number(phasedPayback).toFixed(1)} mo` : "—"}
          </span>
          {steadyPayback != null && (
            <span className="text-white/40" data-testid="steady-payback-months">
              · steady-state: {Number(steadyPayback).toFixed(1)} mo
            </span>
          )}
        </div>
      </div>
      <div className="space-y-1">
        {row("Year 1 (ramp-up)", y1, "phased-year1", "phased")}
        {row("Year 2", y2, "phased-year2", "phased")}
        {row("Year 3", y3, "phased-year3", "phased")}
        {row("Steady-state annual (full realization)", steady, "phased-steady", "steady")}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 pt-2 border-t border-white/10 text-[10px]">
        <div data-testid="phased-3yr-savings">
          <p className="text-white/60 uppercase tracking-wider mb-0.5">3-Yr Phased Savings</p>
          <p className="text-sm font-bold">{fmt(summary.phased_3yr_savings)}</p>
        </div>
        <div data-testid="phased-3yr-net">
          <p className="text-white/60 uppercase tracking-wider mb-0.5">3-Yr Phased Net Benefit</p>
          <p className="text-sm font-bold">{fmt(summary.phased_3yr_net_benefit)}</p>
        </div>
        <div data-testid="phased-roi-percent">
          <p className="text-white/60 uppercase tracking-wider mb-0.5">Phased 3-Yr ROI</p>
          <p className="text-sm font-bold">{Number(summary.phased_roi_percent || 0).toFixed(1)}%</p>
        </div>
      </div>
      {waves.length > 0 && (
        <div className="mt-3 pt-2 border-t border-white/10" data-testid="phased-waves-list">
          <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1.5">Wave Ramp-up</p>
          <div className="space-y-0.5">
            {waves.map((w, idx) => (
              <div
                key={w.id || idx}
                data-testid={`phased-wave-${idx}`}
                className="flex items-baseline justify-between text-[10px] text-white/80"
              >
                <span className="truncate max-w-[55%]">
                  <span className="font-semibold text-white">{w.name}</span>
                  {Array.isArray(w.scope_labels) && w.scope_labels.length > 0 && (
                    <span className="text-white/50"> · {w.scope_labels.join(" + ")}</span>
                  )}
                </span>
                <span className="flex items-center gap-2 whitespace-nowrap">
                  <span className="text-white/60">Live m{w.go_live_month}</span>
                  <span>Y1 {fmt(w.year1_savings)}</span>
                  <span className="text-white/50">· Y2 {fmt(w.year2_savings)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ClientInputsTab({ assessment, onRefresh }) {
  // RFP submissions get a dedicated layout — guided questionnaire sections
  // are intentionally hidden until PRIVA extraction populates them.
  if (assessment?.submission_type === "rfp") {
    return <RfpClientInputsTab assessment={assessment} onRefresh={onRefresh} />;
  }
  return (
    <div className="space-y-3" data-testid="client-inputs-tab">
      <p className="text-xs text-[#6B7280]">
        Read-only snapshot of every answer captured during the SmartEvaluate assessment.
      </p>
      <ClientInputsSummary assessment={assessment} />
    </div>
  );
}

// RoiOutputTab has been merged into RoiTab above — output + inputs now live
// together with automatic debounced recalculation on every edit.

// -----------------------------------------------------------------------------
// Review Report — Consultant preview view (Module 5)
// Client-ready, presentation-style layout built purely from existing computed
// data (narrative + ROI output + recommendations). Does NOT recalculate or
// mutate anything. No finalize / share / email / download — that's a later step.
// -----------------------------------------------------------------------------

// Strip validation/test artefacts from company names (e.g. "VAL_B3_hybrid_brownfield")
// so the report header reads cleanly in consultant previews.
function cleanCompanyName(raw) {
  if (!raw) return "—";
  let s = String(raw).trim();
  if (!s) return "—";
  if (/^VAL[_\s]/i.test(s) || /^val_/i.test(s) || s.toLowerCase().startsWith("validation")) {
    const stripped = s.replace(/^VAL[_\s-]*/i, "").replace(/_/g, " ").trim();
    return stripped ? `Validation scenario · ${toSentenceCase(stripped)}` : "Validation scenario";
  }
  // Never show raw snake_case ids as a company name
  if (/^[a-z0-9_]+$/.test(s) && s.includes("_")) return toSentenceCase(s);
  return s;
}

// Heuristic network-complexity label: uses the "strongest" per-site complexity
// already produced by the scoring engine (no recalculation).
function deriveNetworkComplexity(assessment) {
  const map = assessment?.complexity_scores_by_site || {};
  const labels = Object.values(map).map(v => (v?.label || "").toLowerCase());
  if (labels.some(l => l.includes("high") || l.includes("advanced"))) return "high";
  if (labels.some(l => l.includes("medium") || l.includes("moderate"))) return "medium";
  if (labels.length > 0) return "low";
  return "medium";
}

function ExecWarehouseGroupSummary({ assessment }) {
  const [data, setData] = useState(null);
  const isCloud = (assessment?.scenario_type || "").toLowerCase() === "cloud_migration";
  const hasGroups = (assessment?.warehouse_groups || []).length > 0;
  useEffect(() => {
    if (isCloud || !hasGroups || !assessment?.id) return;
    let alive = true;
    getGroupRoi(assessment.id).then((r) => { if (alive) setData(r); }).catch(() => {});
    return () => { alive = false; };
  }, [assessment?.id, isCloud, hasGroups]);
  if (isCloud || !hasGroups || !data || !(data.groups || []).length) return null;

  const cur = data.display_currency || "USD";
  const rate = data.exchange_rate || 1;
  const fmt = (n) => {
    if (n === null || n === undefined) return "—";
    const v = Number(n) * (Number(rate) || 1);
    try { return new Intl.NumberFormat("en-US", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v); }
    catch { return `${cur} ${Math.round(v).toLocaleString()}`; }
  };
  const mo = (m) => (m == null ? "—" : (m < 1 ? "<1 mo" : `${m} mo`));
  const pct = (p) => (p == null ? "—" : `${Math.round(p).toLocaleString()}%`);
  const ov = data.overall || {};
  const oe = ov.summary?.scenarios?.expected || {};
  const recs = assessment.final_recommendation_by_site || {};
  const deps = assessment.deployment_recommendation_by_site || {};

  return (
    <div data-testid="exec-warehouse-groups" className="border border-[#025979] rounded-sm overflow-hidden">
      <div className="bg-[#025979] text-white px-4 py-3">
        <div className="text-[12px] font-semibold uppercase tracking-wide mb-2">Programme ROI — In-Scope Warehouse Groups</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><div className="text-[9px] uppercase text-[#9Fc3d3]">Gross Annual Savings</div><div className="text-[14px] font-bold tabular-nums" data-testid="exec-overall-gross">{fmt(oe.gross_annual_savings)}</div></div>
          <div><div className="text-[9px] uppercase text-[#9Fc3d3]">Total Investment</div><div className="text-[14px] font-bold tabular-nums" data-testid="exec-overall-investment">{fmt(data.total_one_time_investment)}</div></div>
          <div><div className="text-[9px] uppercase text-[#9Fc3d3]">Payback</div><div className="text-[14px] font-bold tabular-nums">{mo(oe.payback_months)}</div></div>
          <div><div className="text-[9px] uppercase text-[#9Fc3d3]">3-Yr ROI</div><div className="text-[14px] font-bold tabular-nums">{pct(oe.three_year_roi_pct)}</div></div>
        </div>
        <div className="text-[10px] text-[#9Fc3d3] mt-2">
          {ov.group_count} group{ov.group_count === 1 ? "" : "s"} · {ov.total_sites_in_scope} site{ov.total_sites_in_scope === 1 ? "" : "s"} in scope · Expected (80%) scenario
        </div>
      </div>
      <div className="divide-y divide-[#E5E7EB]">
        {data.groups.map((g) => {
          const e = g.summary?.scenarios?.expected || {};
          const rec = recs[g.group_id];
          const dep = deps[g.group_id];
          return (
            <div key={g.group_id} className="px-4 py-3 bg-white" data-testid={`exec-group-${g.group_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-[13px] font-semibold text-[#111827]">
                  {g.group_name}
                  <span className="text-[10px] font-medium text-[#6D28D9] uppercase ml-2">{(g.scenario || "").toUpperCase()}</span>
                  <span className="text-[10px] text-[#6B7280] ml-2">{g.sites_in_scope} of {g.total_sites} sites in scope</span>
                </div>
                <div className="text-[11px] text-[#025979] font-semibold tabular-nums">{fmt(g.total_annual_cost_savings)}/yr</div>
              </div>
              {(rec || dep) && (
                <div className="mt-1.5 text-[11px] text-[#374151]" data-testid={`exec-group-rec-${g.group_id}`}>
                  {dep && <span className="font-medium">{dep}. </span>}{rec}
                </div>
              )}
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2 text-[11px]">
                <div><div className="text-[9px] uppercase text-[#9CA3AF]">Gross Annual</div><div className="font-semibold text-[#111827] tabular-nums">{fmt(e.gross_annual_savings)}</div></div>
                <div><div className="text-[9px] uppercase text-[#9CA3AF]">Payback</div><div className="font-semibold text-[#111827] tabular-nums">{mo(e.payback_months)}</div></div>
                <div><div className="text-[9px] uppercase text-[#9CA3AF]">3-Yr ROI</div><div className="font-semibold text-[#111827] tabular-nums">{pct(e.three_year_roi_pct)}</div></div>
                <div><div className="text-[9px] uppercase text-[#9CA3AF]">Sites in Scope</div><div className="font-semibold text-[#111827] tabular-nums">{g.sites_in_scope} of {g.total_sites}</div></div>
                <div><div className="text-[9px] uppercase text-[#9CA3AF]">Allocated Investment</div><div className="font-semibold text-[#111827] tabular-nums">{g.allocated_investment != null ? fmt(g.allocated_investment) : "—"}</div></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ReviewReportTab({ assessment, assessmentId, onJumpToRoi, onFinalize, isFinalized, isViewingVersion, snapshot, clientView = false, readiness = null }) {
  registerGroupNames(assessment);
  const [narratives, setNarratives] = useState([]);
  const [narrativesLoaded, setNarrativesLoaded] = useState(false);
  const [roiOutput, setRoiOutput] = useState(null);
  const [roiInputValues, setRoiInputValues] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [exchangeRate, setExchangeRate] = useState(1);
  const [discountPct, setDiscountPct] = useState(0);
  const [implementationInputs, setImplementationInputs] = useState(null);
  const [cmRoi, setCmRoi] = useState(null);
  const [groupRoi, setGroupRoi] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // Effective (override-aware) programme recommendation context — same values as
  // the Recommendations header / Implementation tab. Display-only; no recompute.
  const { data: effectiveRecs } = useEffectiveRecommendations(assessmentId);

  useEffect(() => {
    // When viewing a frozen snapshot, hydrate directly from the snapshot
    // payload instead of recomputing from live endpoints. This guarantees the
    // past report never reflects subsequent changes.
    if (snapshot) {
      setNarratives(snapshot.recommendations?.narratives || []);
      setNarrativesLoaded(true);
      setRoiOutput(snapshot.roi?.output || null);
      setCurrency(
        snapshot.roi?.inputs?.global_inputs?.currency ||
        snapshot.roi?.output?.currency || "USD"
      );
      const snapRate = Number(
        snapshot.roi?.inputs?.global_inputs?.exchange_rate ??
        snapshot.roi?.inputs?.values?.exchange_rate
      );
      setExchangeRate(Number.isFinite(snapRate) && snapRate > 0 ? snapRate : 1);
      const snapDiscount = Number(
        snapshot.roi?.inputs?.global_inputs?.discount_pct ??
        snapshot.roi?.inputs?.values?.discount_pct
      );
      setDiscountPct(Number.isFinite(snapDiscount) && snapDiscount >= 0 ? snapDiscount : 0);
      setRoiInputValues(snapshot.roi?.inputs?.values || null);
      setImplementationInputs(snapshot.implementation || null);
      setCmRoi(snapshot.cm_roi || null);
      setGroupRoi(snapshot.group_roi || null);
      setLoaded(true);
      return;
    }
    if (!assessmentId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await getAssessmentNarrative(assessmentId);
        if (!cancelled) {
          setNarratives(r?.narratives || []);
          setNarrativesLoaded(true);
        }
      } catch {
        if (!cancelled) setNarrativesLoaded(true);
      }
      // Always recompute ROI when opening the report so downstream numbers
      // mirror the latest inputs — no reliance on stale saved snapshots.
      // When the assessment is locked (finalized), skip the recompute — the
      // frozen ROI output is the source of truth.
      if (!isFinalized) {
        try {
          const ro = await processRoi(assessmentId);
          if (!cancelled) setRoiOutput(ro?.roi_output || null);
        } catch {
          try {
            const ro = await getRoiOutput(assessmentId);
            if (!cancelled) setRoiOutput(ro?.roi_output || null);
          } catch { /* ROI not ready — show placeholder */ }
        }
      } else {
        try {
          const ro = await getRoiOutput(assessmentId);
          if (!cancelled) setRoiOutput(ro?.roi_output || null);
        } catch { /* ignore */ }
      }
      try {
        const inputs = await getRoiInputs(assessmentId);
        if (!cancelled) {
          setCurrency(inputs?.global_values?.currency || inputs?.values?.currency || "USD");
          const rate = Number(inputs?.global_values?.exchange_rate ?? inputs?.values?.exchange_rate);
          setExchangeRate(Number.isFinite(rate) && rate > 0 ? rate : 1);
          const disc = Number(inputs?.global_values?.discount_pct ?? inputs?.values?.discount_pct);
          setDiscountPct(Number.isFinite(disc) && disc >= 0 ? disc : 0);
          setRoiInputValues(inputs?.values || null);
        }
      } catch { /* keep default */ }
      try {
        const ii = await getImplementationInputs(assessmentId);
        if (!cancelled) setImplementationInputs(ii?.inputs || null);
      } catch { /* implementation inputs optional */ }
      if ((assessment?.scenario_type || "").toLowerCase() === "cloud_migration") {
        try {
          const cr = await getCmRoi(assessmentId);
          if (!cancelled) setCmRoi(cr || null);
        } catch { /* CM ROI optional */ }
      }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [assessmentId, snapshot, isFinalized]);

  // Warehouse Group ROI — the authoritative "new Overall ROI" that drives the
  // redesigned Executive Summary (headline metrics, Warehouse Strategy blocks,
  // Business Case value drivers). Same endpoint the ROI tab consumes, so the
  // numbers reconcile exactly. Greenfield/Brownfield grouped assessments only.
  // When viewing a frozen snapshot (finalized / PDF), group_roi is hydrated
  // directly from the snapshot above — skip the live (admin-auth) fetch.
  useEffect(() => {
    if (snapshot) return;
    if (!assessment?.id) return;
    if ((assessment?.scenario_type || "").toLowerCase() === "cloud_migration") return;
    if (!(assessment?.warehouse_groups || []).length) return;
    let alive = true;
    getGroupRoi(assessment.id).then((r) => { if (alive) setGroupRoi(r); }).catch(() => {});
    return () => { alive = false; };
  }, [assessment?.id, snapshot]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!assessment) return null;
  if (!loaded) return <p className="text-xs text-[#9CA3AF]">Loading report…</p>;

  // Cloud Migration does NOT run the Greenfield/Brownfield scoring engine, so
  // its Executive Summary report readiness is gated on FINALIZED status (not
  // `scoring_status`). Before finalization we surface a scenario-appropriate
  // message alongside the Finalize action; after finalization we open the
  // report shell/preview. Greenfield/Brownfield wording + gating is untouched.
  const isCloudMigration = (assessment.scenario_type || "").toLowerCase() === "cloud_migration";
  if (isCloudMigration) {
    const cmReportReady = isFinalized || isViewingVersion;
    // Same finalize-gate logic as the standard action bar below.
    const cmActionable = (readiness?.blockers || []).filter((b) => b.section !== "finalize");
    const cmCanFinalize = !!readiness && cmActionable.length === 0;
    const cmMissingCount = cmActionable.length;
    return (
      <div data-testid="review-report-tab" className="printable-report space-y-5 max-w-[1000px] mx-auto">
        {!isViewingVersion && !isFinalized && onFinalize && (
          <div
            data-testid="report-action-bar"
            className="no-print flex items-center justify-end gap-2 flex-wrap"
          >
            {!cmCanFinalize && readiness && (
              <span
                data-testid="finalize-blocked-hint"
                className="text-[11px] text-[#92400E] bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-sm px-2 py-1"
                title={cmActionable.map((b) => `• ${b.message}`).join("\n")}
              >
                {cmMissingCount === 1
                  ? `1 item missing before finalize: ${cmActionable[0].message}`
                  : `${cmMissingCount} items missing before finalize (hover for detail)`}
              </span>
            )}
            <Button
              size="sm"
              className="text-xs bg-[#025979] hover:bg-[#024a63] text-white shadow-sm disabled:opacity-50"
              onClick={onFinalize}
              disabled={!cmCanFinalize}
              data-testid="finalize-assessment-btn"
            >
              <Lock className="w-3 h-3 mr-1.5" /> Finalize Evaluation
            </Button>
          </div>
        )}

        <ExecReportHeader
          assessment={assessment}
          implementationInputs={implementationInputs}
        />

        {!cmReportReady ? (
          <div
            data-testid="cm-report-pending"
            className="bg-[#F0F9FF] border border-[#0EA5E9]/30 rounded-sm p-5 text-sm text-[#025979]"
          >
            The Executive Summary Report becomes available after this assessment version is finalized.
          </div>
        ) : (
          <>
            <CmExecutiveReportBody
              assessment={assessment}
              cmRoi={cmRoi}
              narratives={narratives}
              implementationInputs={implementationInputs}
              effectiveRecs={effectiveRecs}
            />
            <ReportFooter assessment={assessment} clientView={clientView} />
          </>
        )}
      </div>
    );
  }

  const scoringDone = assessment.scoring_status === "completed";
  if (!scoringDone) {
    return (
      <div
        data-testid="review-report-blocked"
        className="bg-amber-50 border border-amber-200 rounded-sm p-5 text-sm text-amber-900"
      >
        The report preview becomes available once scoring is completed for this assessment.
      </div>
    );
  }

  const summary = roiOutput?.overall_roi_summary;
  const roiBySite = roiOutput?.roi_by_site || {};
  const complexityKey = deriveNetworkComplexity(assessment);
  const strategyMode = buildStrategyMode(assessment); // "single" | "hybrid"
  const executiveCopy = buildExecutiveSummary(assessment, roiBySite, complexityKey, strategyMode);
  const executiveHeadline = buildExecutiveHeadline(summary, currency, implementationInputs, exchangeRate, discountPct);
  // Inject the persisted implementation inputs so programme coverage uses the
  // same reconciled site total as the Implementation tab.
  const programme = deriveProgrammeContext(
    { ...assessment, implementation_inputs: implementationInputs || assessment.implementation_inputs },
    effectiveRecs,
  );

  return (
    <div data-testid="review-report-tab" className="printable-report space-y-5 max-w-[1000px] mx-auto">
      {/* Action bar — kept in original position, no-print. */}
      {!isViewingVersion && !isFinalized && onFinalize && (() => {
        // iter 67: the Finalize action itself is a blocker — "not yet
        // finalized" is section=finalize. We exclude that from the
        // button-disable gate (otherwise it would be an infinite loop —
        // you can't finalize until you've finalized). Publish gate still
        // checks every blocker including `finalize`.
        const actionableBlockers = (readiness?.blockers || []).filter(
          (b) => b.section !== "finalize"
        );
        const canFinalize = !!readiness && actionableBlockers.length === 0;
        const missingCount = actionableBlockers.length;
        return (
          <div
            data-testid="report-action-bar"
            className="no-print flex items-center justify-end gap-2 flex-wrap"
          >
            {!canFinalize && readiness && (
              <span
                data-testid="finalize-blocked-hint"
                className="text-[11px] text-[#92400E] bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-sm px-2 py-1"
                title={actionableBlockers.map((b) => `• ${b.message}`).join("\n")}
              >
                {missingCount === 1
                  ? `1 item missing before finalize: ${actionableBlockers[0].message}`
                  : `${missingCount} items missing before finalize (hover for detail)`}
              </span>
            )}
            <Button
              size="sm"
              className="text-xs bg-[#025979] hover:bg-[#024a63] text-white shadow-sm disabled:opacity-50"
              onClick={onFinalize}
              disabled={!canFinalize}
              data-testid="finalize-assessment-btn"
            >
              <Lock className="w-3 h-3 mr-1.5" /> Finalize Evaluation
            </Button>
          </div>
        );
      })()}

      {/* ============ REDESIGNED SENIOR-STAKEHOLDER EXECUTIVE SUMMARY ============ */}
      <ExecReportHeader
        assessment={assessment}
        implementationInputs={implementationInputs}
      />

      {/* 1. Executive Decision Summary */}
      <ExecDecisionSummary
        assessment={assessment}
        programme={programme}
        groupRoi={groupRoi}
        summary={summary}
        narrative={executiveCopy}
        currency={currency}
        exchangeRate={exchangeRate}
      />

      {/* 2. Warehouse Strategy — one compact block per in-scope Warehouse Group */}
      <ExecWarehouseStrategy
        assessment={assessment}
        groupRoi={groupRoi}
        implementationInputs={implementationInputs}
      />

      {/* 3. Business Case — Overall ROI roll-up + S1–S5 value drivers */}
      <div className="exec-page-break">
        <ExecBusinessCase
          groupRoi={groupRoi}
          summary={summary}
          currency={currency}
          exchangeRate={exchangeRate}
        />
      </div>

      {/* 4. Implementation Roadmap — consolidated plan / timeline / execution */}
      <ExecImplementationRoadmap
        inputs={implementationInputs}
        programme={programme}
      />

      {/* 5. Key Recommendations & Next Steps */}
      <ExecRecommendationsNextSteps
        assessment={assessment}
        groupRoi={groupRoi}
        summary={summary}
        implementationInputs={implementationInputs}
        narratives={narratives}
      />

      <ReportFooter assessment={assessment} clientView={clientView} />
    </div>
  );
}

// =============================================================================
// Cloud Migration — Executive Summary Report body.
// Reuses the SHARED Executive Summary framework (ExecImplementation*,
// ExecInvestmentBreakdown, ExecMetaCell + card styling) and the SHARED CM
// helpers (complexity / risk / landscape / focus). Only the ~15% CM-specific
// data mapping lives here — no new report engine, no duplicated logic, and the
// Greenfield/Brownfield report path is untouched. Rendered for finalized /
// version-snapshot / PDF (PrintReport re-uses ReviewReportTab).
// =============================================================================
const CM_ROI_STRATEGIC_BENEFITS = [
  "Improved platform supportability",
  "Better disaster recovery readiness",
  "Improved security and compliance posture",
  "Readiness for future SAP innovation",
];

const CM_NEXT_STEPS = [
  "Approve the recommended migration strategy",
  "Finalize the project scope and boundaries",
  "Validate the implementation timeline with stakeholders",
  "Confirm the implementation team and governance",
  "Kick-off the migration assessment / discovery",
  "Begin project preparation and cutover planning",
];

const GF_NEXT_STEPS = [
  "Approve the recommended programme and solution direction",
  "Finalize the project scope and boundaries",
  "Validate the implementation timeline with stakeholders",
  "Confirm the delivery team and governance model",
  "Kick-off detailed blueprint and vendor selection",
  "Begin project preparation and mobilization",
];

function CmExecSection({ title, testid, children }) {
  return (
    <section
      data-testid={testid}
      className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5 print:border print:shadow-none"
    >
      <h3
        className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function CmExecutiveReportBody({ assessment, cmRoi, narratives, implementationInputs, effectiveRecs }) {
  const ctx = cmRoi?.context || {};
  const out = cmRoi?.calculable ? (cmRoi?.outputs || {}) : null;
  const roiCurrency = cmRoi?.inputs?.currency || ctx.currency || "USD";
  const roiRate = (() => {
    const r = Number(cmRoi?.inputs?.exchange_rate);
    return Number.isFinite(r) && r > 0 ? r : 1;
  })();
  const fmtRoi = (v) =>
    v == null
      ? "—"
      : new Intl.NumberFormat("en-US", { style: "currency", currency: roiCurrency, maximumFractionDigits: 0 }).format(v * roiRate);

  // Strategy / approach — authoritative from the ROI context (matches the ROI
  // tab); fall back to the override-aware recommendation when ROI isn't set.
  const fallbackStrategy = deriveCmStrategy(effectiveRecs?.effective_migration_approach);
  const strategyKey = ctx.transformation_strategy || fallbackStrategy.strategyKey;
  const strategyLabel = ctx.transformation_strategy_label || CM_STRATEGY_LABELS[strategyKey] || "Technical Migration";
  const approachLabel = ctx.implementation_approach_label || fallbackStrategy.approach || null;
  const strategyDesc = CM_STRATEGY_DESCRIPTIONS[strategyKey] || "";
  const approachDesc = approachLabel ? CM_APPROACH_DESCRIPTIONS[approachLabel] : null;

  // Narrative (reused verbatim from the recommendation narrative engine).
  const narr = (narratives || [])[0] || {};
  const recs = narr.recommendations || [];
  const allReasons = [];
  const seenR = new Set();
  for (const r of recs) {
    for (const x of r.reasons || []) {
      if (x.description && !seenR.has(x.description)) { allReasons.push(x); seenR.add(x.description); }
    }
  }
  const focusAreas = cmFocusAreas(recs);
  const summaryNarrative = narr.narrative_summary || narr.short_summary || "";

  // Landscape snapshot — same fields as the Evaluation Insights tab.
  const ans = cmMergedAnswers(assessment);
  const { band } = cmComplexityBand(ans);
  const complexitySignals = cmComplexityBand(ans).signals;
  const complexityText = cmComplexityExplanation(complexitySignals);
  const complexityTone = CM_COMPLEXITY_TONE[band] || "#025979";
  const dbSize = cmNum(ans.cm_data_database_size_gb);
  const ifaces = cmNum(ans.cm_int_interface_count);
  const customDev = cmCustomDevTotal(ans);
  const landscapeRows = [
    ["Current ERP", ans.cm_erp_platform],
    ["ERP Version", ans.cm_erp_version],
    ["Current EWM", ans.cm_ewm_deployment],
    ["EWM Version", ans.cm_ewm_version],
    ["Productive Systems", ans.cm_productive_ewm_system_count],
    ["Warehouses", ans.cm_productive_warehouse_count],
    ["Countries", ans.cm_countries_supported],
    ["Users", ans.cm_ewm_user_count],
    ["Hosting Model", ans.cm_hosting_model],
    ["Database Platform", ans.cm_database_platform],
    ["Database Size", dbSize != null ? `${dbSize.toLocaleString()} GB` : undefined],
    ["Approx. Interfaces", ifaces != null ? ifaces.toLocaleString() : undefined],
    ["Custom Development Count", customDev != null ? customDev.toLocaleString() : undefined],
  ];
  const hasLandscape = landscapeRows.some(([, v]) => v !== undefined && v !== null && v !== "");

  // Risks (shared helper) — grouped.
  const risks = cmRiskAreas(ans);
  const techRisks = risks.filter((r) => r.group === "technical");
  const bizRisks = risks.filter((r) => r.group === "business");

  // Implementation investment currency mirrors the Implementation tab.
  const implCurrency = implementationInputs?.cost_breakdown?.currency || "USD";
  const indicativeDuration = out?.approach_modifiers?.indicative_duration || null;

  return (
    <div data-testid="cm-exec-report" className="space-y-5">
      {/* SECTION 1 — Executive Summary (core, always shown) */}
      <CmExecSection title="Executive Summary" testid="cm-exec-summary">
        {summaryNarrative ? (
          <p className="text-[13px] text-[#374151] leading-relaxed" data-testid="cm-exec-summary-narrative">
            {summaryNarrative}
          </p>
        ) : null}
        <p className="text-[13px] text-[#374151] leading-relaxed mt-2" data-testid="cm-exec-summary-composed">
          The current SAP landscape has been evaluated for cloud migration. Based on the assessment, the recommended
          strategy is <span className="font-semibold text-[#0F172A]">{strategyLabel}</span>
          {approachLabel ? <> using <span className="font-semibold text-[#0F172A]">{approachLabel}</span></> : null}.
          {strategyDesc ? ` ${strategyDesc}` : ""}
          {indicativeDuration ? ` The estimated implementation duration is ${indicativeDuration}.` : ""}
          {out && !out.no_payback && out.payback_months != null
            ? ` The expected payback period is approximately ${out.payback_months} months.`
            : ""}
        </p>
      </CmExecSection>

      {/* SECTION 2 — Customer / Migration Landscape */}
      {hasLandscape && (
        <CmExecSection title="Customer Landscape" testid="cm-exec-landscape">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0">
            {landscapeRows.map(([l, v]) => <CmSnapshotRow key={l} label={l} value={v} />)}
          </div>
        </CmExecSection>
      )}

      {/* SECTION 3 — Key Findings */}
      <CmExecSection title="Key Findings" testid="cm-exec-findings">
        <div className="flex items-start gap-4 flex-wrap">
          <span
            data-testid="cm-exec-complexity-band"
            className="inline-flex items-center justify-center text-[13px] font-bold uppercase tracking-wider rounded-sm px-3 py-1.5 border"
            style={{ color: complexityTone, borderColor: complexityTone + "55", background: complexityTone + "12" }}
          >
            {band} Complexity
          </span>
          <p className="flex-1 min-w-[240px] text-[13px] text-[#374151] leading-relaxed self-center">
            {complexityText}
          </p>
        </div>
        {allReasons.length > 0 && (
          <ul className="mt-4 space-y-1.5 text-[12.5px] text-[#374151] leading-relaxed" data-testid="cm-exec-findings-drivers">
            {allReasons.slice(0, 5).map((x, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-[#025979] mt-0.5">•</span><span>{x.description}</span>
              </li>
            ))}
          </ul>
        )}
      </CmExecSection>

      {/* SECTION 4 — Recommended Migration Strategy (core) */}
      <CmExecSection title="Recommended Migration Strategy" testid="cm-exec-strategy">
        <div className="rounded-sm border border-[#025979]/30 bg-gradient-to-br from-[#E0F2FE] to-[#F0F9FF] px-5 py-4">
          <p className="text-[9.5px] uppercase tracking-[0.12em] text-[#025979] font-bold mb-1">Transformation Strategy</p>
          <p data-testid="cm-exec-strategy-name" className="text-[19px] font-bold text-[#0F172A] leading-snug" style={{ fontFamily: "Chivo, sans-serif" }}>
            {strategyLabel}
          </p>
          {strategyDesc && <p className="mt-1.5 text-[12.5px] text-[#334155] leading-relaxed">{strategyDesc}</p>}
          {approachLabel && (
            <div className="mt-3 pt-3 border-t border-[#025979]/15">
              <p className="text-[9.5px] uppercase tracking-[0.12em] text-[#025979] font-bold mb-1">Implementation Approach</p>
              <p data-testid="cm-exec-approach-name" className="text-[15px] font-bold text-[#0F172A]" style={{ fontFamily: "Chivo, sans-serif" }}>{approachLabel}</p>
              {(narr.solution_explanation || approachDesc) && (
                <p className="mt-1 text-[12.5px] text-[#334155] leading-relaxed">{narr.solution_explanation || approachDesc}</p>
              )}
            </div>
          )}
        </div>
        {allReasons.length > 0 && (
          <div className="mt-4" data-testid="cm-exec-strategy-reasons">
            <p className="text-[9.5px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1.5">Migration Rationale</p>
            <ul className="space-y-1 text-[12.5px] text-[#374151] leading-relaxed">
              {allReasons.map((x, i) => (
                <li key={i} className="flex items-start gap-2"><span className="text-[#025979] mt-0.5">•</span><span>{x.description}</span></li>
              ))}
            </ul>
          </div>
        )}
      </CmExecSection>

      {/* SECTION 5 — Implementation Roadmap (shared components) */}
      <ExecImplementationNarrative inputs={implementationInputs} programme={null} />
      <ExecImplementationTimeline inputs={implementationInputs} />
      <ExecResourceAllocation inputs={implementationInputs} />

      {/* SECTION 6 — Investment Summary (shared component, Implementation-tab currency) */}
      <ExecInvestmentBreakdown inputs={implementationInputs} currency={implCurrency} exchangeRate={1} discountPct={0} />

      {/* SECTION 7 — Business Case / ROI (mirrors the ROI tab exactly) */}
      {out ? (
        <CmExecSection title="Business Case / ROI" testid="cm-exec-roi">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            {[
              ["Total Investment", fmtRoi(out.total_investment), "cm-exec-roi-investment"],
              ["Net Annual Benefit", fmtRoi(out.net_annual_benefit), "cm-exec-roi-net-annual"],
              ["Payback", out.no_payback ? "No Payback in Period" : `${out.payback_months} mo`, "cm-exec-roi-payback"],
              ["5-Year ROI", out.five_year_roi_percent != null ? `${out.five_year_roi_percent}%` : "—", "cm-exec-roi-5yr-roi"],
              ["5-Year NPV", fmtRoi(out.five_year_npv), "cm-exec-roi-5yr-npv"],
            ].map(([l, v, tid]) => (
              <div key={l} data-testid={tid} className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{l}</p>
                <p className="text-base font-bold text-[#111827] mt-1">{v}</p>
              </div>
            ))}
          </div>
          {(out.benefit_breakdown || []).length > 0 && (
            <div className="overflow-x-auto" data-testid="cm-exec-roi-benefits">
              <table className="w-full text-xs">
                <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="px-3 py-2">Benefit Category</th>
                    <th className="px-3 py-2 text-right">Baseline</th>
                    <th className="px-3 py-2 text-right">Applied %</th>
                    <th className="px-3 py-2 text-right">Gross</th>
                    <th className="px-3 py-2 text-right">Risk-Adjusted</th>
                  </tr>
                </thead>
                <tbody>
                  {out.benefit_breakdown.map((b) => (
                    <tr key={b.key} className="border-t border-[#F1F5F9]">
                      <td className="px-3 py-2 font-medium text-[#111827]">{b.name}</td>
                      <td className="px-3 py-2 text-right text-[#6B7280]">{fmtRoi(b.baseline)}</td>
                      <td className="px-3 py-2 text-right">{b.applied_pct}%</td>
                      <td className="px-3 py-2 text-right">{fmtRoi(b.gross_benefit)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtRoi(b.risk_adjusted_benefit)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-[#E5E7EB] font-bold">
                    <td className="px-3 py-2">Total</td><td /><td />
                    <td className="px-3 py-2 text-right">{fmtRoi(out.gross_annual_benefit)}</td>
                    <td className="px-3 py-2 text-right">{fmtRoi(out.risk_adjusted_annual_benefit)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4">
            <p className="text-[9.5px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-2">Strategic Benefits (non-financial)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 text-[12px] text-[#374151]">
              {CM_ROI_STRATEGIC_BENEFITS.map((b) => (
                <div key={b} className="flex items-start gap-2"><span className="text-[#025979] mt-0.5">•</span><span>{b}</span></div>
              ))}
            </div>
          </div>
        </CmExecSection>
      ) : (
        <CmExecSection title="Business Case / ROI" testid="cm-exec-roi">
          <p className="text-[12px] text-[#9CA3AF] italic">
            {cmRoi?.message || "ROI model for this transformation strategy is not yet configured."}
          </p>
        </CmExecSection>
      )}

      {/* SECTION 8 — Top Migration Recommendations */}
      {focusAreas.length > 0 && (
        <CmExecSection title="Top Migration Recommendations" testid="cm-exec-recommendations">
          <ul className="space-y-1.5 text-[12.5px] text-[#111827] leading-relaxed">
            {focusAreas.map((f, i) => (
              <li key={i} data-testid={`cm-exec-rec-${i}`} className="flex items-start gap-2">
                <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-[#025979] shrink-0" aria-hidden="true" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </CmExecSection>
      )}

      {/* SECTION 9 — Key Risks */}
      {risks.length > 0 && (
        <CmExecSection title="Key Risks" testid="cm-exec-risks">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {techRisks.length > 0 && (
              <div data-testid="cm-exec-risks-technical">
                <p className="text-[9.5px] uppercase tracking-wider text-[#B45309] font-bold mb-2.5">Technical Risks</p>
                <ul className="space-y-2.5 text-[12.5px] text-[#111827] leading-relaxed">
                  {techRisks.map((r, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#B45309] mt-0.5">▲</span>
                      <span><span className="font-semibold text-[#0F172A]">{r.title}</span> — {r.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {bizRisks.length > 0 && (
              <div data-testid="cm-exec-risks-business">
                <p className="text-[9.5px] uppercase tracking-wider text-[#025979] font-bold mb-2.5">Business Risks</p>
                <ul className="space-y-2.5 text-[12.5px] text-[#111827] leading-relaxed">
                  {bizRisks.map((r, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#025979] mt-0.5">▲</span>
                      <span><span className="font-semibold text-[#0F172A]">{r.title}</span> — {r.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </CmExecSection>
      )}

      {/* SECTION 10 — Next Steps (core) */}
      <CmExecSection title="Next Steps" testid="cm-exec-next-steps">
        <ol className="space-y-1.5 text-[12.5px] text-[#111827] leading-relaxed list-decimal list-inside">
          {CM_NEXT_STEPS.map((s, i) => (
            <li key={i} data-testid={`cm-exec-next-step-${i}`}>{s}</li>
          ))}
        </ol>
      </CmExecSection>
    </div>
  );
}

// --- Review Report sub-components --------------------------------------------
// =============================================================================
// iter 62 — Consulting-style Executive Summary
// Eight tightly-scoped text-first blocks replace the UI-heavy multi-section
// report. Every block reads already-computed values (no re-scoring, no ROI
// recomputation). Print-friendly, single-column, <1000px width.
// =============================================================================

const EXEC_WH_LABELS = {
  cdc: "Central Distribution Center",
  rdc: "Regional Distribution Center",
  manufacturing: "Manufacturing Plant Warehouse",
  ecommerce: "E-commerce Fulfillment Center",
  spare_parts: "Spare Parts / Service Warehouse",
  satellite: "Small / Satellite Warehouse",
};

const EXEC_ROLLOUT_LABELS = {
  pilot_phased: "Pilot + Phased Rollout",
  parallel: "Parallel Rollout",
  big_bang: "Big Bang",
};

function _execCountsByType(assessment) {
  const m3 = assessment?.module_data?.module3 || {};
  const out = {};
  Object.keys(m3).forEach((k) => {
    const n = Number(m3[k]?.op_warehouse_count);
    if (Number.isFinite(n) && n > 0) out[k] = n;
  });
  return out;
}

function _execTotalWarehouses(assessment) {
  const counts = _execCountsByType(assessment);
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return total || 1;
}

// ── Executive Summary: programme-level metadata helpers ─────────────────────
const EXEC_GEO_LABELS = {
  single_site: "Single Site",
  single_country: "Single Country",
  multi_country: "Multi-Country",
  multi_region: "Multi-Region",
  global: "Global",
};

const EXEC_BUSINESS_MODEL_LABELS = {
  b2b: "B2B",
  b2c: "B2C",
  both: "Both",
  b2b2c: "B2B2C",
  d2c: "D2C",
};

function execHumanize(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  if (s === s.toUpperCase() && s.length <= 4) return s; // keep acronyms (e.g. RDC)
  return s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function execPickStr(...vals) {
  for (const v of vals) {
    const s = v == null ? "" : String(v).trim();
    if (s) return s;
  }
  return null;
}

// Business-profile field readers — single source of truth shared by Network
// Coverage, Business Context and the Executive Recommendation panel. Industry /
// Business Model / Geography live in module_data.module2 (same as Client Inputs
// + Evaluation Insights), with a top-level / module1 fallback for legacy records.
function execIndustry(assessment) {
  const m1 = assessment?.module_data?.module1 || {};
  const m2 = assessment?.module_data?.module2 || {};
  const raw = execPickStr(m2.industry, assessment?.industry, m1.industry);
  return raw ? execHumanize(raw) : null;
}

function execBusinessModel(assessment) {
  const m1 = assessment?.module_data?.module1 || {};
  const m2 = assessment?.module_data?.module2 || {};
  const raw = execPickStr(m2.business_model, assessment?.business_model, m1.business_model);
  if (!raw) return null;
  return EXEC_BUSINESS_MODEL_LABELS[String(raw).toLowerCase()] || execHumanize(raw);
}

function execGeography(assessment) {
  const m2 = assessment?.module_data?.module2 || {};
  const raw = execPickStr(m2.geography, assessment?.geography);
  return raw ? (EXEC_GEO_LABELS[String(raw).toLowerCase()] || execHumanize(raw)) : null;
}

// Small label/value cell used across the executive report.
// Shared empty-value policy for all Executive Summary report types.
function execIsEmptyValue(value) {
  const raw = value == null ? "" : String(value).trim();
  return !raw || ["not specified", "unknown", "n/a", "—", "-", "null", "undefined"].includes(raw.toLowerCase());
}

function ExecMetaCell({ label, value, testid, strong = false }) {
  // Empty-field policy (shared across all report types): hide the cell
  // entirely rather than show a "Not Specified"/placeholder so the report
  // always reads as polished and complete.
  if (execIsEmptyValue(value)) return null;
  return (
    <div data-testid={testid}>
      <p className="text-[9px] uppercase tracking-[0.12em] text-[#6B7280] font-semibold">{label}</p>
      <p className={"text-[13px] leading-snug mt-0.5 " + (strong ? "text-[#025979] font-bold" : "text-[#111827] font-semibold")}>
        {value}
      </p>
    </div>
  );
}

// §1 — Full-width Programme Strategy banner (mirrors the Recommendations header).
function ExecStrategyBanner({ programme }) {
  if (!programme) return null;
  const cells = [
    { label: "Solution Strategy", value: programme.solutionDisplay, strong: true, testid: "exec-banner-solution" },
    { label: "Deployment Architecture", value: programme.deployment, testid: "exec-banner-deployment" },
    { label: "Hosting Recommendation", value: programme.hostingModel, testid: "exec-banner-hosting" },
    { label: "Coverage", value: programme.coverageSummary, testid: "exec-banner-coverage" },
  ];
  if (cells.every((c) => execIsEmptyValue(c.value))) return null;
  return (
    <section
      data-testid="exec-strategy-banner"
      className="rounded-sm border border-[#0093CB]/40 bg-gradient-to-br from-[#F0F9FF] to-white px-5 py-4"
    >
      <p className="text-[10px] uppercase tracking-[0.14em] text-[#025979] font-bold mb-3">
        Programme Strategy
      </p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-x-5 gap-y-3">
        {cells.map((c) => <ExecMetaCell key={c.label} {...c} />)}
      </div>
    </section>
  );
}

// §2 — Network Coverage (replaces the old Scope / Approach block).
function ExecNetworkCoverage({ assessment, programme }) {
  const types = (programme?.siteKeys || []).map((k) => EXEC_WH_LABELS[k] || groupLabel(k) || execHumanize(k)).filter(Boolean);
  const cells = [
    { label: "Industry", value: execIndustry(assessment), testid: "exec-coverage-industry" },
    { label: "Business Model", value: execBusinessModel(assessment), testid: "exec-coverage-business-model" },
    { label: isGroupedReport() ? "Warehouse Groups" : "Warehouse Types", value: types.length ? types.join(" + ") : null, testid: "exec-coverage-types" },
    { label: "Total Sites", value: programme ? String(programme.siteCount) : null, testid: "exec-coverage-sites" },
    { label: "Geographic Distribution", value: execGeography(assessment), testid: "exec-coverage-geography" },
  ];
  if (cells.every((c) => execIsEmptyValue(c.value))) return null;
  return (
    <section data-testid="exec-network-coverage" className="rounded-sm border border-[#E5E7EB] bg-white px-5 py-4">
      <p className="text-[10px] uppercase tracking-[0.14em] text-[#025979] font-bold mb-3">Network Coverage</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-3">
        {cells.map((c) => <ExecMetaCell key={c.label} {...c} />)}
      </div>
    </section>
  );
}

// §3a — Current Landscape (Step 2 answers). Self-contained "Assessment Details"
// section; hides completely (heading included) when no field is populated —
// e.g. Greenfield/Brownfield with no meaningful landscape data.
function ExecCurrentLandscape({ assessment }) {
  const ls = insightsReadLandscape(assessment) || {};
  const m2 = assessment?.module_data?.module2 || {};
  const cells = [
    { label: "Current SAP Landscape", value: execPickStr(ls.current_landscape_summary, execHumanize(m2.current_sap_solution)), testid: "exec-landscape-current" },
    { label: "Target Direction", value: execPickStr(ls.target_solution_direction, execHumanize(m2.target_solution_direction)), testid: "exec-landscape-target" },
    { label: "Transformation Objective", value: execHumanize(m2.primary_transformation_objective), testid: "exec-landscape-objective" },
    { label: "Architecture Preference", value: execHumanize(m2.preserve_architecture_importance), testid: "exec-landscape-architecture" },
  ];
  if (cells.every((c) => execIsEmptyValue(c.value))) return null;
  return (
    <section data-testid="exec-assessment-details" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
      <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
        Assessment Details
      </h3>
      <div data-testid="exec-current-landscape">
        <p className="text-[10px] uppercase tracking-[0.12em] text-[#6B7280] font-bold mb-2">Current Landscape</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-3">
          {cells.map((c) => <ExecMetaCell key={c.label} {...c} />)}
        </div>
      </div>
    </section>
  );
}

// §3b — Business Profile (industry / business model / geography / network).
function ExecBusinessProfile({ assessment, programme }) {
  const types = (programme?.siteKeys || []).map((k) => EXEC_WH_LABELS[k] || groupLabel(k) || execHumanize(k)).filter(Boolean);
  const cells = [
    { label: "Industry", value: execIndustry(assessment), testid: "exec-profile-industry" },
    { label: "Business Model", value: execBusinessModel(assessment), testid: "exec-profile-business-model" },
    { label: "Geographic Distribution", value: execGeography(assessment), testid: "exec-profile-geography" },
    { label: isGroupedReport() ? "Warehouse Groups" : "Warehouse Types", value: types.length ? types.join(" + ") : null, testid: "exec-profile-types" },
    { label: "Sites", value: programme ? String(programme.siteCount) : null, testid: "exec-profile-sites" },
  ];
  if (cells.every((c) => execIsEmptyValue(c.value))) return null;
  return (
    <div data-testid="exec-business-profile">
      <p className="text-[10px] uppercase tracking-[0.12em] text-[#6B7280] font-bold mb-2">Business Profile</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-3">
        {cells.map((c) => <ExecMetaCell key={c.label} {...c} />)}
      </div>
    </div>
  );
}

// §3c — Why This Recommendation (reuses generated explanation-tag drivers).
function ExecWhyThisRecommendation({ assessment }) {
  const tagsBySite = assessment?.explanation_tags_by_site || {};
  const seen = new Set();
  const drivers = [];
  Object.values(tagsBySite).forEach((tags) => {
    insightsDriverChipsFor(tags || []).forEach((t) => {
      const text = typeof t === "string" ? t : (t?.text || t?.label);
      if (text && !seen.has(text)) { seen.add(text); drivers.push(text); }
    });
  });
  const top = drivers.slice(0, 6);
  return (
    <div data-testid="exec-why-recommendation">
      <p className="text-[10px] uppercase tracking-[0.12em] text-[#6B7280] font-bold mb-2">Why This Recommendation</p>
      {top.length ? (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
          {top.map((d, i) => (
            <li key={i} className="text-[12.5px] text-[#374151] flex gap-2 leading-snug">
              <span className="text-[#0093CB] font-bold">›</span>
              <span>{d}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[12px] text-[#9CA3AF] italic">Recommendation rationale is detailed in the evaluation insights.</p>
      )}
    </div>
  );
}

// §10 — Structured Executive Recommendation decision panel.
function ExecExecutiveRecommendation({ programme, summary, assessment, currency, exchangeRate }) {
  const fx = (usd) => (usd == null ? "—" : fmtCurrency(Number(usd) * (exchangeRate || 1), currency));
  const annual = summary?.total_annual_savings;
  const investment = summary?.total_investment;
  const payback = summary?.overall_payback_months;
  return (
    <section
      data-testid="exec-executive-recommendation"
      className="rounded-sm border-2 border-[#025979] bg-[#F8FAFC] overflow-hidden"
    >
      <div className="bg-[#025979] px-5 py-2.5">
        <p className="text-[11px] uppercase tracking-[0.16em] text-white font-bold">Executive Recommendation</p>
        <p className="text-[10px] text-[#BAE6FD] mt-0.5">Recommended Programme</p>
      </div>
      <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-3">
        <ExecMetaCell label="Solution Strategy" value={programme?.solutionDisplay} strong testid="exec-rec-solution" />
        <ExecMetaCell label="Deployment Architecture" value={programme?.deployment} testid="exec-rec-deployment" />
        <ExecMetaCell label="Hosting Recommendation" value={programme?.hostingModel} testid="exec-rec-hosting" />
        <ExecMetaCell label="Coverage" value={programme?.coverageSummary} testid="exec-rec-coverage" />
        <ExecMetaCell label="Industry" value={execIndustry(assessment)} testid="exec-rec-industry" />
        <ExecMetaCell label="Business Model" value={execBusinessModel(assessment)} testid="exec-rec-business-model" />
        <ExecMetaCell label="Geographic Distribution" value={execGeography(assessment)} testid="exec-rec-geography" />
        <ExecMetaCell label="Investment" value={fx(investment)} strong testid="exec-rec-investment" />
        <ExecMetaCell label="Annual Benefit" value={fx(annual)} testid="exec-rec-benefit" />
        <ExecMetaCell label="Payback" value={payback != null ? `${Number(payback).toFixed(1)} Months` : "—"} testid="exec-rec-payback" />
      </div>
      <div className="px-5 pb-4">
        <p className="text-[9px] uppercase tracking-[0.12em] text-[#6B7280] font-semibold">Recommendation</p>
        <p className="text-[13px] text-[#111827] font-semibold mt-0.5" data-testid="exec-rec-verdict">
          Proceed to detailed blueprint and vendor selection.
        </p>
      </div>
    </section>
  );
}


// 1. Header — SAP EWM Transformation – Executive Summary · Client · Scope · Approach
function ExecReportHeader({ assessment, implementationInputs }) {
  const company = cleanCompanyName(assessment.company_name);
  const dateLabel = assessment.created_at ? formatReportDate(assessment.created_at) : null;

  return (
    <header
      data-testid="exec-header"
      className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5 print:border-0 print:shadow-none"
    >
      <p
        className="text-[10px] uppercase tracking-[0.22em] text-[#025979] font-semibold"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        SAP EWM Transformation — Executive Summary
      </p>
      <h2
        data-testid="exec-header-company"
        className="mt-2 text-2xl sm:text-[26px] font-bold text-[#0F172A] leading-tight"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        {company}
      </h2>
      {dateLabel && (
        <p className="mt-2 text-[12px] text-[#6B7280]">
          Prepared: <span className="text-[#111827] font-medium" data-testid="exec-header-prepared">{dateLabel}</span>
        </p>
      )}
    </header>
  );
}

// 2. Top metrics strip — 4 KPIs (same visual language as ROI tab)
function ExecKpiStrip({ summary, currency, exchangeRate = 1, discountPct = 0 }) {
  const fmt = (v) => formatCurrency(v == null ? v : v * exchangeRate, currency);
  const totalInvestment = summary?.total_investment;
  const annual = summary?.total_annual_savings;
  const payback = summary?.overall_payback_months;
  const roiPct = summary?.overall_roi_percent;
  const tiles = [
    { key: "investment", label: "Total Investment", value: totalInvestment != null ? fmt(totalInvestment) : "—", sub: discountPct > 0 ? `${Number(discountPct)}% discount applied` : "Includes CAPEX" },
    { key: "savings", label: "Annual Savings", value: annual != null ? fmt(annual) : "—", sub: "Steady-state year" },
    { key: "payback", label: "Payback Period", value: payback != null ? `${Number(payback).toFixed(1)} months` : "—", sub: "Cumulative" },
    { key: "roi", label: "3-Year ROI", value: roiPct != null ? `${Number(roiPct).toFixed(0)}%` : "—", sub: "Net benefit / investment" },
  ];
  return (
    <section
      data-testid="exec-kpi-strip"
      className="grid grid-cols-2 md:grid-cols-4 gap-3"
    >
      {tiles.map((t) => (
        <div
          key={t.key}
          data-testid={`exec-kpi-${t.key}`}
          className="bg-white border border-[#E5E7EB] rounded-sm p-4 print:border print:shadow-none"
        >
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280]">{t.label}</p>
          <p className="text-[22px] font-bold text-[#025979] mt-1 leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
            {t.value}
          </p>
          <p className="text-[10px] text-[#9CA3AF] mt-0.5">{t.sub}</p>
        </div>
      ))}
    </section>
  );
}

// 3. Business context — short paragraph + ROI-linked closing fact line
function ExecBusinessContext({ copy, summary, inputs, assessment, programme, currency, exchangeRate = 1, section = "full" }) {
  // iter 66: Single source of truth with the KPI Strip + Decision Statement.
  // `summary.total_investment` / `total_annual_savings` come straight from the
  // ROI engine and are NOT discount-multiplied on the backend. Applying the
  // frontend discount here would produce different numbers than the Decision
  // Statement (which also uses `value × exchangeRate`). We therefore use the
  // exact same formula as KPI Strip + Decision Statement: FX only.
  const fmt = (v) => formatCurrency((Number(v) || 0) * exchangeRate, currency);
  // Week total pulled from stored phase_groups so it matches the Implementation
  // tab's Total Duration tile exactly.
  const totalWeeks = (inputs?.phase_groups || []).reduce((wk, g) => (
    wk + (g.phases || []).reduce((n, p) => n + (Number(p.duration_weeks) || 0), 0)
  ), 0);
  const months = totalWeeks > 0 ? (totalWeeks / 4.345).toFixed(1) : null;
  const investment = Number(summary?.total_investment);
  const annual = Number(summary?.total_annual_savings);
  const roiPct = Number(summary?.overall_roi_percent);
  const payback = Number(summary?.overall_payback_months);
  const haveRoi = Number.isFinite(investment) && Number.isFinite(annual) && investment > 0 && annual > 0;
  const haveWeeks = totalWeeks > 0;
  let factLine = null;
  if (haveWeeks && haveRoi) {
    factLine = `Delivery spans ${totalWeeks} weeks (~${months} months) with a total investment of ${fmt(investment)}, delivering ${fmt(annual)} in annual savings — a ${Number.isFinite(roiPct) ? `${roiPct.toFixed(0)}% 3-year ROI` : "strong ROI"}${Number.isFinite(payback) ? ` and ${payback.toFixed(1)}-month payback` : ""}.`;
  } else if (haveRoi) {
    factLine = `Total investment of ${fmt(investment)} delivers ${fmt(annual)} in annual savings${Number.isFinite(roiPct) ? ` (${roiPct.toFixed(0)}% 3-year ROI)` : ""}${Number.isFinite(payback) ? `, ${payback.toFixed(1)}-month payback` : ""}.`;
  } else if (haveWeeks) {
    factLine = `Delivery spans ${totalWeeks} weeks (~${months} months).`;
  }

  // `section="summary"` → the AI-generated Executive Summary narrative only.
  if (section === "summary") {
    if (!copy) return null;
    return (
      <section data-testid="exec-executive-summary" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
          Executive Summary
        </h3>
        <p className="text-[13px] text-[#111827] leading-relaxed">{copy}</p>
      </section>
    );
  }

  // `section="context"` → the quantified Business Context fact line only.
  if (section === "context") {
    if (!factLine) return null;
    return (
      <section data-testid="exec-business-context" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
          Business Context
        </h3>
        <p
          data-testid="exec-business-headline"
          className="text-[13px] text-[#111827] leading-relaxed border-l-2 border-[#025979] pl-3"
        >
          {factLine}
        </p>
      </section>
    );
  }

  // Legacy combined layout (kept for safety; no longer used by the report).
  return (
    <section data-testid="exec-business-context" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
      <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
        Business Context
      </h3>
      <p className="text-[13px] text-[#111827] leading-relaxed">{copy}</p>
      {factLine && (
        <p data-testid="exec-business-headline" className="text-[13px] text-[#111827] leading-relaxed border-l-2 border-[#025979] pl-3 mt-3">
          {factLine}
        </p>
      )}
      <div className="mt-5 pt-4 border-t border-[#E5E7EB] space-y-5">
        <ExecCurrentLandscape assessment={assessment} />
        <ExecBusinessProfile assessment={assessment} programme={programme} />
        <ExecWhyThisRecommendation assessment={assessment} />
      </div>
    </section>
  );
}

// 3b. Key Evaluation Insights — compact per-warehouse-type cards (Header ·
// Capability Snapshot · Top Drivers · 1-line recommendation). Silent when
// there is nothing to show. Executive compression of the Evaluation
// Insights tab — uses the same helpers so wording stays consistent.
function ExecKeyEvaluationInsights({ assessment }) {
  if (!assessment) return null;
  const sites = Object.keys(assessment.final_recommendation_by_site || {});
  if (!sites.length) return null;
  const scorePills = [
    { k: "automation_score",  label: "Automation" },
    { k: "integration_score", label: "Integration" },
    { k: "inventory_score",   label: "Inventory" },
    { k: "outbound_score",    label: "Throughput" },
  ];
  const cards = sites.slice(0, 3).map((siteKey) => {
    const info = groupSiteInfo(assessment, siteKey);
    const label = INSIGHTS_WH_LABELS[siteKey] || groupLabel(siteKey) || info.label || warehouseLabel(siteKey);
    const eyebrow = info.eyebrow ? info.eyebrow.toUpperCase() : (groupLabel(siteKey) ? "" : String(siteKey).toUpperCase());
    const sections = (assessment.section_scores_by_site || {})[siteKey] || {};
    const rec = (assessment.final_recommendation_by_site || {})[siteKey];
    const deploy = (assessment.deployment_recommendation_by_site || {})[siteKey];
    const tags = (assessment.explanation_tags_by_site || {})[siteKey] || [];
    const conf = insightsConfidenceFor(assessment, siteKey);
    const driverChips = insightsDriverChipsFor(tags, []).slice(0, 3);
    const recCopy = insightsBuildRecCopy(rec, sections, []);
    return { siteKey, label, eyebrow, sections, rec, deploy, driverChips, recCopy, conf };
  });
  const isSingle = cards.length === 1;
  return (
    <section
      data-testid="exec-key-evaluation-insights"
      className="bg-white border border-[#E5E7EB] rounded-sm px-5 py-5"
    >
      <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
        Key Evaluation Insights
      </h3>
      <div
        data-testid="exec-key-insights-grid"
        className={isSingle
          ? "grid grid-cols-1 gap-3"
          : "grid grid-cols-1 md:grid-cols-3 gap-3"}
      >
        {cards.map((c) => (
          <article
            key={c.siteKey}
            data-testid={`exec-key-insight-${c.siteKey}`}
            className="border border-[#E5E7EB] rounded-sm p-3.5 flex flex-col gap-2.5 bg-[#FAFBFC]"
          >
            {/* Header */}
            <header className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                {c.eyebrow && <p className="text-[9px] uppercase tracking-[0.15em] text-[#9CA3AF]">{c.eyebrow}</p>}
                <p className="text-[12.5px] font-bold text-[#0F172A] leading-tight">{c.label}</p>
                {c.deploy && <p className="text-[10px] text-[#6B7280] mt-0.5">{c.deploy}</p>}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                {c.rec && (
                  <span
                    data-testid={`exec-key-insight-${c.siteKey}-rec`}
                    className="text-[10px] font-semibold text-[#025979] bg-[#E0F2FE] border border-[#025979]/25 rounded-sm px-1.5 py-0.5 whitespace-nowrap"
                  >
                    {c.rec}
                  </span>
                )}
              </div>
            </header>
            {/* Row 1 — Capability snapshot pills */}
            <div className="flex flex-wrap gap-1" data-testid={`exec-key-insight-${c.siteKey}-snapshot`}>
              {scorePills.map((p) => {
                const band = insightsScoreBand(c.sections[p.k]);
                if (band.label === "—") return null;
                return (
                  <span
                    key={p.k}
                    className="inline-flex items-center gap-1 text-[10px] rounded-sm border px-1.5 py-[2px]"
                    style={{ color: band.tone, borderColor: band.tone + "40", background: band.tone + "0D" }}
                  >
                    <span className="text-[#374151]">{p.label}:</span>
                    <span className="font-semibold uppercase tracking-wider">{band.label}</span>
                  </span>
                );
              })}
            </div>
            {/* Row 2 — Top drivers */}
            {c.driverChips.length > 0 && (
              <ul className="space-y-0.5 text-[11px] text-[#111827]" data-testid={`exec-key-insight-${c.siteKey}-drivers`}>
                {c.driverChips.map((d, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-[#025979] mt-0.5">•</span>
                    <span className="leading-snug">{d}</span>
                  </li>
                ))}
              </ul>
            )}
            {/* Row 3 — 1-line recommendation explanation */}
            <p className="text-[11px] text-[#374151] italic leading-snug" data-testid={`exec-key-insight-${c.siteKey}-copy`}>
              {c.recCopy}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

// Top Value Drivers — Executive Summary compressed version. Sits between
// Investment Breakdown and Benefit Timeline. Reads straight from the ROI
// engine's `combined_top_value_drivers` so values / percentages match the
// ROI tab's Top Value Drivers strip exactly.
function ExecTopValueDrivers({ summary, currency, exchangeRate = 1 }) {
  const total = Number(summary?.total_annual_savings) || 0;
  const drivers = Array.isArray(summary?.combined_top_value_drivers)
    ? summary.combined_top_value_drivers
    : [];
  if (drivers.length === 0 || total <= 0) return null;
  const fmt = (v) => formatCurrency((Number(v) || 0) * exchangeRate, currency);
  const top = drivers.slice(0, 3);
  return (
    <section data-testid="exec-top-value-drivers" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold" style={{ fontFamily: "Chivo, sans-serif" }}>
          Top Value Drivers
        </h3>
        <p className="text-[10.5px] text-[#9CA3AF]">Aggregated across all warehouse {isGroupedReport() ? "groups" : "types"} · % of annual savings</p>
      </div>
      <ol className="divide-y divide-[#F3F4F6]">
        {top.map((d, i) => {
          const amt = Number(d.amount) || 0;
          const pct = total > 0 ? Math.round((amt / total) * 100) : 0;
          return (
            <li
              key={d.key || d.label || i}
              data-testid={`exec-driver-${i}`}
              className="flex items-center gap-4 py-2"
            >
              <span className="w-5 text-[12px] font-semibold text-[#025979] tabular-nums">{i + 1}.</span>
              <span
                className="flex-1 min-w-0 truncate text-[12.5px] text-[#0F172A] font-medium"
                title={d.label}
                data-testid={`exec-driver-label-${i}`}
              >
                {d.label}
              </span>
              <span
                className="w-12 text-right text-[12.5px] font-semibold text-[#065F46] tabular-nums"
                data-testid={`exec-driver-pct-${i}`}
              >
                {pct}%
              </span>
              <span
                className="w-28 text-right text-[12.5px] font-bold text-[#0F172A] tabular-nums"
                data-testid={`exec-driver-value-${i}`}
              >
                {fmt(amt)}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// 4. Implementation plan — tight narrative, 2 lines per stage max.
function ExecImplementationNarrative({ inputs, programme }) {
  const mode = inputs?.rollout_mode || "pilot_phased";
  const rolloutStrategy = EXEC_ROLLOUT_LABELS[mode] || "Pilot + Phased Rollout";
  const implementationScope = (programme?.siteCount || 0) > 1
    ? "Multi-site transformation"
    : "Single-site rollout";
  const metaGrid = programme ? (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-3 mb-4 pb-4 border-b border-[#E5E7EB]" data-testid="exec-impl-meta">
      <ExecMetaCell label="Rollout Strategy" value={rolloutStrategy} strong testid="exec-impl-rollout" />
      <ExecMetaCell label="Implementation Scope" value={implementationScope} testid="exec-impl-scope" />
      <ExecMetaCell label="Deployment Architecture" value={programme.deployment} testid="exec-impl-deployment" />
      <ExecMetaCell label="Hosting Model" value={programme.hostingModel} testid="exec-impl-hosting" />
    </div>
  ) : null;
  if (!inputs) {
    return (
      <section data-testid="exec-impl-plan" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
          Implementation Plan
        </h3>
        {metaGrid}
        <p className="text-[12px] text-[#9CA3AF] italic">Implementation plan will appear once the consultant saves the Implementation Inputs.</p>
      </section>
    );
  }
  const groups = (inputs.phase_groups || []).filter((g) => (g.phases || []).length > 0);
  const weeksByStage = groups.map((g) => (g.phases || []).reduce((n, p) => n + (Number(p.duration_weeks) || 0), 0));
  const rows = groups.map((g, idx) => {
    const weeks = weeksByStage[idx];
    const startWk = weeksByStage.slice(0, idx).reduce((a, b) => a + b, 0) + 1;
    const endWk = startWk + weeks - 1;
    const assigns = (g.scope_assignments || []).filter((a) => Number(a.location_count) > 0);
    const scopeText = assigns.length
      ? assigns.map((a) => `${a.location_count} × ${EXEC_WH_LABELS[a.warehouse_type] || groupLabel(a.warehouse_type) || a.warehouse_type}`).join(" + ")
      : "Program-wide (common stage)";
    return { idx, name: g.name || `Stage ${idx + 1}`, weeks, startWk, endWk, scopeText };
  });
  return (
    <section data-testid="exec-impl-plan" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
      <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
        Implementation Plan
      </h3>
      {metaGrid}
      {rows.length === 0 ? (
        <p className="text-[12px] text-[#9CA3AF] italic">No implementation stages defined.</p>
      ) : (
        <ol className="space-y-2.5">
          {rows.map((r) => (
            <li
              key={r.idx}
              data-testid={`exec-impl-stage-${r.idx}`}
              className="text-[13px] text-[#111827] leading-relaxed"
            >
              <span className="font-semibold text-[#0F172A]">{r.name}</span>
              <span className="text-[#6B7280]">
                {" "}— Weeks {r.startWk}–{r.endWk} ({r.weeks} weeks)
              </span>
              <span className="block text-[12px] text-[#374151] mt-0.5">{r.scopeText}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// 5. Implementation Timeline — vertical milestone layout with phase drill-in
// and Go-live callouts. Compact enough to fit within the 1-page report.
function ExecImplementationTimeline({ inputs }) {
  if (!inputs) return null;
  const groups = (inputs.phase_groups || []).filter((g) => (g.phases || []).length > 0);
  if (groups.length === 0) return null;

  const shortLabel = {
    cdc: "CDC", rdc: "RDC", manufacturing: "Manufacturing",
    ecommerce: "E-commerce", spare_parts: "Spare Parts", satellite: "Satellite",
  };
  const isGoLivePhase = (name) => {
    // iter 65: milestone fires ONLY for a pure "Go-Live" phase. Combined
    // names like "Cutover & Go-Live" or "Pre, Cutover & Go-Live" are NOT
    // milestones — they are multi-step phases. Cutover alone is never a
    // milestone.
    const norm = String(name || "").trim().toLowerCase();
    return norm === "go-live" || norm === "go live" || norm === "golive";
  };

  const stageWeeksArr = groups.map((g) => (g.phases || []).reduce((n, p) => n + (Number(p.duration_weeks) || 0), 0));
  const rows = groups.map((g, idx) => {
    const phases = (g.phases || []).map((p) => ({
      name: p.name || "Phase",
      weeks: Number(p.duration_weeks) || 0,
      isGoLive: isGoLivePhase(p.name),
    }));
    const stageWeeks = stageWeeksArr[idx];
    const startWk = stageWeeksArr.slice(0, idx).reduce((a, b) => a + b, 0) + 1;
    const endWk = startWk + stageWeeks - 1;
    const assigns = (g.scope_assignments || []).filter((a) => Number(a.location_count) > 0);
    const scopeText = assigns.length
      ? assigns.map((a) => (Number(a.location_count) > 1
          ? `${a.location_count}× ${shortLabel[a.warehouse_type] || groupLabel(a.warehouse_type) || a.warehouse_type}`
          : (shortLabel[a.warehouse_type] || groupLabel(a.warehouse_type) || a.warehouse_type))).join(" + ")
      : null;
    const hasGoLive = phases.some((p) => p.isGoLive);
    return {
      idx,
      name: g.name || `Stage ${idx + 1}`,
      startWk, endWk, stageWeeks, scopeText, phases, hasGoLive,
    };
  });
  const totalWeeks = stageWeeksArr.reduce((a, b) => a + b, 0);

  return (
    <section data-testid="exec-impl-timeline" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
      <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
        Implementation Timeline
      </h3>
      <ol className="relative">
        {/* Continuous left rail — spans every row except the last half. */}
        <div
          aria-hidden
          className="absolute left-[7px] top-1 bottom-1 w-px bg-[#025979]/25 print:bg-[#025979]/40"
        />
        {rows.map((r, i) => (
          <li
            key={r.idx}
            data-testid={`exec-timeline-row-${r.idx}`}
            className={`relative pl-7 ${i === rows.length - 1 ? "" : "pb-4"}`}
          >
            {/* Milestone dot */}
            <span
              aria-hidden
              className={`absolute left-0 top-[3px] w-[15px] h-[15px] rounded-full border-2 ${
                r.hasGoLive
                  ? "bg-white border-[#EA580C] ring-2 ring-[#EA580C]/15"
                  : "bg-white border-[#025979]"
              }`}
            />
            {/* Header row: Week badge + stage name + duration + scope */}
            <div className="flex items-baseline flex-wrap gap-x-3 gap-y-0.5">
              <span
                data-testid={`exec-timeline-week-${r.idx}`}
                className="inline-flex items-center text-[10.5px] font-semibold uppercase tracking-wider text-[#025979] bg-[#E0F2FE] border border-[#025979]/25 rounded-sm px-2 py-0.5 tabular-nums"
              >
                Week {r.startWk}–{r.endWk}
              </span>
              <span className="text-[13px] font-semibold text-[#0F172A]">{r.name}</span>
              <span className="text-[11.5px] text-[#6B7280] tabular-nums">{r.stageWeeks} weeks</span>
              {r.scopeText && (
                <span className="text-[11.5px] text-[#374151]">· {r.scopeText}</span>
              )}
            </div>
            {/* Phase bullets */}
            {r.phases.length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {r.phases.map((p, pi) => (
                  <li
                    key={pi}
                    data-testid={`exec-timeline-phase-${r.idx}-${pi}`}
                    className="text-[12px] text-[#374151] flex items-baseline gap-2"
                  >
                    <span className="text-[#9CA3AF]">•</span>
                    <span>{p.name}</span>
                    <span className="text-[#9CA3AF] tabular-nums">· {p.weeks}w</span>
                    {p.isGoLive && (
                      <span
                        data-testid={`exec-timeline-golive-${r.idx}-${pi}`}
                        className="ml-1 inline-flex items-center text-[9.5px] font-semibold uppercase tracking-wider text-[#C2410C] bg-[#FFEDD5] border border-[#EA580C]/35 rounded-sm px-1.5 py-[1px]"
                      >
                        Go-Live Milestone
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
      {totalWeeks > 0 && (
        <p className="text-[11px] text-[#6B7280] mt-4 pl-7" data-testid="exec-timeline-total">
          Total program duration:{" "}
          <span className="font-semibold text-[#0F172A]">
            {totalWeeks} weeks ({(totalWeeks / 4.345).toFixed(1)} months)
          </span>
        </p>
      )}
    </section>
  );
}

// Helper — compute peak (max concurrent FTE) + phase + avg.
function _execComputePeakTeam(inputs) {
  const groups = inputs?.phase_groups || [];
  let peakFte = 0;
  let peakPhaseName = null;
  let peakStageName = null;
  let totalHours = 0;
  let totalWeeks = 0;
  for (const g of groups) {
    for (const p of (g.phases || [])) {
      const weeks = Number(p.duration_weeks) || 0;
      const effort = p.effort || {};
      const hours = Object.values(effort).reduce((n, v) => n + (Number(v) || 0), 0);
      totalHours += hours;
      totalWeeks += weeks;
      if (weeks > 0) {
        const fte = hours / (weeks * 40);
        if (fte > peakFte) {
          peakFte = fte;
          peakPhaseName = p.name || "";
          peakStageName = g.name || "";
        }
      }
    }
  }
  const avgFte = totalWeeks > 0 ? totalHours / (totalWeeks * 40) : 0;
  return {
    peakFte: Math.round(peakFte * 10) / 10,
    peakPhaseName,
    peakStageName,
    avgFte: Math.round(avgFte * 10) / 10,
    totalHours,
    totalWeeks,
  };
}

// 6. Execution Model — delivery model + peak team size + team-scaling narrative.
function ExecExecutionModel({ inputs }) {
  if (!inputs) return null;
  const peak = _execComputePeakTeam(inputs);
  const hasData = peak.totalHours > 0;
  const peakLocation = peak.peakStageName && peak.peakPhaseName
    ? `${peak.peakStageName} · ${peak.peakPhaseName}`
    : (peak.peakStageName || peak.peakPhaseName || "peak phase");
  return (
    <section data-testid="exec-execution-model" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
      <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
        Execution Model
      </h3>
      <ul className="space-y-2 text-[13px] text-[#111827] leading-relaxed">
        <li data-testid="exec-execution-delivery">
          <span className="text-[#6B7280]">Delivery model:</span>{" "}
          <span className="font-medium text-[#0F172A]">Offshore-led execution with onsite governance</span>
        </li>
        {hasData && (
          <li data-testid="exec-execution-peak">
            <span className="text-[#6B7280]">Peak team size:</span>{" "}
            <span className="font-medium text-[#0F172A]">
              {Math.ceil(peak.peakFte)} resources
            </span>
            <span className="text-[#6B7280]"> (during {peakLocation})</span>
          </li>
        )}
        <li data-testid="exec-execution-scaling" className="text-[#374151]">
          Team structure scales from a lean design team during Foundation, to a full execution team during Pilot and Rollout waves.
        </li>
      </ul>
    </section>
  );
}

// 7. Resource Allocation by Phase — strategic team structure. No head-counts.
function ExecResourceAllocation({ inputs }) {
  if (!inputs) return null;
  const groups = (inputs.phase_groups || []).filter((g) => (g.phases || []).length > 0);
  if (groups.length === 0) return null;

  // Map each stage to a strategic team-shape label by looking at the stage name.
  const bandFor = (name) => {
    const n = String(name || "").toLowerCase();
    if (n.includes("foundation") || n.includes("discover") || n.includes("blueprint")) return "lean";
    if (n.includes("pilot")) return "full";
    if (n.includes("wave") || n.includes("rollout") || n.includes("hypercare") || n.includes("big bang")) return "scaled";
    return "full";
  };
  const bandNarrative = {
    lean: "Lean team (Architect + Functional Lead)",
    full: "Full team (PM, Architects, Consultants)",
    scaled: "Scaled execution team (Consultants + support)",
  };

  return (
    <section data-testid="exec-resource-allocation" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
      <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
        Resource Allocation by Phase
      </h3>
      <dl className="space-y-2 text-[13px]">
        {groups.map((g, idx) => (
          <div
            key={idx}
            data-testid={`exec-resource-row-${idx}`}
            className="flex items-baseline gap-4"
          >
            <dt className="w-[120px] shrink-0 font-semibold text-[#0F172A]">{g.name || `Stage ${idx + 1}`}:</dt>
            <dd className="text-[#374151]">{bandNarrative[bandFor(g.name)]}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

// 8. Investment breakdown — structured CAPEX / OPEX text (unchanged).
function ExecInvestmentBreakdown({ inputs, currency, exchangeRate = 1, discountPct = 0 }) {
  const cb = inputs?.cost_breakdown || { one_time: {}, recurring: {} };
  const ot = cb.one_time || {};
  const rc = cb.recurring || {};
  const factor = exchangeRate * (1 - Math.max(0, Number(discountPct) || 0) / 100);
  const fmt = (v) => formatCurrency((Number(v) || 0) * factor, currency);
  const capexRows = [
    { label: "Implementation Fees", val: ot.implementation_fees, tid: "exec-capex-impl" },
    { label: "Hardware & Devices", val: ot.hardware_devices, tid: "exec-capex-hw" },
    { label: "Integration & Custom Development", val: ot.integration_custom_dev, tid: "exec-capex-int" },
    { label: "Internal Business Effort", val: ot.internal_business_effort, tid: "exec-capex-internal" },
  ];
  const opexRows = [
    { label: "SAP License / Subscription Delta", val: rc.sap_license_subscription_delta, tid: "exec-opex-license" },
    { label: "Annual Support / AMS", val: rc.annual_support_ams, tid: "exec-opex-ams" },
  ];
  const totalCapex = capexRows.reduce((n, r) => n + (Number(r.val) || 0), 0);
  const totalOpex = opexRows.reduce((n, r) => n + (Number(r.val) || 0), 0);
  const hasData = totalCapex > 0 || totalOpex > 0;
  return (
    <section data-testid="exec-investment-breakdown" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
      <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
        Investment Breakdown
      </h3>
      {!hasData ? (
        <p className="text-[12px] text-[#9CA3AF] italic">Cost model inputs not entered yet. Populate them from the Implementation tab.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-[11px] font-semibold text-[#025979] uppercase tracking-wider mb-2">One-Time Investment (CAPEX)</p>
            <dl className="space-y-1.5 text-[12.5px]">
              {capexRows.map((r) => (
                <div key={r.tid} className="flex justify-between items-baseline gap-3" data-testid={r.tid}>
                  <dt className="text-[#374151]">{r.label}</dt>
                  <dd className="text-[#0F172A] tabular-nums">{fmt(r.val)}</dd>
                </div>
              ))}
              <div className="flex justify-between items-baseline gap-3 border-t border-[#E5E7EB] pt-1.5 mt-1.5 font-semibold" data-testid="exec-capex-total">
                <dt className="text-[#0F172A]">Total CAPEX</dt>
                <dd className="text-[#025979] tabular-nums">{fmt(totalCapex)}</dd>
              </div>
            </dl>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-[#025979] uppercase tracking-wider mb-2">Annual Recurring (OPEX)</p>
            <dl className="space-y-1.5 text-[12.5px]">
              {opexRows.map((r) => (
                <div key={r.tid} className="flex justify-between items-baseline gap-3" data-testid={r.tid}>
                  <dt className="text-[#374151]">{r.label}</dt>
                  <dd className="text-[#0F172A] tabular-nums">{fmt(r.val)}</dd>
                </div>
              ))}
              <div className="flex justify-between items-baseline gap-3 border-t border-[#E5E7EB] pt-1.5 mt-1.5 font-semibold" data-testid="exec-opex-total">
                <dt className="text-[#0F172A]">Total Annual OPEX</dt>
                <dd className="text-[#025979] tabular-nums">{fmt(totalOpex)}</dd>
              </div>
            </dl>
          </div>
        </div>
      )}
    </section>
  );
}

// 6. Benefit timeline — Y1 / Y2 / Y3
function ExecBenefitTimeline({ summary, currency, exchangeRate = 1 }) {
  const fmt = (v) => formatCurrency((Number(v) || 0) * exchangeRate, currency);
  const sby = summary?.savings_by_year || {};
  const annual = Number(summary?.total_annual_savings) || 0;
  const phased = !!summary?.phased_enabled && (sby.year1 || sby.year2 || sby.year3);
  const y1 = phased ? Number(sby.year1) || 0 : annual;
  const y2 = phased ? Number(sby.year2) || 0 : annual;
  const y3 = phased ? Number(sby.year3) || 0 : annual;
  const total = y1 + y2 + y3;
  return (
    <section data-testid="exec-benefit-timeline" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
      <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
        Benefit Timeline
      </h3>
      <div className="grid grid-cols-3 gap-3 mb-2">
        <div data-testid="exec-benefit-y1" className="text-center">
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280]">Year 1</p>
          <p className="text-lg font-bold text-[#0F172A] mt-0.5 tabular-nums">{fmt(y1)}</p>
        </div>
        <div data-testid="exec-benefit-y2" className="text-center">
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280]">Year 2</p>
          <p className="text-lg font-bold text-[#0F172A] mt-0.5 tabular-nums">{fmt(y2)}</p>
        </div>
        <div data-testid="exec-benefit-y3" className="text-center">
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280]">Year 3</p>
          <p className="text-lg font-bold text-[#0F172A] mt-0.5 tabular-nums">{fmt(y3)}</p>
        </div>
      </div>
      <p className="text-[11.5px] text-[#374151] leading-relaxed" data-testid="exec-benefit-note">
        {phased
          ? `Savings ramp up progressively as warehouses go live, reaching steady-state by Year ${y3 >= y1 ? 3 : 2}. 3-year cumulative benefit: `
          : "Savings are realized at steady-state once go-live completes. 3-year cumulative benefit: "}
        <span className="font-semibold text-[#0F172A]">{fmt(total)}</span>.
      </p>
    </section>
  );
}

// 7. Key recommendations — 4–5 bullets sourced from strategy + drivers + narratives
function ExecKeyRecommendations({ assessment, summary, implementationInputs, narratives }) {
  const recs = assessment?.final_recommendation_by_site || {};
  const solutionSet = Array.from(new Set(Object.values(recs).filter(Boolean)));
  const primarySolution = solutionSet[0] || "the recommended SAP warehouse solution";
  const isHybrid = solutionSet.length > 1;
  const mode = implementationInputs?.rollout_mode || "pilot_phased";
  const modeLabel = EXEC_ROLLOUT_LABELS[mode] || "Pilot + Phased Rollout";
  const totalWh = _execTotalWarehouses(assessment);
  const payback = summary?.overall_payback_months;
  const roiPct = summary?.overall_roi_percent;
  const drivers = Array.isArray(summary?.combined_top_value_drivers)
    ? summary.combined_top_value_drivers.slice(0, 2).map((d) => d.label || d.name || d.driver).filter(Boolean)
    : [];

  const bullets = [];
  bullets.push(
    isHybrid
      ? `Adopt a hybrid SAP warehouse strategy — combining ${solutionSet.join(" + ")} — to match each facility's complexity and throughput profile.`
      : `Standardize the network on ${primarySolution} to deliver consistent execution across all ${totalWh} warehouse${totalWh === 1 ? "" : "s"}.`
  );
  bullets.push(
    `Deliver the transformation using a ${modeLabel} approach — Foundation first, a controlled pilot to de-risk the target design, then a phased rollout across remaining sites.`
  );
  if (drivers.length) {
    bullets.push(`Prioritize the highest-impact benefit drivers: ${drivers.join(" and ")}.`);
  } else {
    bullets.push("Prioritize labor productivity, inventory accuracy, and order-fulfilment throughput as the top business-value drivers.");
  }
  if (payback != null) {
    bullets.push(`Lock in the business case — a ${Number(payback).toFixed(1)}-month payback${roiPct != null ? ` and ${Number(roiPct).toFixed(0)}% 3-year ROI` : ""} gives the investment committee a clear decision frame.`);
  }
  bullets.push(
    "Stand up a joint governance board (client + consultant) to review stage gates, risks, and benefit realization on a bi-weekly cadence through go-live."
  );

  // Optional 5th bullet from narratives if we have any high-urgency signals
  const urgent = (narratives || []).find((n) => typeof n === "object" && /urgen|priorit|critical/i.test(JSON.stringify(n).slice(0, 500)));
  if (urgent && bullets.length < 6) {
    bullets.push("Address high-urgency operational gaps identified during the assessment before wave 1 cutover.");
  }

  return (
    <section data-testid="exec-key-recommendations" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
      <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
        Key Recommendations
      </h3>
      <ul className="space-y-2 text-[12.5px] text-[#111827] leading-relaxed">
        {bullets.slice(0, 5).map((b, i) => (
          <li key={i} data-testid={`exec-key-rec-${i}`} className="flex gap-2">
            <span className="text-[#025979] font-semibold mt-0.5">{i + 1}.</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// =============================================================================
// REDESIGNED Executive Summary (senior-stakeholder decision document).
// Presentation/consumption only — reuses finalized outputs from Insights,
// Recommendations, Implementation and the Warehouse Group ROI ("new Overall
// ROI"). No scoring / ROI / recommendation recomputation happens here.
// =============================================================================

// Business Case value-driver labels (map the S1–S5 codes to the approved
// senior-stakeholder wording). Falls back to the engine section title.
const EXEC_VALUE_DRIVER_LABELS = {
  S1: "Inbound & Warehouse Process Efficiency",
  S2: "Outbound Efficiency & Shipping Accuracy",
  S3: "Admin / Back-Office Process Savings",
  S4: "Inventory Accuracy & Shrinkage Savings",
  S5: "Replenishment, Downtime & Cost Avoidance",
};

// Human-readable current warehouse solution for a group (mirrors WarehouseGroups
// display logic). Never leaks a UUID.
function execGroupCurrentSolution(assessment, groupId) {
  const g = ((assessment && assessment.warehouse_groups) || []).find((x) => x.id === groupId);
  if (!g) return null;
  if (g.current_solution === "Other WMS" && g.current_solution_name) {
    return `Other WMS — ${g.current_solution_name}`;
  }
  if (!g.current_solution) return null;
  return g.current_solution + (g.current_version ? ` (v${g.current_version})` : "");
}

// Implementation stage names a given group is rolled out in (scope_assignments
// are keyed by group UUID after the Warehouse Group migration).
function execGroupStageNames(inputs, groupId) {
  const groups = ((inputs && inputs.phase_groups) || []).filter((g) => (g.phases || []).length > 0);
  const names = [];
  groups.forEach((g) => {
    const scoped = (g.scope_assignments || []).filter((a) => Number(a.location_count) > 0);
    const inScope = scoped.some((a) => a.warehouse_type === groupId);
    const programWide = scoped.length === 0; // stage with no group-scoped assignment covers all groups
    if (inScope || programWide) names.push(g.name || "Stage");
  });
  return names;
}

// Shared 4-metric tile strip (Total Investment · Annual Benefit · Payback · ROI).
function ExecMetricTiles({ investment, annual, payback, roi, cur, rate, testidPrefix }) {
  const fx = (v) => (v == null ? "—" : formatCurrency(Number(v) * (rate || 1), cur || "USD"));
  const mo = (m) => (m == null ? "—" : (m < 1 ? "<1 mo" : `${Number(m).toFixed(1)} months`));
  const pct = (p) => (p == null ? "—" : `${Math.round(Number(p)).toLocaleString()}%`);
  const tiles = [
    { key: "investment", label: "Total Investment", value: fx(investment), sub: "One-time (CAPEX)" },
    { key: "benefit", label: "Expected Annual Benefit", value: fx(annual), sub: "Expected (80%) scenario" },
    { key: "payback", label: "Payback", value: mo(payback), sub: "Cumulative" },
    { key: "roi", label: "3-Year ROI", value: pct(roi), sub: "Net benefit / investment" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {tiles.map((t) => (
        <div
          key={t.key}
          data-testid={`${testidPrefix}-${t.key}`}
          className="bg-white border border-[#025979]/25 rounded-sm p-4 print:border"
        >
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280]">{t.label}</p>
          <p className="text-[22px] font-bold text-[#025979] mt-1 leading-tight tabular-nums" style={{ fontFamily: "Chivo, sans-serif" }}>
            {t.value}
          </p>
          <p className="text-[10px] text-[#9CA3AF] mt-0.5">{t.sub}</p>
        </div>
      ))}
    </div>
  );
}

// ---- 1. EXECUTIVE DECISION SUMMARY ----
function ExecDecisionSummary({ assessment, programme, groupRoi, summary, narrative, currency, exchangeRate }) {
  const exp = groupRoi?.overall?.summary?.scenarios?.expected || null;
  const cur = groupRoi?.display_currency || currency || "USD";
  const rate = groupRoi?.exchange_rate || exchangeRate || 1;
  const investment = groupRoi?.total_one_time_investment ?? summary?.total_investment;
  const annual = exp?.gross_annual_savings ?? summary?.total_annual_savings;
  const payback = exp?.payback_months ?? summary?.overall_payback_months;
  const roi = exp?.three_year_roi_pct ?? summary?.overall_roi_percent;
  const metaCells = [
    { label: "Recommended Solution Strategy", value: programme?.solutionDisplay, strong: true, testid: "exec-decision-solution" },
    { label: "Deployment Architecture", value: programme?.deployment, testid: "exec-decision-deployment" },
    { label: "Hosting Recommendation", value: programme?.hostingModel, testid: "exec-decision-hosting" },
    { label: "Coverage", value: programme?.coverageSummary, testid: "exec-decision-coverage" },
  ];
  return (
    <section data-testid="exec-decision-summary" className="rounded-sm border-2 border-[#025979] bg-white overflow-hidden">
      <div className="bg-[#025979] px-6 py-3">
        <p className="text-[11px] uppercase tracking-[0.16em] text-white font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
          Executive Decision Summary
        </p>
        <p className="text-[13px] text-white font-semibold mt-0.5" data-testid="exec-decision-client">
          {cleanCompanyName(assessment?.company_name)}
        </p>
      </div>
      <div className="px-6 py-5 space-y-5">
        {narrative && (
          <p className="text-[13px] text-[#111827] leading-relaxed" data-testid="exec-decision-narrative">{narrative}</p>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-3">
          {metaCells.map((c) => <ExecMetaCell key={c.label} {...c} />)}
        </div>
        <ExecMetricTiles
          investment={investment} annual={annual} payback={payback} roi={roi}
          cur={cur} rate={rate} testidPrefix="exec-decision-metric"
        />
        <div className="border-t border-[#E5E7EB] pt-4">
          <p className="text-[9px] uppercase tracking-[0.12em] text-[#6B7280] font-semibold">Recommended Action</p>
          <p className="text-[13.5px] text-[#0F172A] font-semibold mt-0.5" data-testid="exec-decision-action">
            Proceed to detailed blueprint and vendor selection.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---- 2. WAREHOUSE STRATEGY ----
function ExecWarehouseStrategy({ assessment, groupRoi, implementationInputs }) {
  const groups = (groupRoi?.groups || []).filter((g) => Number(g.sites_in_scope) > 0);
  if (!groups.length) return null;
  const cur = groupRoi?.display_currency || "USD";
  const rate = groupRoi?.exchange_rate || 1;
  const fx = (v) => (v == null ? "—" : formatCurrency(Number(v) * rate, cur));
  const mo = (m) => (m == null ? "—" : (m < 1 ? "<1 mo" : `${Number(m).toFixed(1)} months`));
  const recs = assessment?.final_recommendation_by_site || {};
  const tagsBySite = assessment?.explanation_tags_by_site || {};
  return (
    <section data-testid="exec-warehouse-strategy" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
      <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
        Warehouse Strategy
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {groups.map((g) => {
          const exp = g.summary?.scenarios?.expected || {};
          const reasons = insightsDriverChipsFor(tagsBySite[g.group_id] || [], []).slice(0, 3);
          const stages = execGroupStageNames(implementationInputs, g.group_id);
          const currentSol = execGroupCurrentSolution(assessment, g.group_id);
          const recSol = recs[g.group_id];
          return (
            <article
              key={g.group_id}
              data-testid={`exec-strategy-group-${g.group_id}`}
              className="border border-[#E5E7EB] rounded-sm p-4 flex flex-col gap-2.5 bg-[#FAFBFC]"
            >
              <header className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className="text-[13.5px] font-bold text-[#0F172A] leading-tight" data-testid={`exec-strategy-name-${g.group_id}`}>
                    {g.group_name}
                  </p>
                  <p className="text-[9.5px] uppercase tracking-[0.14em] text-[#6D28D9] font-semibold mt-0.5">
                    {(g.scenario || "").toUpperCase()} · {g.sites_in_scope} of {g.total_sites} sites in scope
                  </p>
                </div>
              </header>
              <dl className="text-[11.5px] space-y-1">
                {currentSol && (
                  <div className="flex gap-2"><dt className="text-[#6B7280] w-[92px] shrink-0">Current</dt><dd className="text-[#111827] font-medium">{currentSol}</dd></div>
                )}
                {recSol && (
                  <div className="flex gap-2"><dt className="text-[#6B7280] w-[92px] shrink-0">Recommended</dt><dd className="text-[#025979] font-semibold" data-testid={`exec-strategy-rec-${g.group_id}`}>{recSol}</dd></div>
                )}
                {stages.length > 0 && (
                  <div className="flex gap-2"><dt className="text-[#6B7280] w-[92px] shrink-0">Rollout</dt><dd className="text-[#374151]">{stages.join(" → ")}</dd></div>
                )}
              </dl>
              {reasons.length > 0 && (
                <ul className="space-y-0.5 text-[11px] text-[#111827]">
                  {reasons.map((d, i) => (
                    <li key={i} className="flex items-start gap-1.5"><span className="text-[#025979] mt-0.5">•</span><span className="leading-snug">{d}</span></li>
                  ))}
                </ul>
              )}
              <div className="grid grid-cols-3 gap-2 pt-2 mt-auto border-t border-[#E5E7EB]">
                <div><div className="text-[9px] uppercase text-[#9CA3AF]">Annual Benefit</div><div className="text-[12px] font-semibold text-[#111827] tabular-nums">{fx(exp.gross_annual_savings ?? g.total_annual_cost_savings)}</div></div>
                <div><div className="text-[9px] uppercase text-[#9CA3AF]">Investment</div><div className="text-[12px] font-semibold text-[#111827] tabular-nums">{fx(g.allocated_investment)}</div></div>
                <div><div className="text-[9px] uppercase text-[#9CA3AF]">Payback</div><div className="text-[12px] font-semibold text-[#111827] tabular-nums">{mo(exp.payback_months)}</div></div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

// ---- 3. BUSINESS CASE ----
function ExecBusinessCase({ groupRoi, summary, currency, exchangeRate }) {
  const overall = groupRoi?.overall || null;
  const scen = overall?.summary?.scenarios || {};
  const exp = scen.expected || null;
  const cur = groupRoi?.display_currency || currency || "USD";
  const rate = groupRoi?.exchange_rate || exchangeRate || 1;
  const fx = (v) => (v == null ? "—" : formatCurrency(Number(v) * rate, cur));
  const pct = (p) => (p == null ? "—" : `${Math.round(Number(p)).toLocaleString()}%`);
  const investment = groupRoi?.total_one_time_investment ?? summary?.total_investment;
  const annual = exp?.gross_annual_savings ?? summary?.total_annual_savings;
  const payback = exp?.payback_months ?? summary?.overall_payback_months;
  const roi = exp?.three_year_roi_pct ?? summary?.overall_roi_percent;
  const sections = overall?.sections || [];
  const totalAnnual = overall?.total_annual_cost_savings;
  return (
    <section data-testid="exec-business-case" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
      <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
        Business Case
      </h3>
      <ExecMetricTiles
        investment={investment} annual={annual} payback={payback} roi={roi}
        cur={cur} rate={rate} testidPrefix="exec-case-metric"
      />
      {sections.length > 0 && (
        <div className="mt-5">
          <p className="text-[10px] uppercase tracking-[0.12em] text-[#025979] font-bold mb-2">Top Value Drivers</p>
          <table className="w-full">
            <tbody>
              {sections.map((s) => (
                <tr key={s.code} className="border-b border-[#F3F4F6]" data-testid={`exec-case-vd-${s.code}`}>
                  <td className="py-1.5 pr-3 text-[12px] text-[#111827]">
                    <span className="text-[#025979] font-semibold mr-1.5">{s.code}</span>
                    {EXEC_VALUE_DRIVER_LABELS[s.code] || s.title}
                  </td>
                  <td className="py-1.5 pl-2 text-right text-[12px] font-medium text-[#111827] tabular-nums">{fx(s.subtotal)}</td>
                </tr>
              ))}
              <tr>
                <td className="pt-2 pr-3 text-[12px] font-semibold text-[#111827] uppercase tracking-wide">Total Annual Savings (Gross, 100%)</td>
                <td className="pt-2 pl-2 text-right text-[14px] font-bold text-[#025979] tabular-nums" data-testid="exec-case-vd-total">{fx(totalAnnual)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {(scen.conservative || scen.optimistic) && (
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-[10.5px] text-[#6B7280] border-t border-[#E5E7EB] pt-3" data-testid="exec-case-secondary-scenarios">
          <span>Conservative: {fx(scen.conservative?.gross_annual_savings)}/yr · {pct(scen.conservative?.three_year_roi_pct)} 3-Yr ROI</span>
          <span>Optimistic: {fx(scen.optimistic?.gross_annual_savings)}/yr · {pct(scen.optimistic?.three_year_roi_pct)} 3-Yr ROI</span>
        </div>
      )}
    </section>
  );
}

// ---- 4. IMPLEMENTATION ROADMAP ----
function ExecImplementationRoadmap({ inputs, programme }) {
  const stages = ((inputs && inputs.phase_groups) || []).filter((g) => (g.phases || []).length > 0);
  const weeksByStage = stages.map((g) => (g.phases || []).reduce((n, p) => n + (Number(p.duration_weeks) || 0), 0));
  const totalWeeks = weeksByStage.reduce((a, b) => a + b, 0);
  const months = totalWeeks > 0 ? (totalWeeks / 4.345).toFixed(1) : null;
  const peak = inputs ? _execComputePeakTeam(inputs) : null;
  const mode = inputs?.rollout_mode || "pilot_phased";
  const rolloutStrategy = EXEC_ROLLOUT_LABELS[mode] || "Pilot + Phased Rollout";
  const shortLabel = { cdc: "CDC", rdc: "RDC", manufacturing: "Manufacturing", ecommerce: "E-commerce", spare_parts: "Spare Parts", satellite: "Satellite" };
  if (!inputs || stages.length === 0) {
    return (
      <section data-testid="exec-implementation-roadmap" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
          Implementation Roadmap
        </h3>
        <p className="text-[12px] text-[#9CA3AF] italic">Implementation roadmap will appear once the consultant saves the Implementation Inputs.</p>
      </section>
    );
  }
  const rows = stages.map((g, idx) => {
    const weeks = weeksByStage[idx];
    const startWk = weeksByStage.slice(0, idx).reduce((a, b) => a + b, 0) + 1;
    const endWk = startWk + weeks - 1;
    const assigns = (g.scope_assignments || []).filter((a) => Number(a.location_count) > 0);
    const scopeText = assigns.length
      ? assigns.map((a) => (Number(a.location_count) > 1
          ? `${a.location_count}× ${shortLabel[a.warehouse_type] || groupLabel(a.warehouse_type) || a.warehouse_type}`
          : (shortLabel[a.warehouse_type] || groupLabel(a.warehouse_type) || a.warehouse_type))).join(" + ")
      : "Program-wide";
    return { idx, name: g.name || `Stage ${idx + 1}`, weeks, startWk, endWk, scopeText };
  });
  const metaCells = [
    { label: "Total Duration", value: months ? `${totalWeeks} weeks (~${months} months)` : null, strong: true, testid: "exec-roadmap-duration" },
    { label: "Peak Team Size", value: peak && peak.peakFte > 0 ? `${Math.ceil(peak.peakFte)} resources` : null, testid: "exec-roadmap-peak" },
    { label: "Delivery Model", value: "Offshore-led execution with onsite governance", testid: "exec-roadmap-delivery" },
    { label: "Rollout Strategy", value: rolloutStrategy, testid: "exec-roadmap-rollout" },
  ];
  return (
    <section data-testid="exec-implementation-roadmap" className="bg-white border border-[#E5E7EB] rounded-sm px-6 py-5">
      <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
        Implementation Roadmap
      </h3>
      {/* Stage flow */}
      <div className="flex items-center flex-wrap gap-2 mb-5" data-testid="exec-roadmap-flow">
        {rows.map((r, i) => (
          <div key={r.idx} className="flex items-center gap-2">
            <span className="inline-flex items-center text-[11px] font-semibold text-[#025979] bg-[#E0F2FE] border border-[#025979]/25 rounded-sm px-2.5 py-1">
              {r.name}
            </span>
            {i < rows.length - 1 && <span className="text-[#0093CB] font-bold">→</span>}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-3 mb-4 pb-4 border-b border-[#E5E7EB]">
        {metaCells.map((c) => <ExecMetaCell key={c.label} {...c} />)}
      </div>
      <ol className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.idx} data-testid={`exec-roadmap-stage-${r.idx}`} className="text-[12.5px] text-[#111827] flex items-baseline flex-wrap gap-x-2">
            <span className="font-semibold text-[#0F172A]">{r.name}</span>
            <span className="text-[#6B7280] tabular-nums">Weeks {r.startWk}–{r.endWk} ({r.weeks}w)</span>
            <span className="text-[#374151]">· {r.scopeText}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ---- 5. KEY RECOMMENDATIONS & NEXT STEPS ----
function ExecRecommendationsNextSteps({ assessment, groupRoi, summary, implementationInputs, narratives }) {
  const recs = assessment?.final_recommendation_by_site || {};
  const solutionSet = Array.from(new Set(Object.values(recs).filter(Boolean)));
  const primarySolution = solutionSet[0] || "the recommended SAP warehouse solution";
  const isHybrid = solutionSet.length > 1;
  const mode = implementationInputs?.rollout_mode || "pilot_phased";
  const modeLabel = EXEC_ROLLOUT_LABELS[mode] || "Pilot + Phased Rollout";
  const exp = groupRoi?.overall?.summary?.scenarios?.expected || null;
  const payback = exp?.payback_months ?? summary?.overall_payback_months;
  const roiPct = exp?.three_year_roi_pct ?? summary?.overall_roi_percent;
  const topDriver = (() => {
    const secs = (groupRoi?.overall?.sections || []).slice().sort((a, b) => (b.subtotal || 0) - (a.subtotal || 0));
    const first = secs[0];
    return first ? (EXEC_VALUE_DRIVER_LABELS[first.code] || first.title) : null;
  })();

  const bullets = [];
  bullets.push(
    isHybrid
      ? `Adopt a hybrid SAP warehouse strategy — combining ${solutionSet.join(" + ")} — matched to each group's complexity and throughput.`
      : `Standardize the network on ${primarySolution} for consistent, scalable warehouse execution.`
  );
  bullets.push(
    `Deliver via a ${modeLabel} approach — Foundation first, a controlled pilot to de-risk the target design, then a phased rollout.`
  );
  if (payback != null || topDriver) {
    const paybackText = payback == null ? null : (Number(payback) < 1 ? "sub-one-month" : `${Number(payback).toFixed(1)}-month`);
    bullets.push(
      payback != null
        ? `Lock in the business case — a ${paybackText} payback${roiPct != null ? ` and ${Math.round(Number(roiPct)).toLocaleString()}% 3-year ROI` : ""}${topDriver ? `, led by ${topDriver.toLowerCase()}` : ""}.`
        : `Prioritize the highest-impact value driver: ${topDriver}.`
    );
  }
  const keyRecs = bullets.slice(0, 3);
  const nextSteps = GF_NEXT_STEPS.slice(0, 3);

  return (
    <section data-testid="exec-recommendations-next-steps" className="rounded-sm border-2 border-[#025979] bg-[#F8FAFC] overflow-hidden">
      <div className="bg-[#025979] px-6 py-2.5">
        <p className="text-[11px] uppercase tracking-[0.16em] text-white font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
          Key Recommendations & Next Steps
        </p>
      </div>
      <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.12em] text-[#6B7280] font-semibold mb-2">Key Recommendations</p>
          <ul className="space-y-2 text-[12.5px] text-[#111827] leading-relaxed">
            {keyRecs.map((b, i) => (
              <li key={i} data-testid={`exec-key-rec-${i}`} className="flex gap-2">
                <span className="text-[#025979] font-semibold mt-0.5">{i + 1}.</span><span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.12em] text-[#6B7280] font-semibold mb-2">Immediate Next Steps</p>
          <ol className="space-y-2 text-[12.5px] text-[#111827] leading-relaxed list-decimal list-inside">
            {nextSteps.map((s, i) => (
              <li key={i} data-testid={`exec-next-step-${i}`}>{s}</li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}


// =============================================================================
// Legacy report sub-components (kept for PDF export / snapshot hydration)
// =============================================================================
function ReportHeader({ assessment }) {
  const company = cleanCompanyName(assessment.company_name);
  const scenario = assessment.scenario_type ? toSentenceCase(assessment.scenario_type) : null;
  const dateLabel = assessment.created_at ? formatReportDate(assessment.created_at) : null;
  const meta = [
    scenario ? `Scenario: ${scenario}` : null,
    dateLabel ? `Evaluation date: ${dateLabel}` : null,
  ].filter(Boolean);

  return (
    <div
      data-testid="report-header"
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <div className="text-center py-8 px-5 sm:px-8">
        <p
          data-testid="report-header-title"
          className="text-[11px] uppercase tracking-[0.22em] text-[#025979] font-semibold"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Warehouse Strategy Assessment
        </p>
        <h2
          data-testid="report-header-company"
          className="mt-3 text-2xl sm:text-3xl font-bold text-[#111827] leading-tight"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {company}
        </h2>
        {meta.length > 0 && (
          <p data-testid="report-header-meta" className="mt-3 text-xs text-[#6B7280]">
            {meta.join(" · ")}
          </p>
        )}
      </div>
    </div>
  );
}

// Accent palette — every accent must stay low-saturation so the report
// keeps its consulting look rather than drifting into dashboard territory.
const REPORT_ACCENTS = {
  neutral: { top: "#9CA3AF", title: "#111827" },  // grey
  blue:    { top: "#93C5FD", title: "#1E3A8A" },  // soft blue
  green:   { top: "#86EFAC", title: "#065F46" },  // soft green — emphasized
  purple:  { top: "#C8BFE3", title: "#4C1D95" },  // soft purple
  slate:   { top: "#CBD5E1", title: "#111827" },  // near-neutral grey
};

/**
 * Consulting-style report section wrapper.
 *
 * @param accent   "neutral" | "blue" | "green" | "purple" | "slate"
 *                 Drives a thin (3px) top border color. Low-saturation only.
 * @param tone     "white" | "muted" — background alternates across sections so
 *                 long reports remain scannable.
 * @param emphasis Adds a little more visual weight (used for the ROI section).
 */
function ReportSection({
  title,
  children,
  testid,
  accent = "slate",
  tone = "white",
  emphasis = false,
}) {
  const a = REPORT_ACCENTS[accent] || REPORT_ACCENTS.slate;
  const bg = tone === "muted" ? "bg-[#FAFAFA]" : "bg-white";
  return (
    <section
      data-testid={testid}
      className={`${bg} border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden ${
        emphasis ? "ring-1 ring-emerald-100" : ""
      }`}
      style={{ borderTop: `3px solid ${a.top}` }}
    >
      <div className="px-5 sm:px-7 pt-5 pb-4 border-b border-[#E5E7EB]">
        <h3
          className="text-[15px] sm:text-base font-semibold tracking-tight"
          style={{ fontFamily: "Chivo, sans-serif", color: a.title }}
        >
          {title}
        </h3>
      </div>
      <div className="px-5 sm:px-7 py-6">{children}</div>
    </section>
  );
}

function buildStrategyMode(assessment) {
  const recs = assessment.final_recommendation_by_site || {};
  const unique = Array.from(new Set(Object.values(recs).filter(Boolean)));
  return unique.length > 1 ? "hybrid" : "single";
}

// Build a 2–3 sentence, business-focused executive summary on the fly. Uses
// only already-computed values — never recalculates scoring or ROI.
function buildExecutiveSummary(assessment, roiBySite, complexityKey, strategyMode) {
  const recs = assessment.final_recommendation_by_site || {};
  const types = Object.keys(recs);
  const totalWarehouses = types.reduce((n, k) => n + (roiBySite?.[k]?.warehouse_count || 1), 0);

  const complexityWord = complexityKey === "high"
    ? "significant operational complexity"
    : complexityKey === "medium"
      ? "moderate operational complexity"
      : "relatively lean operational complexity";

  const scaleWord = totalWarehouses >= 10
    ? "a large multi-site network"
    : totalWarehouses >= 3
      ? "a multi-site network"
      : "a focused warehouse footprint";

  if (strategyMode === "hybrid") {
    return (
      `Based on ${scaleWord} of ${types.length} warehouse type${types.length === 1 ? "" : "s"} ` +
      `and ${complexityWord}, a hybrid SAP warehouse strategy is recommended. ` +
      `High-volume and integration-heavy facilities require advanced warehouse execution capabilities, while ` +
      `smaller or less complex sites are better suited for lean, cost-efficient deployment. ` +
      `This approach balances operational efficiency with investment optimization.`
    );
  }
  return (
    `Based on ${scaleWord} and ${complexityWord}, a single SAP warehouse strategy is recommended. ` +
    `The selected solution aligns with the network's throughput, automation, and integration profile, ` +
    `delivering consistent execution across all warehouses. ` +
    `This approach keeps the operating model simple while preparing the network for continued growth.`
  );
}

// Second sentence added to the Executive Summary once ROI + Implementation
// Inputs are available. Stitches the headline numbers (payback, Y1 savings)
// together with the delivery timeline so execs see the full picture upfront.
// Returns null if neither ROI nor implementation data is available.
function buildExecutiveHeadline(summary, currency, implementationInputs, exchangeRate = 1, discountPct = 0) {
  const fmt = (v) => formatCurrency(v == null ? v : v * exchangeRate, currency);
  const discountFactor = 1 - Math.max(0, Number(discountPct) || 0) / 100;
  const fmtImpl = (v) => formatCurrency(v == null ? v : v * exchangeRate * discountFactor, currency);
  const hasRoi = summary && (summary.payback_months != null || summary.year1_net_savings_sum != null);
  const hasImpl = implementationInputs && (implementationInputs.phase_groups || []).some((g) => (g.phases || []).length > 0);
  if (!hasRoi && !hasImpl) return null;

  const parts = [];
  if (hasRoi) {
    const pieces = [];
    if (summary.year1_net_savings_sum != null) {
      pieces.push(`Year 1 net savings of ${fmt(summary.year1_net_savings_sum)}`);
    }
    if (summary.payback_months != null) {
      pieces.push(`a ${Number(summary.payback_months).toFixed(1)}-month payback`);
    }
    if (summary.three_year_net_savings_sum != null) {
      pieces.push(`${fmt(summary.three_year_net_savings_sum)} over three years`);
    }
    if (pieces.length) {
      parts.push(`The business case delivers ${joinPiecesWithAnd(pieces)}.`);
    }
  }
  if (hasImpl) {
    const core = computeCore(implementationInputs);
    const totalCost = core.totalCost;
    const months = core.totalWeeks > 0 ? (core.totalWeeks / 4.345).toFixed(1) : "0";
    parts.push(
      `Delivery spans ${core.totalWeeks} weeks (~${months} months) at an estimated implementation cost of ${fmtImpl(totalCost)}.`
    );
  }
  return parts.join(" ");
}

function joinPiecesWithAnd(list) {
  if (!list || list.length === 0) return "";
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")}, and ${list[list.length - 1]}`;
}

// --- Overall strategy --------------------------------------------------------
// Splits the per-site recommendations into `solution → [warehouse type labels]`
// buckets so hybrid strategies read clearly (e.g. "EWM Advanced for DCs").
function groupSolutionsByType(assessment) {
  const recs = assessment.final_recommendation_by_site || {};
  const map = new Map();
  Object.entries(recs).forEach(([site, solution]) => {
    if (!solution) return;
    if (!map.has(solution)) map.set(solution, []);
    map.get(solution).push(site);
  });
  return Array.from(map.entries()).map(([solution, sites]) => ({
    solution,
    sites,
    labels: sites.map(s => pluralizeWarehouseLabel(s)),
  }));
}

function pluralizeWarehouseLabel(key) {
  const base = warehouseLabel(key);
  if (!base || base === "—") return base;
  if (/s$/i.test(base)) return base;
  if (/y$/i.test(base)) return base.replace(/y$/i, "ies");
  return `${base}s`;
}

function OverallStrategyBlock({ assessment }) {
  const groups = groupSolutionsByType(assessment);
  const mode = buildStrategyMode(assessment);
  const deployment = assessment.overall_deployment_type;
  // Derive a headline solution label — for single strategies use the one
  // solution; for hybrids use a compact summary. Mirrors the client-facing
  // proposal header so Evaluate, PDF, and Client Dashboard stay in lock-step.
  const headlineSolution = (() => {
    if (groups.length === 0) return null;
    if (groups.length === 1) return groups[0].solution;
    const short = groups.map(g => (g.solution || "").replace("SAP ", ""));
    return "Hybrid (" + short.join(" + ") + ")";
  })();

  if (groups.length === 0) {
    return (
      <p className="text-xs text-[#9CA3AF] italic">
        Strategy will appear once scoring is complete.
      </p>
    );
  }

  return (
    <div data-testid="strategy-block" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span
          data-testid="strategy-mode-pill"
          className={`inline-flex items-center px-3 py-1 rounded-sm text-xs font-semibold ${
            mode === "hybrid"
              ? "bg-[#025979] text-white"
              : "bg-[#E0F2FE] text-[#025979] border border-[#025979]/20"
          }`}
        >
          {mode === "hybrid" ? "Hybrid strategy recommended" : "Single-solution strategy recommended"}
        </span>
      </div>

      {(headlineSolution || deployment) && (
        <div
          data-testid="strategy-headline"
          className="flex flex-wrap items-baseline gap-x-6 gap-y-1 border border-[#E5E7EB] rounded-sm px-4 py-3 bg-[#F8FAFC]"
        >
          {headlineSolution && (
            <div data-testid="strategy-headline-solution">
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280] mr-2">Solution</span>
              <span className="text-sm font-semibold text-[#111827]">{headlineSolution}</span>
            </div>
          )}
          {deployment && (
            <div data-testid="strategy-headline-deployment">
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280] mr-2">Deployment</span>
              <span className="text-sm font-semibold text-[#111827]">{deployment}</span>
            </div>
          )}
        </div>
      )}

      <div className="border border-[#E5E7EB] rounded-sm divide-y divide-[#E5E7EB] overflow-hidden">
        {groups.map((g, idx) => (
          <div
            key={g.solution}
            data-testid={`strategy-row-${idx}`}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 px-4 py-3 bg-white"
          >
            <p className="text-sm text-[#111827]">
              <span className="font-semibold">{g.solution}</span>
              <span className="text-[#6B7280]"> for {joinWithAnd(g.labels.map(l => l.toLowerCase()))}</span>
            </p>
            <span className="text-[11px] text-[#9CA3AF] shrink-0">
              {g.sites.length} type{g.sites.length === 1 ? "" : "s"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function joinWithAnd(items) {
  const arr = items.filter(Boolean);
  if (arr.length <= 1) return arr.join("");
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
  return `${arr.slice(0, -1).join(", ")}, and ${arr[arr.length - 1]}`;
}

// --- Network overview --------------------------------------------------------
function NetworkOverview({ assessment, roiBySite }) {
  const recs = assessment.final_recommendation_by_site || {};
  const types = Object.keys(recs);
  const rows = types.map(k => {
    const count = roiBySite?.[k]?.warehouse_count ?? 1;
    return { key: k, label: warehouseLabel(k), count };
  });
  const totalWh = rows.reduce((n, r) => n + (Number(r.count) || 0), 0);

  if (rows.length === 0) {
    return (
      <p className="text-xs text-[#9CA3AF] italic">
        Warehouse network details will appear once scoring is complete.
      </p>
    );
  }

  return (
    <div data-testid="network-block">
      <p data-testid="network-headline" className="text-base sm:text-lg text-[#111827] mb-4">
        <span className="font-semibold">{rows.length}</span> warehouse type{rows.length === 1 ? "" : "s"}{" "}
        across{" "}
        <span className="font-semibold">{totalWh}</span> site{totalWh === 1 ? "" : "s"}
      </p>
      <ul className="divide-y divide-[#E5E7EB] border border-[#E5E7EB] rounded-sm overflow-hidden">
        {rows.map(r => (
          <li
            key={r.key}
            data-testid={`network-row-${r.key}`}
            className="flex items-center justify-between px-4 py-2.5 bg-white"
          >
            <span className="text-sm text-[#111827]">{pluralizeWarehouseLabel(r.key)}</span>
            <span className="text-xs text-[#6B7280]">
              <span className="text-[#111827] font-semibold">{r.count}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Per-site cards ----------------------------------------------------------
function PerSiteCards({ assessment, narratives, roiBySite, narrativesLoaded }) {
  const recs = assessment.final_recommendation_by_site || {};
  const deploys = assessment.deployment_recommendation_by_site || {};
  const deployReasoning = assessment.deployment_reasoning_by_site || {};
  const byKey = Object.fromEntries((narratives || []).map(n => [n.site_key, n]));
  const types = Object.keys(recs);

  if (types.length === 0) {
    return (
      <p className="text-xs text-[#9CA3AF] italic">
        Recommendations will appear once scoring is complete.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="per-site-grid">
      {types.map(k => {
        const narr = byKey[k];
        const solution = recs[k];
        const deployment = deploys[k];
        const deploymentReason = (deployReasoning[k] || {}).reason;
        const count = roiBySite?.[k]?.warehouse_count ?? 1;
        const drivers = (narr?.business_drivers || []).slice(0, 3);
        const explanation = narr?.solution_explanation;

        return (
          <article
            key={k}
            data-testid={`per-site-card-${k}`}
            className="bg-white border border-[#E5E7EB] rounded-sm p-5 flex flex-col"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-0.5">Warehouse type</p>
                <h4 className="text-sm sm:text-base font-semibold text-[#111827] leading-tight">{warehouseLabel(k)}</h4>
                <p className="text-[11px] text-[#6B7280] mt-0.5">
                  {count} site{count === 1 ? "" : "s"} in the network
                </p>
              </div>
              <span className="shrink-0 px-2.5 py-0.5 bg-[#025979] text-white rounded-sm text-[11px] font-semibold">
                {solution || "—"}
              </span>
            </div>

            {deployment && (
              <div className="mb-3 border-l-2 border-[#025979]/40 pl-3">
                <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Deployment model</p>
                <p className="text-sm text-[#111827]">{deployment}</p>
                {deploymentReason && (
                  <p
                    data-testid={`per-site-deployment-reason-${k}`}
                    className="text-[11px] text-[#6B7280] mt-1 leading-snug"
                  >
                    Reason: {deploymentReason}
                  </p>
                )}
              </div>
            )}

            {narrativesLoaded && drivers.length > 0 && (
              <div className="mb-3">
                <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1.5">Business drivers</p>
                <ul className="space-y-1.5">
                  {drivers.map((d, idx) => (
                    <li
                      key={`${k}-drv-${idx}`}
                      data-testid={`per-site-driver-${k}-${idx}`}
                      className="flex items-start gap-2 text-[12px] text-[#111827] leading-snug"
                    >
                      <span aria-hidden className="mt-[5px] w-1.5 h-1.5 rounded-full bg-[#025979] shrink-0" />
                      <span>
                        <span className="font-medium">{d.label}</span>
                        {d.description && (
                          <span className="text-[#6B7280]"> · {d.description}</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {explanation && (
              <p className="text-[12px] text-[#374151] leading-relaxed mt-auto pt-3 border-t border-[#E5E7EB]">
                {explanation}
              </p>
            )}
          </article>
        );
      })}
    </div>
  );
}

// --- Financial impact summary -----------------------------------------------
function RoiSnapshotBlock({ summary, currency, exchangeRate = 1, roiBySite, onJumpToRoi }) {
  if (!summary) {
    return (
      <div className="bg-white border border-dashed border-[#D1D5DB] rounded-sm p-4 text-[12px] text-[#6B7280]" data-testid="report-roi-empty">
        Financial impact values will appear once ROI is calculated.{" "}
        {onJumpToRoi && (
          <button onClick={onJumpToRoi} className="text-[#025979] font-medium hover:underline">
            Open ROI
          </button>
        )}
      </div>
    );
  }
  const fmt = (v) => formatCurrency(v == null ? v : v * exchangeRate, currency);
  const siteCount = summary.site_count || Object.keys(roiBySite || {}).length;
  const totalWh = summary.warehouse_count_total ||
    Object.values(roiBySite || {}).reduce((n, v) => n + (v.warehouse_count || 1), 0);

  const stats = [
    { key: "investment", label: "Total investment", value: fmt(summary.total_investment) },
    { key: "annual", label: "Annual savings", value: fmt(summary.total_annual_savings) },
    { key: "payback", label: "Payback period",
      value: summary.overall_payback_months !== null && summary.overall_payback_months !== undefined
        ? `${summary.overall_payback_months} months` : "—" },
    { key: "roi", label: "3-year ROI",
      value: summary.overall_roi_percent !== null && summary.overall_roi_percent !== undefined
        ? `${summary.overall_roi_percent}%` : "—" },
  ];

  return (
    <div data-testid="report-roi-block">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map(s => (
          <div key={s.key} className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-4" data-testid={`report-roi-${s.key}`}>
            <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{s.label}</p>
            <p className="text-xl font-bold text-[#111827] mt-1">{s.value}</p>
          </div>
        ))}
      </div>
      {siteCount > 1 && (
        <p className="text-xs text-[#6B7280] mt-3" data-testid="report-roi-coverage">
          Across <span className="text-[#111827] font-semibold">{siteCount}</span> warehouse type{siteCount > 1 ? "s" : ""}
          {totalWh ? (
            <> and <span className="text-[#111827] font-semibold">{totalWh}</span> total site{totalWh > 1 ? "s" : ""}.</>
          ) : "."}
        </p>
      )}
    </div>
  );
}

// --- Top Value Drivers (quantitative) ---------------------------------------
// Surfaces the ROI engine's `combined_top_value_drivers` in plain language so
// clients see *where* the savings come from. Falls back to aggregating
// per-site `top_value_drivers` if the combined list isn't populated on older
// outputs.
function TopValueDriversBlock({ summary, roiBySite, currency, exchangeRate = 1 }) {
  const fmt = (v) => formatCurrency(v == null ? v : v * exchangeRate, currency);
  let drivers = Array.isArray(summary?.combined_top_value_drivers)
    ? summary.combined_top_value_drivers
    : [];
  if (drivers.length === 0 && roiBySite) {
    // Aggregate amounts across sites by driver key, then sort desc.
    const map = new Map();
    Object.values(roiBySite).forEach((site) => {
      (site?.top_value_drivers || []).forEach((d) => {
        const key = d.key || d.label;
        if (!key) return;
        const prev = map.get(key);
        const amount = Number(d.amount || 0);
        if (prev) {
          prev.amount += amount;
        } else {
          map.set(key, { key, label: d.label || key, amount });
        }
      });
    });
    drivers = Array.from(map.values()).sort((a, b) => b.amount - a.amount);
  }
  drivers = drivers.slice(0, 5);
  if (drivers.length === 0) return null;
  const totalAnnual = Number(summary?.total_annual_savings || 0);
  return (
    <div data-testid="report-top-value-drivers">
      <h4
        className="text-xs font-semibold text-[#065F46] uppercase tracking-wider mb-3"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        Top value drivers
      </h4>
      <ol className="space-y-2">
        {drivers.map((d, i) => {
          const amount = Number(d.amount || 0);
          const pct = totalAnnual > 0 ? Math.round((amount / totalAnnual) * 100) : 0;
          return (
            <li
              key={d.key || d.label}
              data-testid={`report-driver-${i}`}
              className="flex items-center gap-3 bg-white/70 border border-emerald-100 rounded-sm px-3 py-2"
            >
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold shrink-0">
                {i + 1}
              </span>
              <span className="flex-1 text-sm text-[#111827]">{d.label}</span>
              {pct > 0 && (
                <span className="text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm font-medium">
                  {pct}%
                </span>
              )}
              <span
                data-testid={`report-driver-amount-${i}`}
                className="text-sm font-semibold text-[#065F46] tabular-nums"
              >
                {fmt(amount)}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// --- ROI context: assumptions used ------------------------------------------
// Previously listed qualitative "key cost drivers" alongside assumptions. The
// new quantitative `TopValueDriversBlock` now covers the "where does the value
// come from?" question, so this block focuses solely on modelling assumptions.
function RoiContextBlock({ assessment, roiInputs, currency, exchangeRate = 1, discountPct = 0 }) {
  const fmt = (v) => formatCurrency(v == null ? v : v * exchangeRate, currency);
  const assumptions = [];
  if (roiInputs) {
    if (roiInputs.year1_realization_pct != null) {
      assumptions.push(`Year-1 benefit realization: ${Number(roiInputs.year1_realization_pct)}%.`);
    }
    if (roiInputs.confidence_multiplier_pct != null) {
      assumptions.push(`Confidence multiplier applied: ${Number(roiInputs.confidence_multiplier_pct)}%.`);
    }
    if (currency) {
      assumptions.push(`All values expressed in ${String(currency).toUpperCase()}.`);
    }
    if (exchangeRate && Number(exchangeRate) !== 1) {
      assumptions.push(`Reference exchange rate: ${Number(exchangeRate)}.`);
    }
    if (Number(discountPct) > 0) {
      assumptions.push(`Implementation pricing discount applied: ${Number(discountPct)}%.`);
    }
    if (roiInputs.avg_fte_cost != null && Number(roiInputs.avg_fte_cost) > 0) {
      assumptions.push(`Labor productivity valued at ${fmt(roiInputs.avg_fte_cost)} per FTE per year.`);
    }
    if (roiInputs.error_cost_per_order != null && Number(roiInputs.error_cost_per_order) > 0) {
      assumptions.push(`Each avoided order error saves ${fmt(roiInputs.error_cost_per_order)}.`);
    }
  }
  assumptions.push(
    "Benefits are phased in over the first year and held steady through year 3 unless stated otherwise."
  );
  assumptions.push(
    "Implementation cost reflects end-to-end phase effort priced at the consultant-defined blended rates."
  );

  return (
    <div data-testid="report-roi-context">
      <h4
        className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-3"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        Assumptions used
      </h4>
      <ul
        data-testid="report-roi-assumptions"
        className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2"
      >
        {assumptions.map((a, i) => (
          <li key={i} className="text-sm text-[#374151] flex gap-2">
            <span aria-hidden className="text-[#025979] mt-[1px]">•</span>
            <span className="text-[#4B5563]">{a}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Report footer -----------------------------------------------------------
function ReportFooter({ assessment, clientView = false }) {
  const raw = assessment?.company_name || "Client";
  // In Client View, render the client name in a human-readable form (swap
  // underscores for spaces) to avoid leaking field-name style identifiers
  // like "VAL_B1_hybrid_triad" into the client-facing report.
  const company = clientView ? cleanCompanyName(raw) : raw;
  const now = new Date();
  const year = now.getFullYear();
  return (
    <footer
      data-testid="report-footer"
      className="border-t border-[#E5E7EB] pt-5 mt-4 text-[11px] text-[#6B7280] flex flex-wrap items-center justify-between gap-3"
    >
      <span>
        Prepared for <span className="text-[#111827] font-medium">{company}</span> · PrimeS4 Consulting
      </span>
      <span className="italic">Confidential — for client discussion only · {year}</span>
    </footer>
  );
}


// --- Implementation Overview blocks (Module 5 report sections) ---------------
// These blocks surface the consultant's saved Implementation Inputs as a
// read-only, client-safe summary. They never recalculate — the math mirrors
// what the Implementation Inputs tab already shows (computeCore).

function ImplementationSummaryBlock({ inputs, currency, exchangeRate = 1, discountPct = 0 }) {
  if (!inputs) {
    return (
      <p className="text-xs text-[#9CA3AF] italic" data-testid="impl-summary-empty">
        Implementation estimate not yet available. Seed it from the Implementation Inputs tab.
      </p>
    );
  }
  const discountFactor = 1 - Math.max(0, Number(discountPct) || 0) / 100;
  const fmtImpl = (v) => formatCurrency(v == null ? v : v * exchangeRate * discountFactor, currency);
  const core = computeCore(inputs);
  const totalHours = core.totalHours;
  const totalCost = core.totalCost;
  const months = core.totalWeeks > 0 ? (core.totalWeeks / 4.345).toFixed(1) : "0";
  const avgTeam = core.totalWeeks > 0
    ? (core.totalHours / (core.totalWeeks * 40)).toFixed(1)
    : "0";
  const hasDiscount = Math.abs(1 - discountFactor) > 1e-9;
  const costSub = hasDiscount
    ? `${Number(discountPct)}% discount applied`
    : "Across all phases";

  const tiles = [
    { key: "duration", label: "Total duration", value: `${core.totalWeeks} weeks`, sub: `${months} months`, testid: "report-impl-duration" },
    { key: "effort", label: "Total effort", value: `${Math.round(totalHours).toLocaleString()} hrs`, sub: "Across all phases", testid: "report-impl-effort" },
    { key: "cost", label: "Total cost", value: fmtImpl(totalCost), sub: costSub, testid: "report-impl-cost" },
    { key: "team", label: "Average team size", value: `${avgTeam} FTE`, sub: "Full-time equivalents", testid: "report-impl-team" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-impl-summary-grid">
      {tiles.map((t) => (
        <div key={t.key} data-testid={t.testid} className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{t.label}</p>
          <p className="text-lg font-bold text-[#111827] mt-1 leading-tight">{t.value}</p>
          <p className="text-[10px] text-[#6B7280] mt-0.5">{t.sub}</p>
        </div>
      ))}
    </div>
  );
}

function ImplementationPlanBlock({ inputs }) {
  const groups = (inputs?.phase_groups || []).filter((g) => (g.phases || []).length > 0);
  if (groups.length === 0) {
    return (
      <p className="text-xs text-[#9CA3AF] italic" data-testid="impl-plan-empty">
        Phase plan will appear once the consultant saves the Implementation Inputs.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="report-impl-plan-grid">
      {groups.map((g, idx) => {
        const phases = g.phases || [];
        const totalWeeks = phases.reduce((n, p) => n + (Number(p.duration_weeks) || 0), 0);
        return (
          <div
            key={idx}
            data-testid={`impl-plan-group-${idx}`}
            className="relative bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-4 flex flex-col"
          >
            <span className="absolute -top-2 left-4 inline-flex items-center justify-center w-6 h-6 rounded-sm bg-[#025979] text-white text-[11px] font-semibold">
              {idx + 1}
            </span>
            <p className="text-sm font-semibold text-[#111827] mt-2">
              Phase {idx + 1} — {g.name}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mt-0.5">
              {totalWeeks} week{totalWeeks === 1 ? "" : "s"}
            </p>
            <ul className="mt-3 space-y-1.5">
              {phases.map((p, pi) => (
                <li key={p.id || pi} className="flex items-start gap-2 text-[12px] text-[#374151] leading-snug">
                  <span aria-hidden className="mt-[5px] w-1.5 h-1.5 rounded-full bg-[#025979]/70 shrink-0" />
                  <span>
                    {p.name}
                    {Number(p.duration_weeks) > 0 && (
                      <span className="text-[#9CA3AF]"> · {p.duration_weeks}w</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

// NOTE: we intentionally display EVERY resource from the Implementation
// Overview (no filter to "key roles"). The consultant has already curated the
// list; hiding roles would silently misrepresent the plan.
// --- Resource model ----------------------------------------------------------
// Groups resources into "Onsite Leadership Team" and "Offshore Delivery Team"
// and merges near-duplicates (e.g. "Functional Consultant-1/-2/-3" → a single
// "Functional Consultants (3)" line). Intentionally list-style, not card-style.
//
// Normalization strips common technical suffixes (trailing `-1`, `_2`, or
// ` 3`) so "New resource 2" and "Functional Consultant-1" collapse cleanly.
function buildResourceModelTitle(inputs) {
  const resources = inputs?.resources || [];
  const onsite = resources.filter((r) => (r.delivery_type || "offshore") === "onsite").length;
  const offshore = resources.length - onsite;
  if (resources.length === 0) return "Resource model";
  return (
    <span className="flex items-baseline flex-wrap gap-2">
      <span>Resource model</span>
      <span
        data-testid="report-resource-model-count"
        className="text-[11px] font-normal text-[#6B7280]"
      >
        ({resources.length} resource{resources.length === 1 ? "" : "s"}
        {" · "}
        <span className="text-[#92400E]">{onsite} onsite</span>
        {" · "}
        <span className="text-[#1E40AF]">{offshore} offshore</span>)
      </span>
    </span>
  );
}

function normalizeRoleName(raw) {
  return String(raw || "")
    .trim()
    .replace(/[\s\-_]*\d+$/g, "")   // drop trailing numeric suffix variants
    .replace(/\s+/g, " ")
    .trim();
}

function pluralizeRole(base, count) {
  if (count <= 1) return base;
  if (!base) return base;
  // Avoid double-s on names already plural (e.g. "Analytics").
  if (/s$/i.test(base)) return base;
  return `${base}s`;
}

// Returns an ordered list of { label, count } entries, preserving first-seen
// order of the underlying resources so the Review Report matches the order
// used in Implementation Inputs.
function groupResourcesForReport(resources, deliveryType) {
  const bucket = new Map();
  const order = [];
  for (const r of resources || []) {
    if ((r.delivery_type || "offshore") !== deliveryType) continue;
    const base = normalizeRoleName(r.name) || (r.name || "Resource");
    if (!bucket.has(base)) {
      bucket.set(base, 0);
      order.push(base);
    }
    bucket.set(base, bucket.get(base) + 1);
  }
  return order.map((base) => {
    const count = bucket.get(base);
    const label = count > 1 ? `${pluralizeRole(base, count)} (${count})` : base;
    return { label, count, base };
  });
}

function ResourceModelBlock({ inputs }) {
  const resources = inputs?.resources || [];
  if (resources.length === 0) {
    return (
      <p className="text-xs text-[#9CA3AF] italic" data-testid="resource-model-empty">
        Resource model will appear once the consultant saves the Implementation Inputs.
      </p>
    );
  }
  const onsite = groupResourcesForReport(resources, "onsite");
  const offshore = groupResourcesForReport(resources, "offshore");

  return (
    <div
      data-testid="report-resource-model-teams"
      className="grid grid-cols-1 md:grid-cols-2 gap-6"
    >
      <ResourceTeam
        title="Onsite Leadership Team"
        tagline="Core team driving program governance and design."
        entries={onsite}
        accent="onsite"
        testid="resource-team-onsite"
      />
      <ResourceTeam
        title="Offshore Delivery Team"
        tagline="Execution team supporting build, testing, and rollout."
        entries={offshore}
        accent="offshore"
        testid="resource-team-offshore"
      />
    </div>
  );
}

function ResourceTeam({ title, tagline, entries, accent, testid }) {
  const accentBar = accent === "onsite" ? "bg-[#92400E]" : "bg-[#1E40AF]";
  return (
    <div data-testid={testid} className="border-l-2 border-[#E5E7EB] pl-4">
      <div className="flex items-center gap-2 mb-1">
        <span aria-hidden className={`w-1.5 h-1.5 rounded-full ${accentBar}`} />
        <h4
          className="text-sm font-semibold text-[#111827]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {title}
        </h4>
      </div>
      <p className="text-[11px] text-[#6B7280] mb-3">{tagline}</p>
      {entries.length === 0 ? (
        <p className="text-xs text-[#9CA3AF] italic">No team members assigned.</p>
      ) : (
        <ul className="space-y-1">
          {entries.map((e, i) => (
            <li
              key={`${e.base}-${i}`}
              data-testid={`${testid}-role-${i}`}
              className="text-sm text-[#374151] flex items-baseline gap-2"
            >
              <span aria-hidden className="text-[#025979]">•</span>
              <span>{e.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- Implementation Timeline -------------------------------------------------
// Simple cumulative-week view per phase group. Highlights a go-live milestone
// whenever a phase name matches go-live variants. No dates, no Gantt — this is
// deliberately a client-friendly flow view.
const GO_LIVE_RE = /\bgo[\s\-]?live\b/i;

function phaseIsGoLive(phase) {
  return GO_LIVE_RE.test(phase?.name || "");
}

function buildTimeline(groups) {
  let cursor = 1;
  return (groups || [])
    .filter((g) => (g.phases || []).length > 0)
    .map((g) => {
      const weeks = (g.phases || []).reduce((n, p) => n + (Number(p.duration_weeks) || 0), 0);
      const startWeek = cursor;
      const endWeek = cursor + Math.max(weeks, 0) - (weeks > 0 ? 1 : 0);
      const range = weeks > 0 ? `Week ${startWeek}–${endWeek}` : `Week ${startWeek}`;
      cursor = endWeek + 1;
      return {
        name: g.name,
        weeks,
        range,
        phases: g.phases || [],
        hasGoLive: (g.phases || []).some(phaseIsGoLive),
      };
    });
}

function ImplementationTimelineBlock({ inputs }) {
  const groups = buildTimeline(inputs?.phase_groups || []);
  if (groups.length === 0) {
    return (
      <p className="text-xs text-[#9CA3AF] italic" data-testid="impl-timeline-empty">
        Timeline appears once the consultant saves a plan with at least one phase.
      </p>
    );
  }
  return (
    <ol data-testid="report-impl-timeline-list" className="relative border-l-2 border-[#025979]/20 pl-5 space-y-5">
      {groups.map((g, idx) => (
        <li key={idx} data-testid={`impl-timeline-group-${idx}`} className="relative">
          {/* Bullet dot */}
          <span
            aria-hidden
            className="absolute -left-[29px] top-1 w-4 h-4 rounded-full bg-white border-2 border-[#025979] flex items-center justify-center"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#025979]" />
          </span>
          <div className="flex flex-wrap items-baseline gap-2">
            <span
              data-testid={`impl-timeline-range-${idx}`}
              className="text-[10px] uppercase tracking-wider text-[#025979] font-semibold bg-[#E0F2FE] px-1.5 py-0.5 rounded-sm"
            >
              {g.range}
            </span>
            <span className="text-sm font-semibold text-[#111827]">{g.name}</span>
            <span className="text-[11px] text-[#9CA3AF]">
              {g.weeks} week{g.weeks === 1 ? "" : "s"}
            </span>
          </div>
          <ul className="mt-2 space-y-1">
            {g.phases.map((p, pi) => {
              const goLive = phaseIsGoLive(p);
              return (
                <li
                  key={p.id || pi}
                  data-testid={`impl-timeline-phase-${idx}-${pi}`}
                  className={`flex items-center gap-2 text-[12px] leading-snug ${
                    goLive ? "text-[#9A3412] font-semibold" : "text-[#374151]"
                  }`}
                >
                  <span aria-hidden className={`w-1.5 h-1.5 rounded-full shrink-0 ${goLive ? "bg-[#F97316]" : "bg-[#025979]/60"}`} />
                  {goLive && <span aria-hidden className="text-base leading-none">🚀</span>}
                  <span>
                    {p.name}
                    {Number(p.duration_weeks) > 0 && (
                      <span className={`ml-1 ${goLive ? "text-[#9A3412]/70" : "text-[#9CA3AF]"}`}>
                        · {p.duration_weeks}w
                      </span>
                    )}
                    {goLive && (
                      <span
                        data-testid={`impl-timeline-golive-${idx}-${pi}`}
                        className="ml-2 text-[9px] uppercase tracking-wider bg-[#FED7AA] text-[#9A3412] px-1.5 py-0.5 rounded-sm"
                      >
                        Go-live milestone
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </li>
      ))}
    </ol>
  );
}

function formatReportDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

// Canonical tab config used by the tab bar.
const CONSULTANT_TABS = [
  { key: "client_inputs", label: "Client Inputs", short: "Client Inputs" },
  { key: "evaluation_insights", label: "Evaluation Insights", short: "Insights" },
  { key: "overview", label: "Recommendations", short: "Recommendations" },
  { key: "implementation_inputs", label: "Implementation", short: "Implementation" },
  { key: "roi", label: "ROI", short: "ROI" },
  { key: "review_report", label: "Executive Summary Report", short: "Executive Summary" },
];

// SmartBid (RFP) terminology overrides for tab labels. Guided records keep the
// SmartEvaluate labels above. UI-only — tab `key` values are unchanged.
const RFP_TAB_LABELS = {
  evaluation_insights: { label: "Bid Insights", short: "Bid Insights" },
  overview: { label: "Bid Recommendations", short: "Bid Recommendations" },
  review_report: { label: "Bid Summary Report", short: "Bid Summary" },
};

function tabsForRecord(isRfp) {
  if (!isRfp) return CONSULTANT_TABS;
  return CONSULTANT_TABS.map((t) => ({ ...t, ...(RFP_TAB_LABELS[t.key] || {}) }));
}


// ---------------------------------------------------------------------------
// Phase-6 governance — Readiness blockers strip + Final Review modal
// ---------------------------------------------------------------------------
const READINESS_SECTION_LABEL = {
  evaluate: "Evaluate",
  implementation: "Implementation",
  cost_model: "Cost Model",
  roi: "ROI",
  finalize: "Finalize",
};

// SmartBid (RFP) contextual relabels — applied ONLY when the record is a
// SmartBid RFP record. SmartEvaluate assessments keep the labels above.
const READINESS_SECTION_LABEL_RFP = {
  evaluate: "Bid Analysis",
  implementation: "Implementation Plan",
  cost_model: "Commercial",
  roi: "Commercial & ROI",
  finalize: "Finalize Bid",
};

function ReadinessBlockers({ readiness, isFinalized, isRfp = false }) {
  if (!readiness || readiness.ready) return null;
  const blockers = (readiness.blockers || []).filter(
    (b) => !(isFinalized && b.section === "finalize"),
  );
  if (!blockers.length) return null;
  const sectionLabels = isRfp ? READINESS_SECTION_LABEL_RFP : READINESS_SECTION_LABEL;
  return (
    <div
      data-testid="readiness-blockers"
      className="mb-4 bg-[#FFF7ED] border border-[#FDBA74]/60 rounded-md px-3 py-2.5"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[12px] font-semibold text-[#9A3412]">
          {isRfp ? "Bid" : "Publish"} is blocked — {blockers.length} item{blockers.length === 1 ? "" : "s"} to complete
        </p>
        <p className="text-[10px] text-[#9A3412]/70 uppercase tracking-wider">
          Consulting-grade gate · no override
        </p>
      </div>
      <ul className="mt-2 space-y-1">
        {blockers.map((b, i) => (
          <li
            key={`${b.section}-${b.field}-${i}`}
            data-testid={`readiness-blocker-${b.section}-${b.field}`}
            className="text-[11px] text-[#7C2D12] flex items-start gap-2"
          >
            <span className="mt-0.5 inline-block w-1.5 h-1.5 bg-[#F59E0B] rounded-full shrink-0" />
            <span>
              <span className="font-semibold">{sectionLabels[b.section] || b.section}:</span>{" "}
              {b.message}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FinalReviewModal({ open, onOpenChange, onConfirm, assessment }) {
  const summary = assessment?.roi_output?.overall_roi_summary || {};
  const impl = assessment?.implementation_inputs || {};
  const cb = impl.cost_breakdown || { one_time: {}, recurring: {} };
  const ot = cb.one_time || {};
  const rc = cb.recurring || {};
  const currency = cb.currency || "USD";
  const finalRecs = assessment?.final_recommendation_by_site || {};
  const solutions = Array.from(new Set(Object.values(finalRecs).filter(Boolean)));
  const solutionDisplay = summary.recommendation_display
    || (solutions.length === 1 ? solutions[0] : `Hybrid (${solutions.map((s) => String(s).replace("SAP ", "")).join(" + ")})`);
  const deployment = assessment?.overall_deployment_type || "—";
  const types = Object.keys(finalRecs);
  const totalLocations = Number(summary.warehouse_count_total || 0);
  const waves = impl.waves || [];
  // SYNC: phase_groups primary (matches Implementation Total Duration, ROI,
  // Executive Summary). waves fallback retained for legacy assessments only.
  const totalWeeks = (impl.phase_groups || []).reduce(
      (a, g) => a + (g.phases || []).reduce((b, p) => b + Number(p.duration_weeks || 0), 0),
      0,
    )
    || waves.reduce((n, w) => n + Number(w.duration_weeks || 0), 0);
  const fmt = (n) => fmtCurrency(n, currency);
  const KV = ({ label, value, testid }) => (
    <div className="flex items-baseline justify-between gap-3 py-1" data-testid={testid}>
      <span className="text-[11px] text-[#6B7280] uppercase tracking-wider">{label}</span>
      <span className="text-[12px] font-semibold text-[#0F172A] text-right">{value}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="final-review-modal"
        className="max-w-[640px] p-0 overflow-hidden"
      >
        <DialogHeader className="px-5 pt-5 pb-2">
          <DialogTitle data-testid="final-review-title" className="text-base">
            Final Review before Publishing
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            Confirm the transformation program the client will see. These
            values are frozen into the published snapshot.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 pb-3 space-y-3 max-h-[60vh] overflow-y-auto">
          <section
            data-testid="review-strategy"
            className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2"
          >
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-1.5">
              Strategy
            </p>
            <KV label="Solution" value={solutionDisplay || "—"} testid="review-kv-solution" />
            <KV label="Deployment" value={deployment} testid="review-kv-deployment" />
            <KV
              label="Coverage"
              value={`${types.length} warehouse type${types.length === 1 ? "" : "s"} across ${totalLocations || "—"} location${totalLocations === 1 ? "" : "s"}`}
              testid="review-kv-coverage"
            />
          </section>

          <section
            data-testid="review-metrics"
            className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2"
          >
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-1.5">
              Financials
            </p>
            <KV label="Total Investment" value={fmt(summary.total_investment)} testid="review-kv-investment" />
            <KV label="Annual Savings" value={fmt(summary.total_annual_savings)} testid="review-kv-annual" />
            <KV
              label="Payback"
              value={summary.overall_payback_months != null ? `${Number(summary.overall_payback_months).toFixed(1)} mo` : "—"}
              testid="review-kv-payback"
            />
            <KV
              label="3-Year ROI"
              value={summary.overall_roi_percent != null ? `${Number(summary.overall_roi_percent).toFixed(1)}%` : "—"}
              testid="review-kv-roi"
            />
          </section>

          <section
            data-testid="review-rollout"
            className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2"
          >
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-1.5">
              Rollout
            </p>
            <KV
              label="Rollout Approach"
              value={({ pilot_phased: "Pilot → Phased", parallel: "Parallel", big_bang: "Big Bang" })[impl.rollout_mode] || impl.rollout_mode || "—"}
              testid="review-kv-rollout-mode"
            />
            <KV
              label="Timeline"
              value={totalWeeks > 0 ? `${Math.round(totalWeeks)} weeks (${(totalWeeks / 4.345).toFixed(1)} mo)` : "—"}
              testid="review-kv-timeline"
            />
            <KV
              label="Waves"
              value={`${waves.length} wave${waves.length === 1 ? "" : "s"}`}
              testid="review-kv-waves"
            />
          </section>

          <section
            data-testid="review-cost-breakdown"
            className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2"
          >
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979] mb-1.5">
              Cost Breakdown
            </p>
            <KV label="Implementation Fees" value={fmt(ot.implementation_fees)} testid="review-kv-impl-fees" />
            <KV label="Hardware & Devices" value={fmt(ot.hardware_devices)} testid="review-kv-hw" />
            <KV label="Integration / Custom Dev" value={fmt(ot.integration_custom_dev)} testid="review-kv-integration" />
            <KV label="Internal Business Effort" value={fmt(ot.internal_business_effort)} testid="review-kv-internal" />
            <div className="border-t border-[#E5E7EB] mt-1.5 pt-1.5">
              <KV
                label="Total One-Time Investment"
                value={fmt(ot.total_one_time_investment)}
                testid="review-kv-total-onetime"
              />
              <KV label="SAP License / Subscription Δ" value={fmt(rc.sap_license_subscription_delta)} testid="review-kv-license" />
              <KV label="Annual Support / AMS" value={fmt(rc.annual_support_ams)} testid="review-kv-ams" />
              <KV
                label="Total Annual Recurring"
                value={fmt(rc.total_annual_recurring)}
                testid="review-kv-total-recurring"
              />
            </div>
          </section>
        </div>

        <div className="px-5 py-3 bg-[#F8FAFC] border-t border-[#E5E7EB] flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="final-review-cancel-btn"
            onClick={() => onOpenChange(false)}
            className="text-xs"
          >
            Back
          </Button>
          <Button
            type="button"
            size="sm"
            data-testid="final-review-confirm-btn"
            onClick={onConfirm}
            className="text-xs bg-[#025979] hover:bg-[#024a63] text-white"
          >
            Confirm & Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export default function AssessmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const clientAssignment = useClientAssignment();
  const clientReadOnly = !!clientAssignment?.readOnly;
  const [manageAccessOpen, setManageAccessOpen] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [tab, setTab] = useState("client_inputs");
  const [err, setErr] = useState("");

  // Keep the URL aligned with the record's workspace so the consultant sidebar
  // highlights the correct dashboard and the back link returns to the right
  // place: RFP records live under /consultant/smartbid/:id, Guided records
  // under /consultant/assessments/:id. Same detail component either way —
  // this is a routing/context change only (no backend/data change).
  useEffect(() => {
    if (!assessment) return;
    const isRfpRec = (assessment.submission_type || "").toLowerCase() === "rfp";
    const path = location.pathname;
    if (isRfpRec && path.includes("/assessments/")) {
      navigate(`/consultant/smartbid/${id}${location.search}`, { replace: true });
    } else if (!isRfpRec && path.includes("/smartbid/")) {
      navigate(`/consultant/assessments/${id}${location.search}`, { replace: true });
    }
  }, [assessment, location.pathname, location.search, id, navigate]);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  // Versioning state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizeSubmitting, setFinalizeSubmitting] = useState(false);
  const [versionsRefreshKey, setVersionsRefreshKey] = useState(0);
  const [snapshotVersion, setSnapshotVersion] = useState(null); // {id, version_number, snapshot, created_at, created_by}

  // "Publish to Client Portal" — releases the finalized Evaluate output to
  // the client-facing Transformation Portal as an immutable snapshot.
  // Backend storage still uses `released_*` fields (no duplicate data model);
  // UI vocabulary is "publish" only.
  const [releaseStatus, setReleaseStatus] = useState(null); // {released_to_client, released_at, released_by, released_for_emails, consultant_message, ...}
  const [releaseBusy, setReleaseBusy] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [publishEmailInput, setPublishEmailInput] = useState("");
  const [publishEmails, setPublishEmails] = useState([]);
  const [publishMessage, setPublishMessage] = useState("");
  const [publishEmailError, setPublishEmailError] = useState("");

  // Phase-6 governance — readiness gate + pre-publish Final Review modal.
  const [readiness, setReadiness] = useState(null); // {ready, blockers, metrics}
  const [reviewOpen, setReviewOpen] = useState(false);

  // Client view mode — distraction-free, read-only presentation of the
  // Executive Summary Report only. Synced with the URL so consultants can
  // share `?view=client` links directly.
  const [searchParams, setSearchParams] = useSearchParams();
  const [clientView, setClientView] = useState(() => searchParams.get("view") === "client");
  const [branding, setBranding] = useState(null);
  useEffect(() => {
    // Keep the URL param and the state in sync after every toggle so the
    // back/forward buttons stay meaningful.
    const cur = searchParams.get("view") === "client";
    if (cur === clientView) return;
    const next = new URLSearchParams(searchParams);
    if (clientView) next.set("view", "client"); else next.delete("view");
    setSearchParams(next, { replace: true });
  }, [clientView]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    // Branding (logo + footer copy) is only needed in client view — lazy-load
    // so consultant mode doesn't pay a request.
    if (!clientView || branding) return undefined;
    import("../lib/adminApi").then(({ getBranding }) =>
      getBranding().then((b) => { if (!cancelled) setBranding(b || {}); }).catch(() => {
        if (!cancelled) setBranding({});
      })
    );
    return () => { cancelled = true; };
  }, [clientView, branding]);
  // Force the report tab whenever client view is turned on.
  useEffect(() => { if (clientView && tab !== "review_report") setTab("review_report"); }, [clientView]); // eslint-disable-line react-hooks/exhaustive-deps

  const sessionKey = `reviewReportConfirmed:${id}`;

  const reloadAssessment = () => {
    getAssessmentDetail(id)
      .then((a) => setAssessment(normalizeAssessmentDeploymentLabels(a)))
      .catch(() => setErr("Failed to load evaluation"));
  };

  useEffect(() => { reloadAssessment(); }, [id]);

  // Fetch release status for the "Publish to Client Portal" button metadata.
  const reloadReleaseStatus = () => {
    if (!id) return;
    getReleaseStatus(id)
      .then(setReleaseStatus)
      .catch(() => setReleaseStatus({ released_to_client: false }));
  };
  useEffect(() => { reloadReleaseStatus(); }, [id]);

  // Readiness gate — refreshes whenever the assessment reloads (so saved
  // ROI / Implementation edits immediately clear blocker chips).
  const reloadReadiness = () => {
    if (!id) return;
    getAssessmentReadiness(id)
      .then(setReadiness)
      .catch(() => setReadiness({ ready: false, blockers: [] }));
  };
  useEffect(() => { reloadReadiness(); }, [id, assessment?.updated_at, assessment?.is_finalized]);

  const requestPublish = () => {
    if (!id) return;
    if (!assessment?.is_finalized) {
      toast.error("Finalize the evaluation before publishing to the client portal.");
      return;
    }
    if (readiness && !readiness.ready) {
      toast.error("Complete all required inputs before publishing.");
      return;
    }
    // Pre-step: open the Final Review modal. Consultant confirms the
    // locked summary (Solution / Deployment / Coverage / Investment /
    // Savings / Payback / Timeline / Rollout / Cost Breakdown). Only
    // after confirmation do we open the existing email-chip modal.
    setReviewOpen(true);
  };

  const handleReviewConfirmed = () => {
    setReviewOpen(false);
    setPublishEmails(releaseStatus?.released_for_emails || []);
    setPublishMessage(releaseStatus?.consultant_message || "");
    setPublishEmailInput("");
    setPublishEmailError("");
    setPublishConfirmOpen(true);
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const commitEmailDraft = () => {
    const draft = publishEmailInput.trim().replace(/,$/, "");
    if (!draft) return true;
    if (!EMAIL_RE.test(draft)) {
      setPublishEmailError(`"${draft}" is not a valid email`);
      return false;
    }
    const v = draft.toLowerCase();
    if (!publishEmails.includes(v)) {
      setPublishEmails((prev) => [...prev, v]);
    }
    setPublishEmailInput("");
    setPublishEmailError("");
    return true;
  };

  const removeEmailChip = (em) =>
    setPublishEmails((prev) => prev.filter((e) => e !== em));

  const handlePublishEmailKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitEmailDraft();
    } else if (e.key === "Backspace" && !publishEmailInput && publishEmails.length) {
      setPublishEmails((prev) => prev.slice(0, -1));
    }
  };

  const handlePublishEmailPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (!/[,;\s]/.test(text)) return; // let default paste happen for single token
    e.preventDefault();
    const tokens = text
      .split(/[,;\s]+/)
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    const next = [...publishEmails];
    const invalid = [];
    for (const t of tokens) {
      if (!EMAIL_RE.test(t)) {
        invalid.push(t);
        continue;
      }
      if (!next.includes(t)) next.push(t);
    }
    setPublishEmails(next);
    setPublishEmailInput("");
    setPublishEmailError(invalid.length ? `Invalid: ${invalid.join(", ")}` : "");
  };

  const handlePublishToClient = async () => {
    if (!id) return;
    // Commit any unfinished draft before publishing
    if (publishEmailInput.trim() && !commitEmailDraft()) return;
    const finalEmails = publishEmails;
    if (finalEmails.length === 0) {
      setPublishEmailError("Add at least one email address.");
      return;
    }
    setReleaseBusy(true);
    try {
      const res = await releaseToCustomer(id, {
        emails: finalEmails,
        message: publishMessage.trim() || null,
      });
      setReleaseStatus(res);
      setPublishConfirmOpen(false);
      toast.success("Published to the client portal.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Publish failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Publish failed.");
    } finally {
      setReleaseBusy(false);
    }
  };

  // On mount / whenever URL changes, detect ?version=<id> and load snapshot.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const versionId = url.searchParams.get("version");
    if (!versionId) {
      setSnapshotVersion(null);
      return;
    }
    getAssessmentVersion(id, versionId)
      .then(setSnapshotVersion)
      .catch(() => setErr("Failed to load version snapshot"));
  }, [id, versionsRefreshKey]);

  const isViewingVersion = !!snapshotVersion;
  const isLocked = !isViewingVersion && !!assessment?.is_finalized;

  // Read-only client: pin the whole tabbed interface to the FINALIZED version
  // shared via the grant. Loading that version snapshot flips the page into
  // its existing read-only `isViewingVersion` mode (all tabs, locked), so the
  // client sees exactly what the consultant sees — minus editing.
  useEffect(() => {
    if (!clientReadOnly) return;
    const vid = clientAssignment?.versionId || assessment?.finalized_version_id;
    if (!vid || snapshotVersion) return;
    getAssessmentVersion(id, vid)
      .then(setSnapshotVersion)
      .catch(() => setErr("Failed to load the shared finalized version"));
  }, [clientReadOnly, clientAssignment?.versionId, assessment?.finalized_version_id, snapshotVersion, id]);

  const handleTabClick = (key) => {
    if (key === "review_report" && tab !== "review_report") {
      // Skip the "Review before viewing final report" reminder once the
      // assessment is finalized OR when the consultant is viewing a
      // historical snapshot version. The popup is a *draft-time* nudge —
      // there's nothing left to validate on an immutable finalized report.
      const isFinalizedOrSnapshot = isLocked || isViewingVersion;
      const alreadyConfirmed =
        typeof window !== "undefined" && window.sessionStorage.getItem(sessionKey) === "1";
      if (!alreadyConfirmed && !isFinalizedOrSnapshot) {
        setDontShowAgain(false);
        setReviewModalOpen(true);
        return;
      }
    }
    setTab(key);
  };

  const handleProceed = () => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(sessionKey, "1");
    }
    setReviewModalOpen(false);
    setTab("review_report");
  };

  const handleGoBack = () => {
    if (dontShowAgain && typeof window !== "undefined") {
      window.sessionStorage.setItem(sessionKey, "1");
    }
    setReviewModalOpen(false);
  };

  const finalizeLockRef = useRef(false);
  const handleFinalize = async () => {
    // Belt-and-braces guard: React state updates are async so a rapid
    // double-click could in theory fire two POSTs before `finalizeSubmitting`
    // reflects `true`. A ref flips synchronously and blocks the second call.
    if (finalizeLockRef.current) return;
    finalizeLockRef.current = true;
    setFinalizeSubmitting(true);
    try {
      const res = await finalizeAssessment(id);
      setFinalizeOpen(false);
      setVersionsRefreshKey(k => k + 1);
      reloadAssessment();
      if (res?.status === "already_finalized") {
        toast.message("This version is already finalized");
      } else {
        toast.success("Evaluation finalized");
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.message("Finalize already in progress");
      } else {
        toast.error("Failed to finalize evaluation");
      }
    } finally {
      setFinalizeSubmitting(false);
      finalizeLockRef.current = false;
    }
  };

  const handleCreateNewVersion = async () => {
    try {
      await cloneAssessmentVersion(id, "current");
      setVersionsRefreshKey(k => k + 1);
      reloadAssessment();
    } catch {
      setErr("Failed to create new version");
    }
  };

  const handleViewVersion = (vid) => {
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("version", vid);
      window.history.replaceState({}, "", url.toString());
    }
    setVersionsRefreshKey(k => k + 1);
    setTab("review_report");
  };

  const handleExitVersionView = () => {
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("version");
      window.history.replaceState({}, "", url.toString());
    }
    setSnapshotVersion(null);
    setVersionsRefreshKey(k => k + 1);
  };

  // Server-rendered PDF URLs and filename are declared after
  // `displayAssessment` below to avoid a temporal dead zone error.


  // Use the snapshot's assessment fields when viewing a past version so the
  // header/tabs reflect the frozen state, not the live draft.
  const displayAssessment = isViewingVersion
    ? { ...(snapshotVersion.snapshot?.executive_summary?.assessment || {}), ...(snapshotVersion.snapshot?.client_inputs || {}) }
    : assessment;

  // Server-rendered PDF — the backend runs Playwright against the dedicated
  // `/print/report/:token` template route. The finalized view exposes ONE
  // action: a styled `<a>` that points at this URL. The server responds
  // with `Content-Disposition: attachment` so a left-click normally triggers
  // the browser's native download; right-click → "Save link as…" is the
  // sandbox-proof fallback that always works inside Emergent's preview
  // iframe. (Declared AFTER `displayAssessment` to avoid TDZ on filename.)
  // When the consultant lands on a finalized assessment WITHOUT explicitly
  // opening Version History, `snapshotVersion` stays null. Fall back to the
  // assessment's `finalized_version_id` so Download/Email still work on the
  // live page right after clicking "Finalize Evaluation".
  const effectiveVersionId =
    snapshotVersion?.id || assessment?.finalized_version_id || null;
  const effectiveVersionNumber =
    snapshotVersion?.version_number || assessment?.finalized_version_number || 1;
  const downloadPdfUrl = useMemo(() => {
    if (!assessment?.id || !effectiveVersionId) return "";
    const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/admin";
    return `${API}/assessments/${assessment.id}/versions/${effectiveVersionId}/report.pdf`;
  }, [assessment?.id, effectiveVersionId]);
  const pdfFilename = useMemo(() => {
    const safe = (displayAssessment?.company_name || "Client")
      .replace(/[^a-zA-Z0-9_-]+/g, "_");
    const isCm = (displayAssessment?.scenario_type || "").toLowerCase() === "cloud_migration";
    const reportType = isCm ? "Cloud_Migration_Executive_Report" : "Warehouse_Strategy_Report";
    return `${safe}_${reportType}_v${effectiveVersionNumber}.pdf`;
  }, [displayAssessment?.company_name, displayAssessment?.scenario_type, effectiveVersionNumber]);

  // Download the Executive Report as a PDF via fetch so we can validate the
  // response BEFORE offering a download — never save a JSON/HTML error body as
  // a ".pdf". On failure we surface the backend message and allow retry.
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const handleDownloadPdf = useCallback(async () => {
    if (!downloadPdfUrl || isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    const toastId = toast.loading("Generating Executive Report PDF…");
    try {
      const res = await fetch(downloadPdfUrl, { credentials: "include" });
      const ctype = res.headers.get("content-type") || "";
      if (!res.ok || !ctype.includes("application/pdf")) {
        let msg = `PDF generation failed (HTTP ${res.status}).`;
        try {
          const body = await res.json();
          if (body?.detail) msg = typeof body.detail === "string" ? body.detail : msg;
        } catch { /* non-JSON error body */ }
        toast.error("PDF generation failed. Please try again or contact support.", { id: toastId, description: msg });
        return;
      }
      const blob = await res.blob();
      if (!blob || blob.size === 0) {
        toast.error("PDF generation failed. Please try again or contact support.", { id: toastId, description: "The generated file was empty." });
        return;
      }
      const cd = res.headers.get("content-disposition") || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const filename = m?.[1] || pdfFilename;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Executive Report downloaded.", { id: toastId });
    } catch (e) {
      toast.error("PDF generation failed. Please try again or contact support.", { id: toastId, description: String(e?.message || e).slice(0, 160) });
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [downloadPdfUrl, isDownloadingPdf, pdfFilename]);

  // Email PDF — submits to /api/admin/.../email-report which server-renders
  // the PDF and sends it via the admin "report_share" email template (Resend).
  // A confirmation dialog fires first so the consultant never sends by accident.
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: "", cc: "", message: "" });
  const [isEmailing, setIsEmailing] = useState(false);
  const openEmailConfirm = () => setEmailConfirmOpen(true);
  const confirmEmailAndCompose = () => {
    const snapshotInputs = snapshotVersion?.snapshot?.client_inputs || {};
    const preferred =
      snapshotInputs.contact_email ||
      snapshotInputs.email ||
      displayAssessment?.contact_email ||
      displayAssessment?.email ||
      "";
    setEmailForm((f) => ({
      to: f.to || preferred,
      cc: f.cc || "",
      message: f.message || "",
    }));
    setEmailConfirmOpen(false);
    setEmailDialogOpen(true);
  };
  const handleSendReportEmail = async () => {
    const to = (emailForm.to || "").trim();
    if (!to || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) {
      toast.error("Please enter a valid recipient email");
      return;
    }
    if (!assessment?.id || !effectiveVersionId) {
      toast.error("No finalized version loaded");
      return;
    }
    setIsEmailing(true);
    const toastId = toast.loading("Rendering & emailing report…");
    try {
      const { default: axios } = await import("axios");
      const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/admin";
      const { data } = await axios.post(
        `${API}/assessments/${assessment.id}/versions/${effectiveVersionId}/email-report`,
        {
          to,
          cc: emailForm.cc?.trim() || undefined,
          message: emailForm.message?.trim() || undefined,
        },
        { withCredentials: true, headers: { "Content-Type": "application/json" } },
      );
      if (data?.status === "stub_or_failed") {
        toast.success("Report queued (Resend API key missing — stub)", { id: toastId });
      } else {
        toast.success(`Emailed to ${to}`, { id: toastId });
      }
      if (data?.last_email) {
        setSnapshotVersion((v) => (v ? { ...v, last_email: data.last_email } : v));
      }
      setEmailDialogOpen(false);
      setEmailForm((f) => ({ ...f, message: "" }));
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "unknown error";
      toast.error(`Unable to email report · ${detail}`, { id: toastId });
    } finally {
      setIsEmailing(false);
    }
  };

  if (clientView) {
    return (
      <ClientReportView
        assessment={displayAssessment}
        assessmentId={id}
        branding={branding}
        snapshot={isViewingVersion ? snapshotVersion?.snapshot : null}
        snapshotVersion={snapshotVersion}
        onExit={() => setClientView(false)}
      />
    );
  }

  const isRfpRecord = (assessment?.submission_type || "").toLowerCase() === "rfp";

  return (
    <div>
      {!clientReadOnly && (
        <Link
          to={isRfpRecord ? "/consultant/smartbid" : "/consultant/assessments"}
          className="text-xs text-[#6B7280] hover:text-[#025979] inline-flex items-center gap-1 mb-2"
          data-testid="back-to-list"
        >
          <ArrowLeft className="w-3 h-3" />{" "}
          {isRfpRecord ? "Back to SmartBid Dashboard" : "Back to Evaluations"}
        </Link>
      )}

      <div className="flex items-start justify-between gap-3 mb-1">
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>
          {displayAssessment?.company_name || "Assessment"}
        </h1>
        <div className="flex items-center gap-3 shrink-0">
          {clientReadOnly ? (
            <span
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 h-7 rounded-sm bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE]"
              data-testid="client-readonly-badge"
            >
              <Lock className="w-3 h-3" /> Read Only
            </span>
          ) : (
            <>
              <label
                className="flex items-center gap-2 text-[11px] text-[#374151] cursor-pointer select-none"
                data-testid="client-view-toggle-label"
                title="Switch to a read-only, distraction-free version of the Executive Summary Report suitable for client sharing."
              >
                Client View
                <Switch
                  data-testid="client-view-toggle"
                  checked={clientView}
                  onCheckedChange={setClientView}
                />
              </label>
              <Button
                size="sm"
                variant="outline"
                className="text-[11px] h-7 px-2 border-[#E5E7EB] text-[#374151] hover:bg-[#F3F4F6]"
                onClick={() => setHistoryOpen(true)}
                data-testid="open-version-history-btn"
              >
                <History className="w-3 h-3 mr-1" /> Version History
              </Button>
              {isLocked ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-[11px] h-7 px-2 border-[#025979] text-[#025979] hover:bg-[#E0F2FE]"
                  onClick={() => setManageAccessOpen(true)}
                  data-testid="manage-client-access-btn"
                >
                  <Users className="w-3 h-3 mr-1" /> Manage Client Access
                </Button>
              ) : null}
            </>
          )}
        </div>
      </div>
      {!clientReadOnly && (
        <ManageClientAccessDialog
          open={manageAccessOpen}
          onOpenChange={setManageAccessOpen}
          module="smartevaluate"
          assignmentId={id}
          assignmentTitle={displayAssessment?.company_name || "this assessment"}
        />
      )}
      <p className="text-xs text-[#9CA3AF] mb-2">
        {displayAssessment?.email} · {displayAssessment?.contact_name || "—"} · {toSentenceCase(displayAssessment?.scenario_type)}
      </p>
      <div className="mb-5">
        <AuditMeta record={assessment} variant="inline" testId="assessment-audit-meta" />
      </div>

      {err && <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-600">{err}</div>}

      {isViewingVersion && !clientReadOnly && (
        <VersionBanner
          mode="readonly"
          versionNumber={snapshotVersion.version_number}
          finalizedAt={snapshotVersion.created_at}
          finalizedBy={snapshotVersion.created_by}
          onExit={handleExitVersionView}
        />
      )}
      {isLocked && (
        <VersionBanner
          mode="locked"
          isRfp={isRfpRecord}
          versionNumber={assessment?.finalized_version_number}
          finalizedAt={assessment?.finalized_at}
          finalizedBy={null}
          onCreateNewVersion={handleCreateNewVersion}
        />
      )}

      {/* Publish to Client Portal — only surfaces once the evaluation is
          finalized. Pushes the client-safe view-model snapshot to the
          Transformation Portal. Once the *current finalized version* has
          been published, the button disappears and a read-only
          "Published" pill takes its place — finalized + published versions
          are immutable, so there's nothing left to republish. The next
          publish only becomes possible after a NEW version has been
          created and finalized (`finalized_version_id` changes). */}
      {isLocked && !isViewingVersion && (() => {
        const currentVersionId = assessment?.finalized_version_id || null;
        const releasedVersionId = releaseStatus?.source_version?.version_id || null;
        const currentVersionAlreadyPublished =
          !!releaseStatus?.released_to_client &&
          !!currentVersionId &&
          releasedVersionId === currentVersionId;
        return (
        <div
          data-testid="publish-to-client-portal-band"
          data-published={currentVersionAlreadyPublished ? "true" : "false"}
          className="mb-4 bg-white border border-[#E5E7EB] rounded-md px-3 py-2.5 flex flex-wrap items-center justify-between gap-3"
        >
          <div className="flex items-start gap-2 min-w-0">
            <Send className="w-3.5 h-3.5 mt-0.5 text-[#025979] shrink-0" />
            <div className="text-[11px] leading-snug min-w-0">
              <span
                className="font-semibold text-[#111827]"
                data-testid="publish-section-title"
              >
                Client Portal Publishing
              </span>
              {releaseStatus?.released_to_client ? (
                <div className="text-[#0369A1]" data-testid="publish-status-meta">
                  <div>
                    Published: <span className="font-semibold">Yes</span>
                    {releaseStatus.released_at
                      ? ` · Last published ${new Date(releaseStatus.released_at).toLocaleString()}`
                      : ""}
                    {releaseStatus.released_by?.email
                      ? ` · by ${releaseStatus.released_by.email}`
                      : ""}
                  </div>
                  {(releaseStatus.released_for_emails || []).length > 0 && (
                    <div
                      className="mt-1 flex flex-wrap gap-1"
                      data-testid="published-emails-list"
                    >
                      <span className="text-[#475569]">Access granted to:</span>
                      {releaseStatus.released_for_emails.map((em) => (
                        <span
                          key={em}
                          data-testid={`published-email-chip-${em}`}
                          className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-[#E0F2FE] border border-[#025979]/20 text-[#025979] rounded-sm"
                        >
                          {em}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-[#6B7280]" data-testid="publish-helper-text">
                  Publish the finalized {isRfpRecord ? "bid analysis" : "transformation program"} to the
                  client-facing portal.
                </div>
              )}
            </div>
          </div>
          {currentVersionAlreadyPublished ? (
            <span
              data-testid="publish-status-pill"
              className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 h-7 rounded-sm bg-[#DCFCE7] text-[#166534] border border-[#BBF7D0]"
              title="Finalized versions are immutable. Create a new version to publish further changes."
            >
              <CheckCircle2 className="w-3 h-3" />
              Published to Client Portal
            </span>
          ) : (
            <Button
              size="sm"
              data-testid="publish-to-client-portal-btn"
              onClick={requestPublish}
              disabled={releaseBusy || (readiness && !readiness.ready)}
              title={readiness && !readiness.ready
                ? "Complete Implementation, Cost Model, and ROI before publishing."
                : undefined}
              className="text-[11px] h-7 px-3 bg-[#025979] hover:bg-[#024a63] text-white disabled:opacity-60"
            >
              <Send className="w-3 h-3 mr-1" />
              {releaseBusy ? "Publishing..." : "Publish to Client Portal"}
            </Button>
          )}
        </div>
        );
      })()}

      {readiness && !readiness.ready && (
        <ReadinessBlockers
          readiness={readiness}
          isFinalized={isLocked}
          isRfp={isRfpRecord}
        />
      )}

      {/* Final Review modal — pre-step before Publish. Shows the locked
          summary the client will see and requires explicit confirmation
          before the recipient-email modal opens. */}
      <FinalReviewModal
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        onConfirm={handleReviewConfirmed}
        assessment={assessment}
      />

      {/* Publish modal — multi-email chip input + optional consultant note */}
      <Dialog open={publishConfirmOpen} onOpenChange={setPublishConfirmOpen}>
        <DialogContent data-testid="publish-confirm-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid="publish-confirm-title">
              Publish to Client Portal
            </DialogTitle>
            <DialogDescription data-testid="publish-confirm-message">
              Grant secure access to the transformation dashboard for client
              users. Only the emails listed below will be able to view this
              program.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {/* Multi-email input */}
            <div>
              <Label
                htmlFor="publish-emails-input"
                className="text-xs font-semibold text-[#111827]"
              >
                Portal Access Email IDs <span className="text-red-500">*</span>
              </Label>
              <div
                data-testid="publish-emails-field"
                className="mt-1.5 min-h-[38px] border border-[#E5E7EB] rounded-sm bg-white px-2 py-1.5 flex flex-wrap gap-1.5 focus-within:ring-1 focus-within:ring-[#025979] focus-within:border-[#025979]"
                onClick={() => {
                  const el = document.getElementById("publish-emails-input");
                  if (el) el.focus();
                }}
              >
                {publishEmails.map((em) => (
                  <span
                    key={em}
                    data-testid={`publish-email-chip-${em}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-[#E0F2FE] border border-[#025979]/30 text-[#025979] rounded-sm"
                  >
                    {em}
                    <button
                      type="button"
                      data-testid={`publish-email-chip-remove-${em}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeEmailChip(em);
                      }}
                      className="text-[#025979]/60 hover:text-[#025979] font-bold"
                      aria-label={`Remove ${em}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  id="publish-emails-input"
                  data-testid="publish-emails-input"
                  type="email"
                  value={publishEmailInput}
                  onChange={(e) => {
                    setPublishEmailInput(e.target.value);
                    setPublishEmailError("");
                  }}
                  onKeyDown={handlePublishEmailKeyDown}
                  onPaste={handlePublishEmailPaste}
                  onBlur={() => commitEmailDraft()}
                  placeholder={publishEmails.length === 0 ? "name@company.com, another@company.com" : ""}
                  className="flex-1 min-w-[140px] bg-transparent outline-none text-xs text-[#111827] placeholder:text-[#9CA3AF] px-1"
                />
              </div>
              <p className="mt-1 text-[10px] text-[#6B7280]">
                Press Enter or comma to add. Duplicates are removed automatically.
              </p>
              {publishEmailError && (
                <p
                  className="mt-1 text-[11px] text-red-600"
                  data-testid="publish-emails-error"
                >
                  {publishEmailError}
                </p>
              )}
            </div>

            {/* Optional consultant message */}
            <div>
              <Label
                htmlFor="publish-message-input"
                className="text-xs font-semibold text-[#111827]"
              >
                Optional Message
              </Label>
              <Textarea
                id="publish-message-input"
                data-testid="publish-message-input"
                value={publishMessage}
                onChange={(e) => setPublishMessage(e.target.value)}
                placeholder="Short note to include in the invite email (optional)."
                rows={3}
                className="mt-1.5 text-xs border-[#E5E7EB] focus-visible:ring-[#025979] focus-visible:border-[#025979]"
              />
            </div>

            {/* Reassurance line */}
            <div
              className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] px-2.5 py-2 text-[11px] text-[#475569] leading-snug"
              data-testid="publish-reassurance-note"
            >
              This will make the transformation program visible to the
              listed client users. You can update progress later, but core
              recommendations should be finalized.
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              data-testid="publish-confirm-cancel-btn"
              onClick={() => setPublishConfirmOpen(false)}
              className="text-xs h-8 px-4"
            >
              Cancel
            </Button>
            <Button
              data-testid="publish-confirm-publish-btn"
              onClick={handlePublishToClient}
              disabled={releaseBusy || (publishEmails.length === 0 && !publishEmailInput.trim())}
              className="text-xs h-8 px-4 bg-[#025979] hover:bg-[#024a63] text-white"
            >
              <Send className="w-3 h-3 mr-1" />
              {releaseBusy ? "Publishing…" : "Publish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="text-[11px] text-[#9CA3AF] mb-2">
        Navigate across sections to review and refine this {isRfpRecord ? "bid analysis" : "assessment"}.
      </p>

      <div
        data-testid="assessment-tabs"
        role="tablist"
        className="mb-1 flex flex-wrap items-center gap-2"
      >
        {tabsForRecord(isRfpRecord).map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              data-testid={`tab-${t.key}`}
              onClick={() => handleTabClick(t.key)}
              className={`px-3.5 py-1.5 text-xs rounded-full transition-all duration-200 whitespace-nowrap ${
                active
                  ? "bg-[#025979] text-white font-medium shadow-sm ring-1 ring-[#025979]/10"
                  : "text-[#6B7280] font-normal hover:bg-[#F3F4F6] hover:text-[#111827]"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <p
        data-testid="consultant-tabs-flow-hint"
        className="text-[10.5px] text-[#9CA3AF] mb-5 italic"
      >
        {isRfpRecord
          ? "Follow the flow: Check Inputs → Review Bid Insights → Validate Bid Recommendations → Build Implementation Plan → Review ROI → View Bid Summary"
          : "Follow the flow: Check Inputs → Review Insights → Evaluate Recommendations → Build Implementation Plan → Review ROI → View Executive Summary"}
      </p>

      {/* Single-row action bar: download + email. Both actions use the same
          Playwright-rendered PDF bytes.
          - Download is a plain `<a download>` so right-click → "Save link
            as…" always works in Emergent's sandboxed preview iframe — and
            it's safe to expose on every finalized version a consultant
            views from history.
          - Email is only exposed on the *current* latest finalized version
            (`assessment.finalized_version_id === snapshotVersion.id`) so
            consultants can't accidentally mail a superseded snapshot. */}
      {/* Visible AFTER finalization (live page) AND when a consultant is
          explicitly browsing a version from history. Hidden while a draft
          is still open. */}
      {(isViewingVersion || isLocked) && tab === "review_report" && downloadPdfUrl && (() => {
        const hasOpenDraft = assessment?.is_finalized === false;
        const isLatestFinalized = !!(
          !hasOpenDraft &&
          assessment?.finalized_version_id &&
          (
            // Live page after finalize: snapshotVersion is null but the
            // effective version IS the latest finalized one.
            !snapshotVersion?.id ||
            snapshotVersion.id === assessment.finalized_version_id
          )
        );
        const isRfp = (assessment?.submission_type || "").toLowerCase() === "rfp";
        const dropdownLabel = isRfp ? "Generate Proposal" : "Download Executive Report";
        const proposalUrl = proposalDocxUrl(assessment?.id);
        const proposalPptUrl = proposalPptxUrl(assessment?.id);
        return (
        <div
          data-testid="report-action-bar-download"
          className="no-print flex flex-col items-end gap-1 mb-3"
        >
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  data-testid="report-actions-dropdown-trigger"
                  className="text-xs h-8 px-3 bg-[#025979] hover:bg-[#024a63] text-white font-medium shadow-sm"
                >
                  {dropdownLabel}
                  <ChevronDown className="w-3 h-3 ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72" data-testid="report-actions-dropdown-content">
                <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-[#6B7280]">
                  Executive Report
                </DropdownMenuLabel>
                <DropdownMenuItem
                  data-testid="dropdown-download-exec-report"
                  disabled={isDownloadingPdf}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownloadPdf();
                  }}
                  className="cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5 mr-2" />
                  {isDownloadingPdf ? "Generating PDF…" : "Download Executive Report (PDF)"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="dropdown-email-exec-report"
                  disabled={!isLatestFinalized}
                  onClick={(e) => {
                    if (!isLatestFinalized) {
                      e.preventDefault();
                      return;
                    }
                    openEmailConfirm();
                  }}
                  className="cursor-pointer"
                >
                  <Mail className="w-3.5 h-3.5 mr-2" />
                  Send Executive Report by Email
                </DropdownMenuItem>
                {isRfp && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-[#6B7280]">
                      Proposal Documents
                    </DropdownMenuLabel>
                    <DropdownMenuItem asChild data-testid="dropdown-generate-proposal-docx">
                      <a
                        href={proposalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="cursor-pointer"
                      >
                        <FileText className="w-3.5 h-3.5 mr-2" />
                        Generate Proposal Document (Word)
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="dropdown-proposal-presentation">
                      <a
                        href={proposalPptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="cursor-pointer"
                      >
                        <FileType className="w-3.5 h-3.5 mr-2" />
                        Generate Proposal Presentation (PPT)
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="dropdown-email-proposal"
                      disabled
                      className="cursor-not-allowed opacity-60"
                    >
                      <Send className="w-3.5 h-3.5 mr-2" />
                      Send Proposal by Email
                      <span className="ml-auto text-[10px] uppercase tracking-wide text-[#9CA3AF]">Coming Soon</span>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <span className="text-[10.5px] text-[#6B7280] italic" data-testid="email-pdf-hint">
            {isLatestFinalized
              ? "Tip: use “Download Executive Report” for the PDF, or email it directly to the client."
              : hasOpenDraft
                ? "Emailing is disabled while an open draft exists. Finalize the current draft to share the latest report."
                : "Viewing an older finalized version — emailing is disabled; only the latest finalized version can be emailed."}
          </span>
        </div>
        );
      })()}
      {/* The Recommendations (overview) tab hosts the Consultant
          Recommendation Override controls. Backend already enforces the
          release gate via `can_override`, so we render this tab OUTSIDE
          the lock fieldset to allow override edits on finalized-but-
          unreleased working versions. Snapshot views (isViewingVersion)
          stay read-only via a dedicated wrapper. */}
      {tab === "overview" && (
        <fieldset
          disabled={isViewingVersion}
          data-testid="tab-overview-fieldset"
          style={{ minWidth: 0, border: 0, padding: 0, margin: 0 }}
        >
          {(displayAssessment?.scenario_type || "").toLowerCase() === "cloud_migration" ? (
            <CloudMigrationRecommendations assessment={displayAssessment} assessmentId={id} />
          ) : (
            <OverviewTab assessment={displayAssessment} assessmentId={id} />
          )}
        </fieldset>
      )}
      <fieldset
        disabled={isLocked || isViewingVersion}
        className={(isLocked || isViewingVersion) ? "opacity-100" : ""}
        data-testid="tab-content-fieldset"
        style={{ minWidth: 0, border: 0, padding: 0, margin: 0 }}
      >
        {tab === "roi" && ((displayAssessment?.scenario_type || "").toLowerCase() === "cloud_migration"
          ? <CmRoiTab assessmentId={id} />
          : ((displayAssessment?.warehouse_groups || []).length > 0
            ? <GroupRoiTab assessmentId={id} />
            : <RoiTab assessmentId={id} assessment={displayAssessment} readOnly={clientReadOnly} />))}
        {tab === "client_inputs" && <ClientInputsTab assessment={displayAssessment} onRefresh={reloadAssessment} />}
        {tab === "evaluation_insights" && <EvaluationInsightsTab assessment={displayAssessment} assessmentId={id} onRefresh={reloadAssessment} />}
        {tab === "implementation_inputs" && (
          <ImplementationInputsTab
            assessmentId={id}
            assessment={displayAssessment}
            onSaved={() => { reloadAssessment(); reloadReadiness(); }}
          />
        )}
        {tab === "review_report" && (
          <ReviewReportTab
            assessment={displayAssessment}
            assessmentId={id}
            onJumpToRoi={() => setTab("roi")}
            onFinalize={() => {
              // iter 67: ignore the `finalize` blocker itself (which is
              // literally "not yet finalized") — it would deadlock this
              // very action.
              const actionable = (readiness?.blockers || []).filter(
                (b) => b.section !== "finalize"
              );
              if (actionable.length > 0) {
                toast.error(actionable[0]?.message || "Complete all required inputs before finalizing.");
                return;
              }
              setFinalizeOpen(true);
            }}
            isFinalized={isLocked}
            isViewingVersion={isViewingVersion}
            snapshot={isViewingVersion ? snapshotVersion.snapshot : null}
            readiness={readiness}
          />
        )}
      </fieldset>

      <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
        <DialogContent
          data-testid="review-confirmation-modal"
          className="sm:max-w-[460px] p-0 overflow-hidden border border-[#E5E7EB]"
        >
          <div className="px-6 pt-6 pb-1">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#E6F2F5] text-[#025979]">
                  <CheckCircle2 className="w-4 h-4" />
                </span>
                <DialogTitle
                  className="text-[15px] font-semibold text-[#111827] tracking-tight"
                  style={{ fontFamily: "Chivo" }}
                >
                  Review before viewing final report
                </DialogTitle>
              </div>
              <DialogDescription className="text-xs text-[#6B7280] leading-relaxed pt-1">
                Before proceeding, please confirm:
              </DialogDescription>
            </DialogHeader>

            <ul className="mt-3 space-y-2 text-xs text-[#374151]">
              {[
                "Recommendations align with client context",
                "ROI assumptions and values are reviewed",
                "Implementation plan (timeline, resources, cost) is validated",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span
                    aria-hidden
                    className="mt-[5px] w-1.5 h-1.5 rounded-full bg-[#025979] shrink-0"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <p className="mt-3 text-[11px] text-[#9CA3AF] leading-relaxed">
              This ensures the final report is accurate and ready for client discussion.
            </p>

            <label
              className="mt-4 flex items-center gap-2 text-[11px] text-[#6B7280] cursor-pointer select-none"
              data-testid="review-modal-dont-show-label"
            >
              <Checkbox
                checked={dontShowAgain}
                onCheckedChange={(v) => setDontShowAgain(Boolean(v))}
                data-testid="review-modal-dont-show-checkbox"
                className="h-3.5 w-3.5"
              />
              <span>Don’t show this again for this session</span>
            </label>
          </div>

          <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] flex sm:justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleGoBack}
              data-testid="review-modal-go-back-btn"
              className="text-xs border-[#E5E7EB] text-[#374151] hover:bg-white"
            >
              Go back and review
            </Button>
            <Button
              size="sm"
              onClick={handleProceed}
              data-testid="review-modal-proceed-btn"
              className="text-xs bg-[#025979] hover:bg-[#024a63] text-white shadow-sm"
            >
              Proceed to Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VersionHistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        assessmentId={id}
        refreshKey={versionsRefreshKey}
        onViewVersion={handleViewVersion}
        onVersionChanged={() => { setVersionsRefreshKey(k => k + 1); reloadAssessment(); }}
      />
      <FinalizeModal
        open={finalizeOpen}
        onOpenChange={setFinalizeOpen}
        onConfirm={handleFinalize}
        submitting={finalizeSubmitting}
      />

      {/* Confirm before composing the email — prevents accidental sends. */}
      <Dialog open={emailConfirmOpen} onOpenChange={setEmailConfirmOpen}>
        <DialogContent className="max-w-sm" data-testid="email-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo" }}>
              Send evaluation report via email?
            </DialogTitle>
            <DialogDescription className="text-[11px] text-[#6B7280] leading-relaxed">
              The finalized Evaluation Report will be shared as a PDF attachment.<br />
              You can review the recipient details before sending.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-8 px-3 border-[#E5E7EB]"
              onClick={() => setEmailConfirmOpen(false)}
              data-testid="email-confirm-no-btn"
            >
              No, go back
            </Button>
            <Button
              size="sm"
              className="text-xs h-8 px-3 bg-[#025979] hover:bg-[#024a63] text-white"
              onClick={confirmEmailAndCompose}
              data-testid="email-confirm-yes-btn"
            >
              <Mail className="w-3 h-3 mr-1.5" /> Yes, compose
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email PDF Report dialog — uses the admin `report_share` template
          + Resend. Backend server-renders the same Playwright PDF used by
          the Download button and attaches it automatically. */}
      <Dialog open={emailDialogOpen} onOpenChange={(o) => { if (!isEmailing) setEmailDialogOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="email-pdf-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo" }}>
              Email PDF Report
            </DialogTitle>
            <DialogDescription className="text-[11px] text-[#6B7280]">
              Sends the finalized report (v{snapshotVersion?.version_number}) for{" "}
              <span className="font-medium text-[#111827]">{displayAssessment?.company_name || "this client"}</span>
              {" "}using the admin <span className="font-mono">report_share</span> template.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="email-to" className="text-[11px] font-medium text-[#374151]">Recipient email</Label>
              <Input
                id="email-to"
                type="email"
                placeholder="client@example.com"
                value={emailForm.to}
                onChange={(e) => setEmailForm((f) => ({ ...f, to: e.target.value }))}
                className="h-9 text-sm"
                data-testid="email-to-input"
                disabled={isEmailing}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email-cc" className="text-[11px] font-medium text-[#374151]">CC (optional, comma-separated)</Label>
              <Input
                id="email-cc"
                type="text"
                placeholder="partner@example.com, pm@example.com"
                value={emailForm.cc}
                onChange={(e) => setEmailForm((f) => ({ ...f, cc: e.target.value }))}
                className="h-9 text-sm"
                data-testid="email-cc-input"
                disabled={isEmailing}
              />
            </div>
            {snapshotVersion?.last_email?.sent_at && (
              <div className="text-[10.5px] text-[#9CA3AF]" data-testid="last-emailed-info">
                Last sent: {new Date(snapshotVersion.last_email.sent_at).toLocaleString()} · {snapshotVersion.last_email.to}
              </div>
            )}
          </div>

          <DialogFooter className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-8 px-3 border-[#E5E7EB]"
              onClick={() => setEmailDialogOpen(false)}
              disabled={isEmailing}
              data-testid="email-dialog-close-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="text-xs h-8 px-3 bg-[#025979] hover:bg-[#024a63] text-white"
              onClick={handleSendReportEmail}
              disabled={isEmailing || !emailForm.to}
              data-testid="email-send-btn"
            >
              <Mail className="w-3 h-3 mr-1.5" /> {isEmailing ? "Sending…" : "Send email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Client View — read-only, distraction-free presentation of the Executive
// Summary Report only. Meant for screen-sharing or direct client sharing
// via the `?view=client` URL param. No tabs, no edit controls, no admin
// chrome — just the branded header, the Review Report content, and a
// confidentiality footer.
// ---------------------------------------------------------------------------
function ClientReportView({ assessment, assessmentId, branding, snapshot, snapshotVersion, onExit }) {
  // Partner Logo takes precedence over Platform Logo on client-facing
  // surfaces when uploaded. Both are managed in Admin → Branding.
  const platformLogo = branding?.partner_logo || branding?.platform_logo || "";
  // Client logo is stored on the assessment doc as base64 + mime
  // (see admin_routes.upload_client_logo). Reuse the same shape that
  // PrintReport.jsx uses so the Client View header picks it up
  // automatically when uploaded from the Version History drawer.
  const clientLogoDataUrl = assessment?.client_logo_base64
    ? `data:${assessment.client_logo_mime || "image/png"};base64,${assessment.client_logo_base64}`
    : "";
  const clientLogo =
    snapshot?.client_logo ||
    clientLogoDataUrl ||
    assessment?.client_logo ||
    assessment?.company_logo ||
    "";
  const footerText =
    branding?.footer_text ||
    "Confidential. Prepared for the named client only — not for redistribution.";

  return (
    <div
      data-testid="client-view-root"
      className="min-h-screen bg-white"
    >
      {/* Branded header — logos only, no text chrome. Kept inside the same
          max-width container as the body so left/right edges align. */}
      <header
        data-testid="client-view-header"
        className="max-w-6xl mx-auto px-8 md:px-16 pt-10 pb-6 flex items-center justify-between gap-6"
      >
        {platformLogo ? (
          <img loading="lazy"
            src={platformLogo}
            alt="Platform logo"
            data-testid="client-view-platform-logo"
            className="h-11 w-auto object-contain"
          />
        ) : (
          <span className="text-sm font-semibold tracking-wider text-[#0F172A]">PrimeS4</span>
        )}
        {clientLogo ? (
          <img loading="lazy"
            src={clientLogo}
            alt="Client logo"
            data-testid="client-view-client-logo"
            className="h-11 w-auto object-contain"
          />
        ) : null}
      </header>

      {/* Hair-line divider — consistent with consulting-report aesthetics. */}
      <div className="max-w-6xl mx-auto px-8 md:px-16">
        <div className="border-t border-[#E5E7EB]" />
      </div>

      {/* Report body — ReviewReportTab reused, interactive controls locked
          out via CSS + pointer-events. `client-view-mode` enables
          Client-View-only typographic polish (defined in App-level CSS). */}
      <main
        data-testid="client-view-body"
        className="client-view-mode max-w-6xl mx-auto px-8 md:px-16 pt-10 pb-16 space-y-10
                   [&_button]:hidden [&_input]:hidden [&_textarea]:hidden
                   [&_select]:hidden [&_[data-testid=open-version-history-btn]]:hidden"
        style={{ pointerEvents: "none", userSelect: "text" }}
      >
        <ReviewReportTab
          assessment={assessment}
          assessmentId={assessmentId}
          snapshot={snapshot}
          isFinalized={!!snapshot}
          isViewingVersion={!!snapshot}
          clientView={true}
        />
      </main>

      {/* Footer — minimal, human-readable copy. */}
      <footer
        data-testid="client-view-footer"
        className="max-w-6xl mx-auto px-8 md:px-16 py-6 border-t border-[#E5E7EB] flex flex-col md:flex-row items-center md:items-end justify-between gap-2 text-[11px] text-[#6B7280]"
      >
        <p data-testid="client-view-footer-text">{footerText}</p>
        <p className="uppercase tracking-wider" data-testid="client-view-footer-brand">
          PrimeS4 · WarehouseX SmartEvaluate
        </p>
      </footer>

      {/* Hidden exit affordance so internal users can still toggle back via
          ?view=client removal, keyboard shortcut, or by calling onExit in
          the console. The button is visually invisible but keeps React's
          state wiring intact in case we ever add a subtle escape hatch. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        data-testid="client-view-exit-toggle"
        onClick={() => onExit?.()}
        className="sr-only"
      >
        Exit Client View
      </button>
    </div>
  );
}

